/**
 * SkeletonProcessingPipeline.ts
 *
 * Pipeline for processing floor skeleton graphs with configurable passes.
 * Extends BasePipeline to provide pass management, timing, and UI integration.
 *
 * Each pass operates on the FloorSkeleton, modifying nodes and edges
 * to clean up the graph structure.
 */

import { BasePipeline, ProcessorFunction } from './BasePipeline';
import * as THREE from 'three';
import { computeStraightSkeleton, Vec2 } from './skeleton/straightSkeleton';
import { computeRasterThinSkeleton, RasterDebug } from './skeleton/rasterThinSkeleton';

/** Reference to the original room vertex that contributed to a skeleton node. */
export interface SkeletonSourceVertex {
  /** Room (or synthetic outer/cavity) the vertex belongs to. */
  roomId: string;
  /** Index of the vertex within that room's polygon. */
  vertexIndex: number;
}

/** A polygon edge that the chosen probe for one side of a source vertex hit during expansion. */
export interface SkeletonProbedHit {
  /** The source vertex this hit belongs to (matches an entry in sourceVertices). */
  sourceRoomId: string;
  sourceVertexIndex: number;
  /** Which adjacent edge of the source vertex produced this hit. */
  sourceEdgeKind: 'prev' | 'next';
  /** The room (real or synthetic outer/cavity) whose polygon edge was hit. */
  hitRoomId: string;
  /** Index of the hit edge within that polygon. */
  hitEdgeIndex: number;
}

/** Node representing a wall intersection point in the skeleton */
export interface SkeletonNode {
  id: string;
  position: THREE.Vector3;
  /** IDs of rooms that share this vertex */
  roomIds: string[];
  /** IDs of edges connected to this node */
  edgeIds: string[];
  /** Original room vertices that this node was built from (one per merged contribution). */
  sourceVertices: SkeletonSourceVertex[];
  /** Polygon edges that the chosen probes hit when expanding the source vertices. */
  probedHits: SkeletonProbedHit[];
  /** Visual mesh for this node */
  mesh?: THREE.Mesh;
  selected: boolean;
  /** Flexible metadata as JSON string */
  metadata?: string;
}

/** Wall type classification for skeleton edges */
export type SkeletonWallType = 'INNER' | 'OUTER' | 'BLOCK';

/** Edge representing a wall segment between two nodes */
export interface SkeletonEdge {
  id: string;
  startNodeId: string;
  endNodeId: string;
  /** IDs of rooms that share this wall */
  roomIds: string[];
  /** Wall thickness on the left side (perpendicular to edge direction) */
  leftSize: number;
  /** Wall thickness on the right side (perpendicular to edge direction) */
  rightSize: number;
  /** Classification of the wall type */
  wallType: SkeletonWallType;
  /** Visual line for this edge */
  line?: THREE.Line;
  selected: boolean;
  /** Flexible metadata as JSON string */
  metadata?: string;
  /** Whether this edge is a junction edge (created by splitting a junction node into a ring) */
  isJunctionEdge?: boolean;
}

/** Expanded vertex data for pipeline processing */
export interface ExpandedVertex {
  position: THREE.Vector3;
  roomId: string;
  originalIndex: number;
  /** Index of this vertex within its room's polygon (used for source-vertex tracking on nodes). */
  vertexIndexInRoom: number;
  /** Walls hit by the chosen probes on each side of this vertex (only successful hits are included). */
  probedHits: SkeletonProbedHit[];
}

/** Room connectivity data for edge creation */
export interface RoomConnectivity {
  identifier: string;
  polygon: THREE.Vector3[];
  vertexStartIndex: number;
}

/** Debug ray data for visualization */
export interface DebugRay {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  distance: number;
  hit: boolean;
  /** Optional label for categorization */
  label?: string;
}

/** Identifies one side (one edge) of a polygon as a source for the wall-pairing pass. */
export interface SourceEdgeRef {
  roomId: string;
  edgeIndex: number;
}

/**
 * A loose midline segment produced by the wall-pairing pass, before the
 * connection pass turns these into a connected skeleton. Each segment
 * sits halfway between two facing polygon edges (or at a fixed offset
 * from one edge when no match was found), and tracks the source-edge
 * sides that claim it so a later source can extend rather than duplicate.
 */
export interface PairedSegment {
  /** Start point of the segment (3D, z = floorHeight). */
  a: THREE.Vector3;
  /** End point of the segment. */
  b: THREE.Vector3;
  /** Source-edge sides that point to this segment as their match. */
  sources: SourceEdgeRef[];
  /** Whether a partner edge was actually found (vs. fixed-offset fallback). */
  matched: boolean;
}

/**
 * One polygon edge (outline or room boundary) treated as a candidate "wall" by
 * the center-in-walls passes. `(ax,ay)-(bx,by)` with precomputed unit direction.
 */
export interface CenterWall {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  ux: number; // unit direction
  uy: number;
  len: number;
}

/**
 * A target line for an edge: a point `(px,py)` on the line and a unit direction
 * `(dx,dy)`. The edge should lie on this line after centering.
 */
export interface CenterTargetLine {
  px: number;
  py: number;
  dx: number;
  dy: number;
}

/**
 * A "virtual edge": one REAL skeleton edge that the match pass split off onto a
 * precomputed wall midline so it can move freely AND stay visible in the viewer.
 * `edgeId` is the real edge; `startNodeId`/`endNodeId` are its two PRIVATE nodes
 * (created by the match pass — no longer shared with neighbours). `origStart`/
 * `origEnd` are the original shared node ids those private nodes came from; the
 * reconnect pass groups virtual edges by original node to merge their private
 * nodes back. `line` is the matched midline (as an infinite target line);
 * `midlineIndex` indexes the midline in `CenterWallsState.midlines` so the
 * combine pass can group edges that share a midline.
 */
export interface CenterVirtualEdge {
  edgeId: string;
  startNodeId: string;
  endNodeId: string;
  origStart: string;
  origEnd: string;
  line: CenterTargetLine;
  midlineIndex: number;
}

/** Intermediate state shared across the three center-in-walls passes. */
export interface CenterWallsState {
  /** Precomputed wall midlines (from `computeWallMidlines`) that edges snap to. */
  midlines: PairedSegment[];
  virtuals: CenterVirtualEdge[];
}

/** The complete skeleton graph structure */
export interface FloorSkeleton {
  nodes: Map<string, SkeletonNode>;
  edges: Map<string, SkeletonEdge>;
  floorHeight: number;
  /** Expanded vertices (before node creation) - used by pipeline */
  expandedVertices?: ExpandedVertex[];
  /** Room connectivity data - used by edge creation pass */
  roomConnectivity?: RoomConnectivity[];
  /** Maps original vertex index to node ID - used by edge creation pass */
  vertexToNodeMap?: Map<number, string>;
  /** Loose midline segments produced by the wall-pairing pass (consumed by the connection pass). */
  pairedSegments?: PairedSegment[];
  /** Debug rays collected during processing */
  debugRays?: DebugRay[];
  /** Raster grids from the raster-thin skeleton pass, for the debug overlay. */
  rasterDebug?: RasterDebug;
  /** Virtual edges + walls shared between the three center-in-walls passes. */
  centerWalls?: CenterWallsState;
}

/** Kwargs for skeleton processing passes */
export interface SkeletonPassKwargs {
  mergeTolerance?: number;
  angleThreshold?: number;
  maxEdgeLength?: number;
  snapTolerance?: number;
  /** Whether to move nodes when resolving edge crossings (default: true) */
  moveNodes?: boolean;
  /** Outer wall thickness for straightening pass */
  outerWallThickness?: number;
  /** Inner wall thickness for straightening pass */
  innerWallThickness?: number;
  /** Maximum ray cast distance for straightening pass */
  maxRayDistance?: number;
  /** Maximum ray cast distance for dead-end node extension (default: 5.0) */
  deadEndMaxRayDistance?: number;
  /** Prune Dead Ends: max ray cast distance when probing for a dead end's flat head / side walls (meters, default: 1.0) */
  pruneDeadEndMaxRayDistance?: number;
  /** Prune Dead Ends: max deviation (degrees) for a wall in front of the open end to count as a perpendicular "flat head", and for the two side walls to count as parallel (default: 5) */
  pruneDeadEndAngleTolDeg?: number;
  /** Maximum iterations for straightening pass (default: 50) */
  straightenMaxIterations?: number;
  /** Distance to slide new nodes along edges when splitting junctions (default: 0.1) */
  junctionSlideDistance?: number;
  /** Minimum angle (degrees) between edges to consider them perpendicular (default: 70) */
  perpendicularAngleThreshold?: number;
  /** Maximum deviation from 180° (in degrees) for an angle to be straightened (default: 15) */
  straightenAngleThreshold?: number;
  /**
   * Remove Collinear Nodes: only dissolve a degree-2 collinear node when its two
   * incident edges have ~EQUAL wall thickness, within this relative tolerance
   * (fraction, default: 0.03 = ±3%). 0 or negative disables the check (any
   * thickness pair may dissolve).
   */
  removeCollinearThicknessTol?: number;
  /** When splitting an edge through a nearby node, skip if the node would land within this distance of an existing endpoint (default: 0.05) */
  endpointMargin?: number;
  /** Maximum probe-sweep length per edge during node creation (meters, default: 0.32) */
  probeMaxWalk?: number;
  /** Minimum circumradius (meters) for a triangle to qualify for longest-leg removal (default: 0.5) */
  triangleMinCircumradius?: number;
  /** Grow Pairs: max angular drift between similar edges to merge, in degrees (default: 10) */
  growPairsMergeAngleDeg?: number;
  /** Grow Pairs: max perpendicular distance between similar lines to merge, in meters (default: 0.1) */
  growPairsMergePerpTol?: number;
  /** Grow Pairs: max tangent gap between similar segments to merge, in meters (default: 0.2) */
  growPairsMergeGapTol?: number;
  /** Measure Wall Thickness: number of perpendicular probes per edge side (default: 9) */
  measureWallThicknessSampleCount?: number;
  /** Measure Wall Thickness: reject hits that aren't near-perpendicular. Allowed deviation from 90° in degrees (default: 30) */
  measureWallThicknessMaxAngleDeg?: number;
  /** Measure Wall Thickness: virtually thicken every polygon edge by this perpendicular distance (meters) during ray-casts so grazing probes still hit (only used as a fallback when no real-segment hit exists). Also extends the SKELETON edge's span by the same amount during face MATCHING, so a short zigzag piece finds the face sitting just beyond its own ends. Default: 0.5 m. */
  measureWallThicknessThickenBy?: number;
  /** Extend Pairs: max extension distance per endpoint (meters, default: 5). */
  extendPairsMaxDistance?: number;
  /** Extend Pairs: distance tolerance for snapping a hit point to an existing node (meters, default: 0.05). */
  extendPairsSnapTol?: number;
  /** Extend Pairs: half-width of each cap perpendicular to the edge — caps are finite segments of total length `2 * capHalfWidth` straddling the edge's endpoint (meters, default: 0.15). */
  extendPairsCapHalfWidth?: number;
  /** Merge Zigzags: max length (meters) of a connector edge to treat as a stair-step jog between two collinear walls (default: 0.25). */
  zigzagMaxConnectorLength?: number;
  /** Merge Zigzags: max deviation from collinear (degrees) for the two walls flanking the jog (default: 12). */
  zigzagCollinearAngleDeg?: number;
  /** Merge Zigzags: flank walls within this thickness difference (meters) fuse into one edge; beyond it a node is kept to preserve the depth change (default: 0.03). */
  zigzagThicknessEqTol?: number;
  /** Raster WallSketch: grid cell size (meters). Omit/0 = auto from wall thickness (~thickness/4). */
  rasterCellSize?: number;
  /** Raster WallSketch: Douglas–Peucker simplify tolerance (meters). Omit = 1.5 × cell. */
  rasterSimplifyTolerance?: number;
  /** Raster WallSketch: max deviation (degrees) for snapping a segment to horizontal/vertical (default: 8). */
  rasterAxisSnapAngleDeg?: number;
  /** Center In Walls: max perpendicular distance (meters) to consider a polygon edge a wall match (default: 1.5). */
  centerWallsMaxDistance?: number;
  /** Center In Walls: max angle (degrees) between a skeleton edge and a polygon edge to count as parallel (default: 20). */
  centerWallsMaxAngleDeg?: number;
  /** Center In Walls: min overlap fraction of the skeleton edge's span that a polygon edge must cover to match (default: 0.25). */
  centerWallsMinOverlap?: number;
  /** Prune Unmatched Walls: the proximity SCALE of the match score (metres)
   * — a trace this far from a polygon edge scores half proximity — and the
   * base of the candidate search radius (3× this). Not a hard cutoff
   * (default: 0.5). */
  pruneUnmatchedMaxDistance?: number;
  /** Prune Unmatched Walls: unsupported clusters up to this total length
   * (metres) that BRIDGE supported walls are kept as connectors — corner jogs
   * and junction bridges have no midline of their own but hold wall chains
   * together (default: 1.0). */
  pruneUnmatchedConnectorKeepLength?: number;
  /** Prune Unmatched Walls: edges OUTSIDE every room/outline polygon AND
   * farther than this (metres) from every polygon segment are beyond the
   * drawn geometry — no midline can possibly support them there, so
   * "unsupported" would merely mean "no information". Such edges are kept
   * (the pass's global no-polygons rule, applied locally). Real wall traces
   * beyond the drawn rooms (corridors, unselected areas) survive; noise
   * inside rooms keeps being pruned. Keep this close to
   * `pruneUnmatchedMaxDistance`: midlines only exist inside the wall bands
   * hugging the polygon edges, so beyond maxDist of every segment support is
   * impossible and judging is meaningless — a larger value swallows narrow
   * undrawn corridors whose far wall then gets wrongly pruned (default: 0.5). */
  pruneUnmatchedCoverageDistance?: number;
  /** Prune Unmatched Walls: a long unsupported chain bridging supported walls
   * is KEPT (instead of connectivity-checked for deletion) when its ends
   * continue the attached walls within this angle (degrees) at ≥2 attachment
   * points — a straight continuation is a gap in a real wall run, not a noise
   * chain criss-crossing a room (default: 35). */
  pruneUnmatchedContinuationAngleDeg?: number;
  /** Prune Unmatched Walls: chain PROMOTION collinearity (degrees) — an
   * unlocked edge continuing a locked edge within this angle at a shared
   * node is adopted as the same wall. Much stricter than the continuation
   * angle: real wall pieces run dead straight, while corner jogs kink
   * 20–35° and must stay collapsible into clean corners (default: 15). */
  pruneUnmatchedPromotionAngleDeg?: number;
  /** Snap Small Segments: edges SHORTER than this (metres) are snapped
   * collinear-or-perpendicular to their biggest neighbor; longer edges are
   * never touched and never rotate (default: 0.5). */
  snapSmallMaxLength?: number;
  /** Nudge Walls Into Bounds: maximum lateral distance (metres) a collinear
   * chain may be translated to land in the MIDDLE of its wall band (or just
   * past the violated face when the band's far side isn't found). A chain
   * needing more than this stays put (default: 1.0). */
  nudgeMaxDistance?: number;
  /** Center Match Walls: edges SHORTER than this (metres) are NOT matched to
   * midlines. Small junction/connector pieces picking their own match group
   * is what tears T-junctions apart — they must FOLLOW their large
   * neighbors instead (run Snap Small Segments after the centering trio),
   * so a wall's small pieces can never drag it or leave holes (default: 0.5). */
  centerWallsMinEdgeLength?: number;
  /** Measure Wall Thickness: edges SHORTER than this (metres) are not probed —
   * they inherit thickness from their most-collinear measured neighbour. Tiny
   * junction bridge connectors (cw_conn_*, ~a few cm) probe nothing but
   * junction interior and would get junk asymmetric thickness that then blocks
   * collinear merging (default: 0.15). */
  measureWallThicknessMinEdgeLength?: number;
  /** Center match: max rotation (degrees) the rotate-in-place guard fallback may
   * apply to square an edge onto its midline's direction (default: 15). An edge
   * needing more than this is left untouched — the wide `centerWallsMaxAngleDeg`
   * match window (42° in the default chain) exists to FIND a wall pair, but
   * swinging an already-placed edge that far means the match was a different
   * wall, not this one (an edge in the right place must not be spun wrong). */
  centerWallsMaxRotateDeg?: number;
}

/** Type alias for skeleton processor functions */
export type SkeletonProcessor = ProcessorFunction<FloorSkeleton, SkeletonPassKwargs>;

/**
 * Find an edge between two nodes in the skeleton
 */
export function findEdgeBetweenNodes(skeleton: FloorSkeleton, nodeId1: string, nodeId2: string): SkeletonEdge | null {
  for (const edge of skeleton.edges.values()) {
    if ((edge.startNodeId === nodeId1 && edge.endNodeId === nodeId2) || (edge.startNodeId === nodeId2 && edge.endNodeId === nodeId1)) {
      return edge;
    }
  }
  return null;
}

/**
 * Find the closest point on a line segment to a given point
 */
export function closestPointOnSegment(point: THREE.Vector3, segStart: THREE.Vector3, segEnd: THREE.Vector3): { closestPoint: THREE.Vector3; distance: number; t: number } {
  const line = new THREE.Vector3().subVectors(segEnd, segStart);
  const lineLength = line.length();

  if (lineLength === 0) {
    return { closestPoint: segStart.clone(), distance: point.distanceTo(segStart), t: 0 };
  }

  const lineDir = line.clone().normalize();
  const pointToStart = new THREE.Vector3().subVectors(point, segStart);

  let t = pointToStart.dot(lineDir) / lineLength;
  t = Math.max(0, Math.min(1, t));

  const closestPoint = new THREE.Vector3().addVectors(segStart, lineDir.multiplyScalar(t * lineLength));
  const distance = point.distanceTo(closestPoint);

  return { closestPoint, distance, t };
}

/**
 * Check if two line segments intersect (2D, ignoring Z)
 * Returns the intersection point if they intersect, null otherwise
 */
function segmentIntersection2D(a1: THREE.Vector3, a2: THREE.Vector3, b1: THREE.Vector3, b2: THREE.Vector3): THREE.Vector3 | null {
  const d1x = a2.x - a1.x;
  const d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x;
  const d2y = b2.y - b1.y;

  const cross = d1x * d2y - d1y * d2x;

  // Parallel lines
  if (Math.abs(cross) < 0.0001) return null;

  const dx = b1.x - a1.x;
  const dy = b1.y - a1.y;

  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // Check if intersection is within both segments (excluding endpoints)
  const epsilon = 0.001;
  if (t > epsilon && t < 1 - epsilon && u > epsilon && u < 1 - epsilon) {
    return new THREE.Vector3(a1.x + t * d1x, a1.y + t * d1y, a1.z);
  }

  return null;
}

// ============================================================================
// Pass Implementations
// ============================================================================

/**
 * Pair every polygon edge in roomConnectivity (real rooms + synthetic
 * outer shell + cavities) with a facing edge across the wall material,
 * and emit a midline segment per pair. Edges with no facing match get
 * a midline placed at `outerWallThickness / 2` from the source.
 *
 * Per-source-edge bookkeeping ensures that when two sources discover
 * each other as best matches the same midline is shared (extended if
 * needed) rather than duplicated. Extension is rejected when the
 * extended segment would cross any polygon edge — in that case the
 * later source creates its own midline.
 *
 * Output sits on `skeleton.pairedSegments` for the next pass (which
 * will connect the loose segments into a real skeleton graph).
 *
 * Conventions (from `generateSkeletonFromRooms`):
 *  - Real rooms / cavities are CW, so the wall-material side is
 *    left-of-travel `(-dy, dx)`.
 *  - The synthetic outer shell is CCW, so wall material sits on
 *    right-of-travel `(+dy, -dx)`.
 */
/**
 * Core of the wall-pairing logic, shared by `pairWallsPass` (which materializes
 * the result as preview geometry) and `centerMatchWallsPass` (which snaps
 * skeleton edges onto these midlines). Pairs every polygon edge with its facing
 * parallel wall across the wall material and returns one midline `PairedSegment`
 * per pair. Writes `skeleton.debugRays` as a side effect (probe visualization);
 * does NOT touch `skeleton.nodes`/`edges`/`pairedSegments`.
 */
export function computeWallMidlines(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): PairedSegment[] {
  if (!skeleton.roomConnectivity) return [];

  const outerWallThickness = kwargs?.outerWallThickness ?? 0.3;
  const z = skeleton.floorHeight;

  // Debug rays: every perpendicular probe (real or extended) we cast
  // gets one entry. Reset on pass entry so re-runs don't accumulate.
  // Reuses the shared `debugRays` field; WallSketchEditorTool routes
  // them to per-stage debug groups by label.
  skeleton.debugRays = [];

  const OUTER_PREFIX = '__OUTER__';

  // Flatten every polygon edge into a uniform record. `outwardX/Y` is
  // the unit perpendicular pointing into the wall material from this
  // edge (where partners are expected to live).
  interface SourceEdge {
    roomId: string;
    edgeIndex: number;
    sx: number;
    sy: number;
    ex: number;
    ey: number;
    /** Unit tangent (s → e). */
    tx: number;
    ty: number;
    /** Unit outward normal (toward wall material). */
    ox: number;
    oy: number;
    length: number;
    /** True if this edge belongs to a synthetic outer-shell polygon. */
    isOuterShell: boolean;
  }

  const sources: SourceEdge[] = [];
  const sourceKey = (roomId: string, edgeIndex: number) => `${roomId}#${edgeIndex}`;

  // Indexable view of each polygon, used by the "is extension still
  // in wall material?" point-in-polygon check.
  const polygonByRoomId = new Map<string, THREE.Vector3[]>();

  /** Standard ray-crossing point-in-polygon test. */
  const pointInPolygon = (px: number, py: number, poly: THREE.Vector3[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      const intersects = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  };

  /**
   * Signed area under the shoelace alternate Σ(b.x-a.x)·(b.y+a.y).
   * Matches the convention used by `FloorProcessors.generateWallSketch-
   * FromRooms` to normalize windings. Positive = math-CW in Y-up.
   */
  const signedAreaAlt = (poly: THREE.Vector3[]): number => {
    let s = 0;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      s += (b.x - a.x) * (b.y + a.y);
    }
    return s;
  };

  for (const room of skeleton.roomConnectivity) {
    let poly = room.polygon;
    const isOuterShell = room.identifier.startsWith(OUTER_PREFIX);

    // Force-normalize the winding here so the pass can never produce
    // wrong-side midlines from an inverted ring sneaking in upstream.
    // Contract:
    //   - real rooms + cavities → math-CW (signedAreaAlt ≥ 0)
    //   - outer shells          → math-CCW (signedAreaAlt < 0)
    // The same convention `FloorProcessors` aims to maintain — we
    // just don't depend on it succeeding. If we have to reverse,
    // also write back so downstream uses (`isInWallMaterial`,
    // tangent-extension) see the same orientation.
    if (poly.length >= 3) {
      const area = signedAreaAlt(poly);
      const isCW = area >= 0;
      const wantCW = !isOuterShell;
      if (isCW !== wantCW) {
        console.warn(`pairWallsPass: forcing winding flip on polygon "${room.identifier}" (got ${isCW ? 'CW' : 'CCW'}, expected ${wantCW ? 'CW' : 'CCW'}).`);
        poly = poly.slice().reverse();
        room.polygon = poly;
      }
    }
    polygonByRoomId.set(room.identifier, poly);

    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      const tx = dx / len;
      const ty = dy / len;

      // Outward = the side where the skeleton lives. With the
      // upstream normalized windings, left-of-travel `(-ty, tx)` is
      // wall material for every polygon kind we feed in:
      //   - rooms (math-CW): walking CW, interior is on the RIGHT.
      //     Left-of-travel exits the room into the wall material.
      //   - cavities (math-CW around the void): walking CW, the
      //     cavity void is on the RIGHT. Left-of-travel points INTO
      //     the surrounding wall material.
      //   - outer shells (math-CCW): walking CCW, the building's
      //     interior is on the LEFT. Left-of-travel points INTO the
      //     building = wall material side (where the skeleton
      //     should sit). The outer shell encloses the BUILDING, so
      //     its polygon interior IS the wall-material-bearing
      //     region.
      const ox = -ty;
      const oy = tx;
      sources.push({ roomId: room.identifier, edgeIndex: i, sx: a.x, sy: a.y, ex: b.x, ey: b.y, tx, ty, ox, oy, length: len, isOuterShell });
    }
  }

  if (sources.length === 0) {
    return [];
  }

  /**
   * Decide whether `(px, py)` sits in the GLOBAL wall material —
   * outside every room/cavity polygon AND inside at least one outer
   * shell. Used by the tangent-extension logic to check whether a
   * probe origin past the source's endpoint still sits in valid wall
   * material BEFORE casting a perpendicular ray from there.
   *
   * Per-polygon "is this in MY polygon's wall material?" is not
   * enough: a tangent extension from a room edge that walks past the
   * source's endpoint can land in another room (outside its own
   * polygon, but inside someone else's). The probe ray cast from
   * there then fires across that other room's interior, producing
   * the spurious rays that go deep into rooms.
   *
   * `_S` is kept on the signature so the caller can still pass the
   * source for clarity / future use; the actual check is global.
   */
  const isInWallMaterial = (px: number, py: number, _S: SourceEdge): boolean => {
    let insideAnyShell = false;
    let anyShell = false;
    for (const [roomId, poly] of polygonByRoomId) {
      const inside = pointInPolygon(px, py, poly);
      const isShell = roomId.startsWith(OUTER_PREFIX);
      if (isShell) {
        anyShell = true;
        if (inside) insideAnyShell = true;
      } else if (inside) {
        // Inside any room or cavity = NOT in wall material.
        return false;
      }
    }
    // If outer shells exist, the point must sit inside at least one
    // to count as wall material (= inside the building envelope).
    // If no shells exist at all, fall back to the rooms-only rule.
    return anyShell ? insideAnyShell : true;
  };

  /**
   * Cast a ray from `(ox, oy)` in direction `(dx, dy)` and return the
   * first-hit distance + the target's source index. Skips edges of
   * `skipRoomId` (same polygon as the probe origin). Returns
   * `{ dist: Infinity, targetIdx: -1 }` if nothing is hit within
   * `maxRange`.
   */
  interface RayHit {
    dist: number;
    targetIdx: number;
  }
  const rayFirstHit = (ox: number, oy: number, dx: number, dy: number, skipSourceIdx: number, maxRange: number): RayHit => {
    let bestDist = Infinity;
    let bestIdx = -1;
    for (let tIdx = 0; tIdx < sources.length; tIdx++) {
      // Only skip the originating edge itself — other edges of the
      // same polygon (e.g. the opposite wall of an L-shaped room) are
      // legitimate self-intersection targets.
      if (tIdx === skipSourceIdx) continue;
      const T = sources[tIdx];
      const wx = T.ex - T.sx;
      const wy = T.ey - T.sy;
      const denom = dx * wy - dy * wx;
      if (Math.abs(denom) < 1e-10) continue;
      const rx = T.sx - ox;
      const ry = T.sy - oy;
      const t = (rx * wy - ry * wx) / denom;
      const u = (rx * dy - ry * dx) / denom;
      const EPS = 1e-5;
      if (t <= EPS) continue;
      if (t > maxRange) continue;
      if (u < -EPS || u > 1 + EPS) continue;
      // Side check: the ray must approach T from T's wall-material
      // side (= T.ox/T.oy direction). A ray going INTO that side
      // (sideDot > 0) is the wrong-side hit — it would have had to
      // punch through T's polygon interior first to reach this edge.
      const sideDot = dx * T.ox + dy * T.oy;
      if (sideDot > -1e-6) continue;
      if (t < bestDist) {
        bestDist = t;
        bestIdx = tIdx;
      }
    }
    return { dist: bestDist, targetIdx: bestIdx };
  };

  // Per-pair dedup: tracks every {S, C} pair that has already
  // produced a midline (order-independent key). When B's stage loop
  // re-discovers A — or when S's multi-candidate fan-out re-tries a
  // partner it already paired with — we skip the duplicate. Both
  // sources are otherwise free to keep pairing with OTHER opposing
  // walls.
  const emittedPairs = new Set<string>();
  const pairKey = (k1: string, k2: string) => (k1 < k2 ? `${k1}|${k2}` : `${k2}|${k1}`);
  const segments: PairedSegment[] = [];

  /**
   * Sweep perpendicular probes along source S. Probes inside the
   * `[0, length]` span always fire; if `allowExtension`, probes
   * continue past each endpoint along the tangent for as long as the
   * sample point still sits in S's wall-material region (per
   * `isInWallMaterial`). Each probe casts outward (S.ox/S.oy) until
   * the first cross-polygon hit within `maxRange`.
   *
   * Returns the list of probe samples (each with its t-parameter on
   * S, hit distance, and target index). Also pushes one debug-ray per
   * probe into `skeleton.debugRays` under `stageLabel`.
   */
  interface ProbeSample {
    /** Tangent parameter on S (negative or >S.length for extensions). */
    t: number;
    hit: boolean;
    /** Hit distance, or `maxRange` (for renderable misses). */
    dist: number;
    /** Source index of the hit target, or -1 on miss. */
    targetIdx: number;
  }
  const sweepProbes = (S: SourceEdge, sIdx: number, maxRange: number, allowExtension: boolean, stageLabel: string): ProbeSample[] => {
    const probes: ProbeSample[] = [];
    const sampleCount = 7;
    const step = S.length / sampleCount;

    // Render cap for unbounded misses — `Infinity` lines are useless;
    // pick a length that's visible but not absurd.
    const renderMissCap = Math.max(S.length * 4, outerWallThickness * 8);

    const fire = (t: number) => {
      const ox0 = S.sx + S.tx * t;
      const oy0 = S.sy + S.ty * t;
      const hit = rayFirstHit(ox0, oy0, S.ox, S.oy, sIdx, maxRange);
      const isHit = hit.dist !== Infinity;
      const renderedDist = isHit ? hit.dist : Math.min(maxRange, renderMissCap);
      skeleton.debugRays?.push({
        origin: new THREE.Vector3(ox0, oy0, z),
        direction: new THREE.Vector3(S.ox, S.oy, 0),
        distance: renderedDist,
        hit: isHit,
        label: stageLabel,
      });
      probes.push({ t, hit: isHit, dist: isHit ? hit.dist : maxRange, targetIdx: hit.targetIdx });
    };

    // Primary sweep: samples inside [0, length].
    for (let k = 0; k <= sampleCount; k++) fire(k * step);

    if (allowExtension) {
      // Hard ceiling on extension steps as a safety against runaway
      // extensions when geometry somehow keeps the tangent inside
      // wall material forever (shouldn't happen with valid input,
      // but a stray malformed polygon shouldn't lock the pass up).
      const EXTENSION_STEP_LIMIT = 200;

      // Walk past t=0 in the negative direction while still in wall
      // material relative to S.
      for (let k = 1; k <= EXTENSION_STEP_LIMIT; k++) {
        const t = -k * step;
        const px = S.sx + S.tx * t;
        const py = S.sy + S.ty * t;
        if (!isInWallMaterial(px, py, S)) break;
        fire(t);
      }
      // Walk past t=length in the positive direction.
      for (let k = 1; k <= EXTENSION_STEP_LIMIT; k++) {
        const t = S.length + k * step;
        const px = S.sx + S.tx * t;
        const py = S.sy + S.ty * t;
        if (!isInWallMaterial(px, py, S)) break;
        fire(t);
      }
    }

    return probes;
  };

  /**
   * Group the probe hits by target edge and return EVERY eligible
   * candidate (one entry per target that received at least one probe
   * hit). Each candidate produces its own midline downstream — a source
   * edge that sees two parallel walls (e.g. a thin wall between two
   * rooms) emits a midline pair against each opposing wall.
   *
   * This multi-partner fan-out is safe for the SPAN-ONLY stages, where
   * probes stay within the source's [0, length] extent. The extension
   * stages (which let probes reach past the source's endpoints and so
   * graze many far-off walls) are disabled for now — see the stage loop.
   *
   * Non-parallel targets are rejected: a partner that diverges from
   * S by more than `PARALLEL_TOL_DEG` would force the midline onto
   * an angle-bisector, not actually halfway between the hits.
   *
   * Multiple sources may legitimately share the same partner.
   */
  const PARALLEL_TOL_DEG = 15;
  const PARALLEL_DOT = Math.cos((PARALLEL_TOL_DEG * Math.PI) / 180);
  const collectPartners = (probes: ProbeSample[], S: SourceEdge): Array<{ targetIdx: number; hitProbes: ProbeSample[] }> => {
    const byTarget = new Map<number, ProbeSample[]>();
    for (const p of probes) {
      if (!p.hit || p.targetIdx < 0) continue;
      const T = sources[p.targetIdx];
      const dot = Math.abs(S.tx * T.tx + S.ty * T.ty);
      if (dot < PARALLEL_DOT) continue;
      const arr = byTarget.get(p.targetIdx) ?? [];
      arr.push(p);
      byTarget.set(p.targetIdx, arr);
    }
    const out: Array<{ targetIdx: number; hitProbes: ProbeSample[] }> = [];
    for (const [targetIdx, hitProbes] of byTarget) {
      out.push({ targetIdx, hitProbes });
    }
    return out;
  };

  /**
   * Place a fresh midline for source S based on the chosen partner C
   * and the probes that landed on C.
   *
   * Midline span depends on whether this was an extension match:
   *  - span-only (`isExtension=false`): C's projection onto S, clipped
   *    to S's own [0, length] — the portion of S that faces C.
   *  - extension (`isExtension=true`): the t-range of the HITTING probes
   *    on the SEARCHING line S, which runs past S's [0, length] into the
   *    extended region. The midline then follows the wall S is tracing
   *    rather than snapping back to S's pre-extension extent.
   *
   * Each successful pair emits its own dedicated segment.
   */
  const emitMidline = (S: SourceEdge, C: SourceEdge, hitProbes: ProbeSample[], isExtension: boolean): boolean => {
    if (hitProbes.length === 0) return false;
    const sKey = sourceKey(S.roomId, S.edgeIndex);
    const cKey = sourceKey(C.roomId, C.edgeIndex);

    // Per-pair dedup: if {S, C} already produced a midline (either
    // via S's own loop earlier in this stage, or via C's loop with
    // the order swapped), skip the duplicate. S and C are otherwise
    // free to keep pairing with their OTHER opposing walls.
    const pKey = pairKey(sKey, cKey);
    if (emittedPairs.has(pKey)) return false;

    let tLoOnS: number;
    let tHiOnS: number;
    if (isExtension) {
      // EXTENSION match: the span belongs to the SEARCHING line S, not
      // the partner. The hitting probes' t-parameters on S already mark
      // exactly where S found C — and those t's run past S's own
      // [0, length] into the extended region. Span the midline over that
      // hit-range so the line follows the wall S is actually tracing,
      // instead of snapping back to S's original (pre-extension) extent.
      tLoOnS = hitProbes.reduce((m, p) => Math.min(m, p.t), Infinity);
      tHiOnS = hitProbes.reduce((m, p) => Math.max(m, p.t), -Infinity);
      if (tHiOnS - tLoOnS < 1e-4) {
        // Single-probe hit — give the segment S's length around it so it
        // isn't degenerate.
        tLoOnS = Math.min(tLoOnS, 0);
        tHiOnS = Math.max(tHiOnS, S.length);
      }
    } else {
      // Span-only match: the shared length between S and C — i.e. C's
      // projection onto S's tangent, clipped to S's own [0, length]
      // extent. This makes it visually obvious which portion of S
      // actually has a pairing: the line only exists where the wall is
      // shared with the partner.
      const tCA = (C.sx - S.sx) * S.tx + (C.sy - S.sy) * S.ty;
      const tCB = (C.ex - S.sx) * S.tx + (C.ey - S.sy) * S.ty;
      const tCLo = Math.min(tCA, tCB);
      const tCHi = Math.max(tCA, tCB);
      tLoOnS = Math.max(0, tCLo);
      tHiOnS = Math.min(S.length, tCHi);
      if (tHiOnS - tLoOnS < 1e-4) {
        // No overlap: C is short and offset along S's tangent so its
        // projection doesn't reach S's span. Emit the midline against
        // the full source anyway — the wall S represents still exists.
        tLoOnS = 0;
        tHiOnS = S.length;
      }
    }

    // Midline = locus of midpoints between corresponding points on S
    // and C. For each S-tangent parameter t, take the point on S at
    // that t and the point on C that sits on the perpendicular ray
    // from S to C; average them. When S and C are parallel this
    // reduces to a constant perpendicular offset (the previous
    // behavior); when they're not, the midline's direction is the
    // average of S and C, which is what an angle-bisector wall
    // centerline should be.
    const corresponding = (tOnS: number): { mx: number; my: number } => {
      const sPx = S.sx + S.tx * tOnS;
      const sPy = S.sy + S.ty * tOnS;
      // Project sPx,sPy onto C's infinite line.
      const tOnC = (sPx - C.sx) * C.tx + (sPy - C.sy) * C.ty;
      const cPx = C.sx + C.tx * tOnC;
      const cPy = C.sy + C.ty * tOnC;
      return { mx: (sPx + cPx) * 0.5, my: (sPy + cPy) * 0.5 };
    };
    const mStart = corresponding(tLoOnS);
    const mEnd = corresponding(tHiOnS);
    const mStartX = mStart.mx;
    const mStartY = mStart.my;
    const mEndX = mEnd.mx;
    const mEndY = mEnd.my;

    // Note: we DON'T reject when the midline crosses some other
    // polygon edge. The midline runs through wall material between
    // S and C, but at junctions it can legitimately cross a
    // perpendicular wall (e.g. a partition that meets the wall S
    // and C represent). The downstream connect pass splits the
    // midline at any actual crossings, so rejecting here would
    // silently drop legitimate pairs whose midline happens to pass
    // through such a junction.

    const seg: PairedSegment = {
      a: new THREE.Vector3(mStartX, mStartY, z),
      b: new THREE.Vector3(mEndX, mEndY, z),
      sources: [
        { roomId: S.roomId, edgeIndex: S.edgeIndex },
        { roomId: C.roomId, edgeIndex: C.edgeIndex },
      ],
      matched: true,
    };
    segments.push(seg);
    // Record the {S, C} pair so its symmetric discovery later (or a
    // re-pair within the same stage's multi-candidate fan-out) gets
    // rejected. We do NOT block S or C from contributing further
    // midlines with their OTHER opposing walls.
    emittedPairs.add(pKey);
    // Diagnostic: log each emitted midline so spurious pairings can
    // be tracked back to their {S, C} source edges. The midpoint
    // gives a click-to-find anchor in the viewer.
    const midX = (mStartX + mEndX) * 0.5;
    const midY = (mStartY + mEndY) * 0.5;
    console.debug(`pairWallsPass: emit ${sKey} ↔ ${cKey}  mid=(${midX.toFixed(3)}, ${midY.toFixed(3)})  len=${Math.hypot(mEndX - mStartX, mEndY - mStartY).toFixed(3)}`);
    return true;
  };

  /**
   * Unified multi-probe attempt: sweep probes along S (optionally
   * with tangent extensions), collect EVERY parallel partner that
   * received at least one probe hit, and emit a midline against
   * each one.
   *
   * Returns true when the stage "found something" — either a new
   * midline was emitted, OR a candidate pair was rejected because
   * `{S, C}` was already emitted by an earlier source. Both are
   * cascade stop conditions: we don't want a later (wider) stage to
   * re-discover the same pair and produce a noisier match.
   */
  const tryMultiProbe = (sIdx: number, maxRange: number, allowExtension: boolean, stageLabel: string): boolean => {
    const S = sources[sIdx];
    const sKey = sourceKey(S.roomId, S.edgeIndex);
    const probes = sweepProbes(S, sIdx, maxRange, allowExtension, stageLabel);
    // Every stage commits to a single closest partner.
    const partners = collectPartners(probes, S);
    if (partners.length === 0) return false;
    let found = false;
    for (const partner of partners) {
      const C = sources[partner.targetIdx];
      const cKey = sourceKey(C.roomId, C.edgeIndex);
      // Cascade stop on a known pair: if {S, C} is already in
      // `emittedPairs`, S has already been paired with this C by an
      // earlier iteration. The current stage's discovery confirms
      // that finding — no need to escalate.
      if (emittedPairs.has(pairKey(sKey, cKey))) {
        found = true;
        continue;
      }
      if (emitMidline(S, C, partner.hitProbes, allowExtension)) found = true;
    }
    return found;
  };

  // Single search stage per source: span-only probes (no tangent
  // extension past the endpoints) cast out to the FAR range. This is the
  // old stage 3 — the widest span-only pass — run on its own.
  //
  // The escalating ladder used to be:
  //   1. Match ≤ 50 cm   — span-only, near.
  //   2. Extend ≤ 50 cm  — extension, near.
  //   3. Match ≤ 3 m      — span-only, far.   ← the one we keep
  //   4. Extend ≤ 3 m     — extension, far.
  // The two EXTENSION stages let probes reach past the source's
  // endpoints and graze far-off walls, which caused chaotic pairings, so
  // they're disabled for now. The near span-only stage (1) is redundant
  // with the far one (3) — anything it finds within 50 cm is also found
  // within 3 m — so a single far span-only pass covers it. The far range
  // is capped at 3 m (not unbounded) so a stray opposing wall on the
  // other side of the floor can't pair through empty space.
  //
  // Pairing model: each source runs this one stage and emits a midline
  // against EVERY parallel facing wall it sees (see `collectPartners`).
  // The per-pair dedup in `emitMidline` (via `emittedPairs`) skips an
  // `{S, C}` pairing that's already been recorded — including the
  // symmetric `{C, S}` rediscovery from C's own loop — so a shared wall
  // isn't drawn twice.
  const FAR_RANGE = 3.0;
  for (let sIdx = 0; sIdx < sources.length; sIdx++) {
    // 1. Span-only: probes stay within the source's [0, length] extent. This is
    //    the clean, primary match and handles the vast majority of walls.
    if (tryMultiProbe(sIdx, FAR_RANGE, false, 'pair-walls-match-inf')) continue;

    // 2. FALLBACK — extension search: only when span-only found NOTHING. Let the
    //    probes walk past the source's endpoints along its tangent (while still
    //    in wall material) to reach a facing wall a short, offset, or stepped
    //    source edge wouldn't see within its own span. Gated behind the span-only
    //    miss so it can't add noisy pairings where a clean facing wall already
    //    existed — it only rescues sources that would otherwise emit nothing.
    tryMultiProbe(sIdx, FAR_RANGE, true, 'pair-walls-extend');

    // If still nothing, emit nothing — a wall only exists when two polygon edges
    // actually face each other across wall material.
  }

  return segments;
}

/**
 * Pair every polygon edge in roomConnectivity (real rooms + synthetic outer
 * shell + cavities) with a facing edge across the wall material, and emit a
 * midline segment per pair. Output sits on `skeleton.pairedSegments` for the
 * next pass (which connects the loose segments into a real skeleton graph).
 *
 * The actual pairing is done by `computeWallMidlines`; this wrapper stores the
 * result and materializes the loose segments as preview nodes + edges so the
 * viewer renders them in isolation.
 */
export function pairWallsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity) return skeleton;

  const segments = computeWallMidlines(skeleton, kwargs);
  skeleton.pairedSegments = segments;

  // Materialize loose segments as preview nodes + edges so the existing
  // skeleton viz renders them even when no downstream connection pass
  // is enabled yet. `createNodesPass` / `createEdgesPass` both clear()
  // these maps on entry, so when the full pipeline runs the previews
  // get replaced cleanly. This makes the pass observable in isolation.
  skeleton.nodes.clear();
  skeleton.edges.clear();
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const roomIds = Array.from(new Set(seg.sources.map((s) => s.roomId)));
    const startId = `pair_n_${i}_a`;
    const endId = `pair_n_${i}_b`;
    skeleton.nodes.set(startId, {
      id: startId,
      position: seg.a.clone(),
      roomIds: roomIds.slice(),
      edgeIds: [`pair_e_${i}`],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    skeleton.nodes.set(endId, {
      id: endId,
      position: seg.b.clone(),
      roomIds: roomIds.slice(),
      edgeIds: [`pair_e_${i}`],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    skeleton.edges.set(`pair_e_${i}`, {
      id: `pair_e_${i}`,
      startNodeId: startId,
      endNodeId: endId,
      roomIds,
      leftSize: 0.1,
      rightSize: 0.1,
      wallType: seg.matched ? 'INNER' : 'OUTER',
      selected: false,
    });
  }

  return skeleton;
}

/**
 * Grow Pairs: extend every paired-segment's endpoints along its own
 * tangent until each end hits a polygon edge (room or outline). The
 * loose midlines from `pairWallsPass` only span the source-edge
 * extent; this pass reaches them out to the surrounding walls so the
 * subsequent merge step can find real intersections.
 *
 * No-op for ends that have no hit along their growth direction —
 * defensive, so a malformed input doesn't extend infinitely.
 *
 * Output: mutates `skeleton.pairedSegments` in place, and rebuilds
 * the preview nodes/edges so the viewer reflects the new endpoints.
 */
export function growPairsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const segments = skeleton.pairedSegments;
  if (!segments || segments.length === 0 || !skeleton.roomConnectivity) return skeleton;

  const z = skeleton.floorHeight;
  const mergeAngleDeg = kwargs?.growPairsMergeAngleDeg ?? 10;
  const mergePerpTol = kwargs?.growPairsMergePerpTol ?? 0.1;
  const mergeGapTol = kwargs?.growPairsMergeGapTol ?? 0.2;

  // Flatten polygon edges (room + outline) into a flat list once;
  // the grow step ray-casts against all of them. Each edge is
  // virtually extended by 5 cm at both ends so a grow ray that just
  // grazes past a wall's corner still counts as a hit — those
  // near-misses otherwise produce spurious long extensions to the
  // next wall over.
  //
  // Each entry also caches the wall-material-side normal: left-of-
  // travel `(-ty, tx)`. For every normalized polygon kind (rooms +
  // cavities CW, outer shells CCW), left-of-travel is the side where
  // the skeleton lives. The grow ray must approach from this side —
  // hitting the BACK of a polygon edge (from inside a room, or from
  // outside the outline) corresponds to a ray that punched through
  // the polygon interior first, and should be ignored.
  const POLY_EDGE_EXTENSION = 0.05;
  interface PolyEdge {
    sx: number;
    sy: number;
    ex: number;
    ey: number;
    /** Wall-material-side (= valid approach side) normal — unit. */
    nx: number;
    ny: number;
  }
  const polyEdges: PolyEdge[] = [];
  for (const room of skeleton.roomConnectivity) {
    const poly = room.polygon;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) {
        polyEdges.push({ sx: a.x, sy: a.y, ex: b.x, ey: b.y, nx: 0, ny: 0 });
        continue;
      }
      const ux = dx / len;
      const uy = dy / len;
      polyEdges.push({
        sx: a.x - ux * POLY_EDGE_EXTENSION,
        sy: a.y - uy * POLY_EDGE_EXTENSION,
        ex: b.x + ux * POLY_EDGE_EXTENSION,
        ey: b.y + uy * POLY_EDGE_EXTENSION,
        // Left-of-travel: (-ty, tx) where (tx, ty) is the edge tangent.
        nx: -uy,
        ny: ux,
      });
    }
  }

  /**
   * Cast a ray from `(ox, oy)` in direction `(dx, dy)` against every
   * polygon edge. Returns the smallest positive `t` such that
   * `origin + t·direction` lands on a polygon edge, or `Infinity` if
   * nothing is hit. Open-interval epsilon avoids self-hits when the
   * origin sits exactly on a polygon edge (which the source endpoints
   * do not, but extensions might).
   *
   * Rejects hits where the ray approaches the polygon edge from the
   * wrong side (i.e. ray direction aligns with the wall-material
   * normal instead of opposing it). That filters out hits where the
   * ray would have had to cross the polygon interior first — exactly
   * what you don't want when growing a skeleton segment outward to
   * its bounding walls.
   */
  const rayFirstPolygonHit = (ox: number, oy: number, dx: number, dy: number): number => {
    let best = Infinity;
    for (const E of polyEdges) {
      const wx = E.ex - E.sx;
      const wy = E.ey - E.sy;
      const denom = dx * wy - dy * wx;
      if (Math.abs(denom) < 1e-10) continue;
      const rx = E.sx - ox;
      const ry = E.sy - oy;
      const t = (rx * wy - ry * wx) / denom;
      const u = (rx * dy - ry * dx) / denom;
      const EPS = 1e-5;
      if (t <= EPS) continue;
      if (u < -EPS || u > 1 + EPS) continue;
      // Side check: the ray's direction must oppose the polygon
      // edge's wall-material-side normal — i.e. the ray must
      // approach the edge from the wall-material side. A positive
      // dot means the ray is leaving the wall material (or entered
      // it by punching through the polygon interior). Tiny epsilon
      // accepts perfectly grazing rays.
      const sideDot = dx * E.nx + dy * E.ny;
      if (sideDot > -1e-6) continue;
      if (t < best) best = t;
    }
    return best;
  };

  /**
   * Grow a single segment's endpoints outward from its midpoint
   * until each end hits a polygon edge. Casting from the midpoint
   * (rather than the endpoints) keeps the rays in clear wall
   * material so they can't self-hit or punch through an adjacent
   * polygon before finding the actual far wall.
   */
  const growSegment = (seg: PairedSegment) => {
    const dx = seg.b.x - seg.a.x;
    const dy = seg.b.y - seg.a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return;
    const tx = dx / len;
    const ty = dy / len;
    const midX = (seg.a.x + seg.b.x) * 0.5;
    const midY = (seg.a.y + seg.b.y) * 0.5;

    const tForward = rayFirstPolygonHit(midX, midY, tx, ty);
    if (tForward !== Infinity) {
      seg.b.set(midX + tx * tForward, midY + ty * tForward, z);
    }
    const tBackward = rayFirstPolygonHit(midX, midY, -tx, -ty);
    if (tBackward !== Infinity) {
      seg.a.set(midX - tx * tBackward, midY - ty * tBackward, z);
    }
  };

  for (const seg of segments) growSegment(seg);

  // Similar-edge merge: after growing, two paired segments may sit on
  // nearly the same line (with small angular drift and a small
  // perpendicular offset) and represent the same wall. Collapse them
  // into one averaged segment so the merge-pairs pass doesn't create
  // duplicate crossings.
  //
  // "Average" means each merged property is the literal mean of the
  // two inputs:
  //   - tangent       = normalize(A.tangent + B.tangent_aligned)
  //   - line position = passes through the midpoint of (A.mid + B.mid) / 2
  //   - endpoints (a, b) = pair up A.a↔B.a, A.b↔B.b (or A.a↔B.b, A.b↔B.a
  //     if B is antiparallel), project each pair onto the new line,
  //     and average them
  //
  // Tolerances are generous because grown endpoints can sit a few
  // millimeters off the theoretical line and the half-thickness
  // midline math drifts slightly between sources on the same wall.
  // Defaults: 10° angular, 10 cm perpendicular, 20 cm tangent gap.
  // Tunable via kwargs so the pipeline UI can dial these in per
  // dataset.
  const PARALLEL_DOT = Math.cos((mergeAngleDeg * Math.PI) / 180);
  const PERP_TOL = mergePerpTol;
  const GAP_TOL = mergeGapTol;

  /**
   * Cluster all collinear-overlapping segments into groups, then
   * replace each group with a single line fitted through ALL its
   * members at once. The fitted direction is the principal axis of
   * the group's endpoint cloud (weighted by segment length, so long
   * segments dominate), and the fitted position is the weighted
   * centroid. This avoids the drift the previous iterative pairwise
   * merge accumulated: a small noisy edge that "kicked off" a chain
   * could pull the whole chain off the true wall axis. With a single
   * fit, the long edges anchor the result and the short edges only
   * contribute their tiny share.
   */

  // Build groups via union-find over segment indices.
  const parent = new Int32Array(segments.length);
  for (let i = 0; i < segments.length; i++) parent[i] = i;
  const find = (a: number): number => {
    let r = a;
    while (parent[r] !== r) r = parent[r];
    let cur = a;
    while (parent[cur] !== r) {
      const next = parent[cur];
      parent[cur] = r;
      cur = next;
    }
    return r;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };

  for (let i = 0; i < segments.length; i++) {
    const A = segments[i];
    const aLen = Math.hypot(A.b.x - A.a.x, A.b.y - A.a.y);
    if (aLen < 1e-9) continue;
    const aTx = (A.b.x - A.a.x) / aLen;
    const aTy = (A.b.y - A.a.y) / aLen;
    for (let j = i + 1; j < segments.length; j++) {
      const B = segments[j];
      const bLen = Math.hypot(B.b.x - B.a.x, B.b.y - B.a.y);
      if (bLen < 1e-9) continue;
      const bTx = (B.b.x - B.a.x) / bLen;
      const bTy = (B.b.y - B.a.y) / bLen;
      const dot = aTx * bTx + aTy * bTy;
      if (Math.abs(dot) < PARALLEL_DOT) continue;

      // Perpendicular distance from B.a to A's line.
      const dx = B.a.x - A.a.x;
      const dy = B.a.y - A.a.y;
      const perp = Math.abs(dx * -aTy + dy * aTx);
      if (perp > PERP_TOL) continue;

      // Tangent-span proximity on A's tangent (touch / overlap).
      const tBa = dx * aTx + dy * aTy;
      const tBb = (B.b.x - A.a.x) * aTx + (B.b.y - A.a.y) * aTy;
      const tBLo = Math.min(tBa, tBb);
      const tBHi = Math.max(tBa, tBb);
      if (tBHi < -GAP_TOL || tBLo > aLen + GAP_TOL) continue;

      union(i, j);
    }
  }

  // Collect group members and the indices we'll keep / drop.
  const groups = new Map<number, number[]>();
  for (let i = 0; i < segments.length; i++) {
    const root = find(i);
    const arr = groups.get(root) ?? [];
    arr.push(i);
    groups.set(root, arr);
  }

  const dropIdx = new Set<number>();
  for (const [, members] of groups) {
    if (members.length < 2) continue;

    // Weighted centroid + 2x2 scatter matrix over endpoint cloud,
    // weighted by each segment's length. Each segment contributes
    // both endpoints with the same weight.
    let wSum = 0;
    let cx = 0;
    let cy = 0;
    for (const idx of members) {
      const s = segments[idx];
      const len = Math.hypot(s.b.x - s.a.x, s.b.y - s.a.y);
      if (len < 1e-9) continue;
      wSum += 2 * len;
      cx += (s.a.x + s.b.x) * len;
      cy += (s.a.y + s.b.y) * len;
    }
    if (wSum < 1e-9) continue;
    cx /= wSum;
    cy /= wSum;

    // Scatter matrix entries [Sxx Sxy; Sxy Syy].
    let Sxx = 0;
    let Syy = 0;
    let Sxy = 0;
    for (const idx of members) {
      const s = segments[idx];
      const len = Math.hypot(s.b.x - s.a.x, s.b.y - s.a.y);
      if (len < 1e-9) continue;
      for (const p of [s.a, s.b]) {
        const dx = p.x - cx;
        const dy = p.y - cy;
        Sxx += len * dx * dx;
        Syy += len * dy * dy;
        Sxy += len * dx * dy;
      }
    }

    // Principal direction = eigenvector of the larger eigenvalue of
    // the 2x2 scatter matrix. Closed-form:
    //   theta = 0.5 * atan2(2*Sxy, Sxx - Syy)
    // gives the angle of the major axis.
    const theta = 0.5 * Math.atan2(2 * Sxy, Sxx - Syy);
    let tx = Math.cos(theta);
    let ty = Math.sin(theta);

    // Pick the side of the eigenvector pair that aligns with the
    // first member's direction so antiparallel segments don't flip
    // the merged segment's a/b convention.
    {
      const first = segments[members[0]];
      const fLen = Math.hypot(first.b.x - first.a.x, first.b.y - first.a.y);
      if (fLen > 1e-9) {
        const fTx = (first.b.x - first.a.x) / fLen;
        const fTy = (first.b.y - first.a.y) / fLen;
        if (fTx * tx + fTy * ty < 0) {
          tx = -tx;
          ty = -ty;
        }
      }
    }

    // Project every endpoint of every member onto the fitted line
    // and take the union span. The merged segment is laid out along
    // the fitted direction from min to max projection.
    let tLo = Infinity;
    let tHi = -Infinity;
    for (const idx of members) {
      const s = segments[idx];
      for (const p of [s.a, s.b]) {
        const t = (p.x - cx) * tx + (p.y - cy) * ty;
        if (t < tLo) tLo = t;
        if (t > tHi) tHi = t;
      }
    }

    // Write the merged geometry into the lowest-index member,
    // mark the rest for deletion, and carry over their sources.
    const keepIdx = members[0];
    const keep = segments[keepIdx];
    keep.a.set(cx + tx * tLo, cy + ty * tLo, z);
    keep.b.set(cx + tx * tHi, cy + ty * tHi, z);
    for (let k = 1; k < members.length; k++) {
      const other = segments[members[k]];
      for (const src of other.sources) {
        const already = keep.sources.some((s) => s.roomId === src.roomId && s.edgeIndex === src.edgeIndex);
        if (!already) keep.sources.push(src);
      }
      keep.matched = keep.matched || other.matched;
      dropIdx.add(members[k]);
    }
  }

  if (dropIdx.size > 0) {
    // Splice from the end so indices stay valid as we delete.
    const sortedDrops = Array.from(dropIdx).sort((a, b) => b - a);
    for (const idx of sortedDrops) segments.splice(idx, 1);
  }

  // Re-grow after the merge: averaging two near-collinear segments
  // can shrink the merged segment compared to the inputs (the
  // averaged endpoints are the mean of corresponding endpoints, not
  // their union). Casting the grow rays again from the new midpoint
  // restores the endpoints to the surrounding polygon walls so the
  // downstream merge-pairs pass can still find proper crossings.
  for (const seg of segments) growSegment(seg);

  // Rebuild the preview nodes/edges so the in-pass visualization
  // reflects the new endpoint positions. Mirrors what
  // `pairWallsPass` does at its tail.
  skeleton.nodes.clear();
  skeleton.edges.clear();
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const roomIds = Array.from(new Set(seg.sources.map((s) => s.roomId)));
    const startId = `pair_n_${i}_a`;
    const endId = `pair_n_${i}_b`;
    skeleton.nodes.set(startId, {
      id: startId,
      position: seg.a.clone(),
      roomIds: roomIds.slice(),
      edgeIds: [`pair_e_${i}`],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    skeleton.nodes.set(endId, {
      id: endId,
      position: seg.b.clone(),
      roomIds: roomIds.slice(),
      edgeIds: [`pair_e_${i}`],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    skeleton.edges.set(`pair_e_${i}`, {
      id: `pair_e_${i}`,
      startNodeId: startId,
      endNodeId: endId,
      roomIds,
      leftSize: 0.1,
      rightSize: 0.1,
      wallType: seg.matched ? 'INNER' : 'OUTER',
      selected: false,
    });
  }

  return skeleton;
}

/**
 * Extend Pairs: extend each paired-segment endpoint outward along
 * its tangent line and connect it to the closest piece of other
 * graph geometry it finds. "Other geometry" for each other edge F
 * consists of three lines:
 *   - F's body (the infinite line through F's two endpoints).
 *   - F's two CAPS — lines perpendicular to F passing through each
 *     endpoint.
 *
 * Plus every room/outline polygon edge counts as a hit candidate
 * too (the green-highlighted walls in the floor plan).
 *
 * Across all candidates, the CLOSEST positive hit wins. At the
 * winning hit point:
 *
 *  - Body of another skeleton edge → insert a new node on F at the
 *    intersection point (splitting F into two sub-edges) and snap
 *    the extending endpoint to that node.
 *  - Cap of another skeleton edge → move the extending endpoint to
 *    the cap-intersection point and create a new edge from there
 *    to F's endpoint node that the cap passed through. Skip the
 *    new edge if one already exists between the two nodes.
 *  - Polygon wall edge → move the extending endpoint to the hit
 *    point and leave it as a free leaf (no other skeleton edge
 *    available to merge with there).
 *
 * After moving, if the extending endpoint coincides with any
 * existing node within `snapTol`, the two nodes merge.
 *
 * Endpoints that already have ≥ 2 incident edges (junctions) are
 * skipped — they're already anchored to other geometry.
 *
 * Output: writes `skeleton.nodes` + `skeleton.edges` directly.
 * Replaces `mergePairsPass` as the loose-segments → connected-graph
 * step.
 */
export function extendPairsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const segments = skeleton.pairedSegments;
  if (!segments || segments.length === 0) return skeleton;

  const z = skeleton.floorHeight;
  const MAX_EXTENSION = kwargs?.extendPairsMaxDistance ?? 5.0;
  const CAP_HALF_WIDTH = kwargs?.extendPairsCapHalfWidth ?? 0.15;

  // ── Build graph state from pairedSegments ────────────────────────
  const POSITION_EPS = 1e-3;
  const positionKey = (x: number, y: number) => `${Math.round(x / POSITION_EPS)}|${Math.round(y / POSITION_EPS)}`;

  interface NodeRec {
    id: string;
    x: number;
    y: number;
    edgeIds: Set<string>;
    /** Union of all room ids associated with edges meeting at this node. */
    roomIds: Set<string>;
  }
  interface EdgeRec {
    id: string;
    startNodeId: string;
    endNodeId: string;
    matched: boolean;
    /** Room ids inherited from the originating `PairedSegment` (or merged
     * across parent + neighbour when the edge was created during cap-hit
     * connection / split). Drives door/window cutter assignment downstream. */
    roomIds: Set<string>;
  }
  const nodes = new Map<string, NodeRec>();
  const edges = new Map<string, EdgeRec>();
  const nodeIdByKey = new Map<string, string>();
  let nodeCounter = 0;
  let edgeCounter = 0;

  const internNode = (x: number, y: number): NodeRec => {
    const key = positionKey(x, y);
    const existingId = nodeIdByKey.get(key);
    if (existingId) {
      const r = nodes.get(existingId);
      if (r) return r;
    }
    const id = `ext_n_${nodeCounter++}`;
    const rec: NodeRec = { id, x, y, edgeIds: new Set(), roomIds: new Set() };
    nodes.set(id, rec);
    nodeIdByKey.set(key, id);
    return rec;
  };

  const addEdge = (startId: string, endId: string, matched: boolean, roomIds: Iterable<string> = []): EdgeRec | null => {
    if (startId === endId) return null;
    // Skip duplicates — but still merge in any new roomIds so a cap-hit
    // connection that reuses an existing edge inherits the connecting
    // edge's room context too.
    for (const e of edges.values()) {
      if ((e.startNodeId === startId && e.endNodeId === endId) || (e.startNodeId === endId && e.endNodeId === startId)) {
        for (const rid of roomIds) e.roomIds.add(rid);
        return e;
      }
    }
    const id = `ext_e_${edgeCounter++}`;
    const rec: EdgeRec = { id, startNodeId: startId, endNodeId: endId, matched, roomIds: new Set(roomIds) };
    edges.set(id, rec);
    nodes.get(startId)?.edgeIds.add(id);
    nodes.get(endId)?.edgeIds.add(id);
    // Propagate the edge's roomIds onto its two endpoint nodes too, so
    // the final node-level roomIds reflect every wall meeting there.
    const sNode = nodes.get(startId);
    const eNode = nodes.get(endId);
    for (const rid of rec.roomIds) {
      sNode?.roomIds.add(rid);
      eNode?.roomIds.add(rid);
    }
    return rec;
  };

  // Seed graph from input segments. Carry each segment's source-edge
  // room context onto the seeded edge (and onto its endpoints via
  // addEdge) so the final skeleton edges/nodes know which rooms they
  // belong to — needed downstream for door/window cutter assignment.
  for (const seg of segments) {
    if (Math.hypot(seg.b.x - seg.a.x, seg.b.y - seg.a.y) < 1e-6) continue;
    const a = internNode(seg.a.x, seg.a.y);
    const b = internNode(seg.b.x, seg.b.y);
    const segRoomIds = seg.sources.map((s) => s.roomId);
    addEdge(a.id, b.id, seg.matched, segRoomIds);
  }

  // ── Polygon edges (room + outline) as additional hit candidates ──
  interface PolyEdge {
    sx: number;
    sy: number;
    ex: number;
    ey: number;
  }
  const polyEdges: PolyEdge[] = [];
  if (skeleton.roomConnectivity) {
    for (const room of skeleton.roomConnectivity) {
      const poly = room.polygon;
      for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        polyEdges.push({ sx: a.x, sy: a.y, ex: b.x, ey: b.y });
      }
    }
  }

  // ── Geometric primitives ─────────────────────────────────────────
  /**
   * Intersect a ray with a segment (parametrized 0..1). Returns the
   * ray distance and the segment parameter u, or null if the
   * intersection isn't within the segment's bounds.
   */
  const raySegmentIntersect = (ox: number, oy: number, dx: number, dy: number, sx: number, sy: number, ex: number, ey: number, maxRange: number): { t: number; u: number; px: number; py: number } | null => {
    const wx = ex - sx;
    const wy = ey - sy;
    const denom = dx * wy - dy * wx;
    if (Math.abs(denom) < 1e-10) return null;
    const rx = sx - ox;
    const ry = sy - oy;
    const t = (rx * wy - ry * wx) / denom;
    const u = (rx * dy - ry * dx) / denom;
    const EPS = 1e-5;
    if (t <= EPS) return null;
    if (t > maxRange) return null;
    if (u < -EPS || u > 1 + EPS) return null;
    return { t, u, px: ox + dx * t, py: oy + dy * t };
  };

  /**
   * For a ray going from (ox, oy) in direction (dx, dy), return the
   * distance to the first polygon-edge intersection within
   * `maxRange`, or Infinity if nothing is hit. Hits at distance less
   * than `nearOriginIgnore` are skipped — used to avoid registering
   * the polygon wall that the source endpoint is already touching
   * (otherwise body grow returns ~0 and the body's effective
   * extension is empty, which is exactly the bug we're seeing).
   */
  const POLY_NEAR_ORIGIN_IGNORE = 0.05; // 5 cm
  const polygonFirstHit = (ox: number, oy: number, dx: number, dy: number, maxRange: number): number => {
    let best = Infinity;
    for (const pe of polyEdges) {
      const hit = raySegmentIntersect(ox, oy, dx, dy, pe.sx, pe.sy, pe.ex, pe.ey, maxRange);
      if (hit && hit.t > POLY_NEAR_ORIGIN_IGNORE && hit.t < best) best = hit.t;
    }
    return best;
  };

  /**
   * Pre-compute each skeleton edge's "effective body" — the segment
   * it would grow into if extended along its tangent until hitting a
   * room/outline polygon wall on each side. Body-hit ray tests use
   * this extended segment rather than the edge's nominal extent, so
   * extensions can rightly target an edge's projected line even when
   * the line currently ends before reaching the cast ray.
   */
  const effectiveBody = new Map<string, { sx: number; sy: number; ex: number; ey: number }>();
  // Cap body growth WAY tighter than the endpoint extension range.
  // If we let bodies grow up to MAX_EXTENSION (5m), each edge's
  // effective body becomes a 10m segment, and crossings with such
  // a long virtual body produce arbitrary "this far edge crosses my
  // ray" hits — which is exactly the "cyan line punches through
  // everything" symptom. Keep bodies short enough that they only
  // attract endpoints to genuinely-nearby walls.
  const BODY_GROW_LIMIT = 1.0;
  // When no polygon wall blocks the body's growth, we still don't
  // want to extrapolate the line freely — that lets a tiny stub
  // edge act as if it stretches across the entire floor.
  const BODY_GROW_NO_HIT_CAP = 0.3;
  for (const edge of edges.values()) {
    const startNode = nodes.get(edge.startNodeId);
    const endNode = nodes.get(edge.endNodeId);
    if (!startNode || !endNode) continue;
    const fdx = endNode.x - startNode.x;
    const fdy = endNode.y - startNode.y;
    const fLen = Math.hypot(fdx, fdy);
    if (fLen < 1e-9) continue;
    const ftx = fdx / fLen;
    const fty = fdy / fLen;
    // Grow PAST endNode along (+ftx, +fty) until a polygon blocks.
    const tForward = polygonFirstHit(endNode.x, endNode.y, ftx, fty, BODY_GROW_LIMIT);
    const tBackward = polygonFirstHit(startNode.x, startNode.y, -ftx, -fty, BODY_GROW_LIMIT);
    const growForward = tForward === Infinity ? BODY_GROW_NO_HIT_CAP : tForward;
    const growBackward = tBackward === Infinity ? BODY_GROW_NO_HIT_CAP : tBackward;
    effectiveBody.set(edge.id, {
      sx: startNode.x - ftx * growBackward,
      sy: startNode.y - fty * growBackward,
      ex: endNode.x + ftx * growForward,
      ey: endNode.y + fty * growForward,
    });
  }

  // Tag what kind of geometry we hit so the resolver knows what to do.
  type HitKind = { kind: 'body'; edge: EdgeRec; u: number } | { kind: 'cap'; edge: EdgeRec; capNode: NodeRec } | { kind: 'polygon' };
  interface Hit {
    t: number;
    px: number;
    py: number;
    info: HitKind;
  }

  // Brute-force diagnostic — when verbose logging is on (toggle via
  // EXTEND_PAIRS_VERBOSE), `findClosestHit` records every candidate
  // considered into `lastConsidered`. The caller dumps the sorted
  // top-N alongside the winning hit to figure out why a far hit
  // beat a closer one.
  const EXTEND_PAIRS_VERBOSE = true;
  let lastConsidered: Array<{ t: number; kind: string }> = [];

  const findClosestHit = (ox: number, oy: number, dx: number, dy: number, selfEdgeId: string): Hit | null => {
    let best: Hit | null = null;
    const considered: Array<{ t: number; kind: string }> = [];
    const consider = (cand: Hit) => {
      if (EXTEND_PAIRS_VERBOSE) {
        const k = cand.info.kind === 'polygon' ? 'polygon' : `${cand.info.kind} of ${cand.info.edge.id}`;
        considered.push({ t: cand.t, kind: k });
      }
      if (!best || cand.t < best.t) best = cand;
    };
    // Stash for the caller to dump after findClosestHit returns.
    lastConsidered = considered;

    // WallSketch edges: body (as a segment) and caps (perpendicular
    // segments at each endpoint).
    for (const edge of edges.values()) {
      if (edge.id === selfEdgeId) continue;
      const startNode = nodes.get(edge.startNodeId);
      const endNode = nodes.get(edge.endNodeId);
      if (!startNode || !endNode) continue;
      const fdx = endNode.x - startNode.x;
      const fdy = endNode.y - startNode.y;
      const fLen = Math.hypot(fdx, fdy);
      if (fLen < 1e-9) continue;
      const ftx = fdx / fLen;
      const fty = fdy / fLen;

      // Body: ray vs F's EFFECTIVE body — the segment from F's
      // backward-grown end to its forward-grown end (each end
      // extended until a polygon wall blocked it). Computed once
      // before the main loop and cached in `effectiveBody`.
      const eb = effectiveBody.get(edge.id);
      let bodyEffectiveT: number | null = null;
      if (eb) {
        const bodyHit = raySegmentIntersect(ox, oy, dx, dy, eb.sx, eb.sy, eb.ex, eb.ey, MAX_EXTENSION);
        if (bodyHit) {
          bodyEffectiveT = bodyHit.t;
          consider({ t: bodyHit.t, px: bodyHit.px, py: bodyHit.py, info: { kind: 'body', edge, u: bodyHit.u } });
        }
      }
      // Also compute what the body's `t` would have been if treated
      // as an INFINITE line — used by the post-resolution log below
      // to detect cases where the effective-body cap clipped what
      // should have been the closest hit.
      let bodyInfiniteT: number | null = null;
      {
        const wx = endNode.x - startNode.x;
        const wy = endNode.y - startNode.y;
        const denom = dx * wy - dy * wx;
        if (Math.abs(denom) >= 1e-10) {
          const rx = startNode.x - ox;
          const ry = startNode.y - oy;
          const tInf = (rx * wy - ry * wx) / denom;
          if (tInf > 1e-5 && tInf <= MAX_EXTENSION) bodyInfiniteT = tInf;
        }
      }

      // Caps: FINITE perpendicular segments through each endpoint,
      // straddling the endpoint by `CAP_HALF_WIDTH` on each side
      // (total length = 2 * CAP_HALF_WIDTH). Direction is (-fty,
      // ftx) — perpendicular to F's tangent. Modeled as a segment
      // from capStart to capEnd so a ray that grazes past the cap
      // (beyond the wall thickness) doesn't register a false hit.
      let bestCapTForEdge: number | null = null;
      for (const capNode of [startNode, endNode]) {
        const capDirX = -fty;
        const capDirY = ftx;
        const capStartX = capNode.x - capDirX * CAP_HALF_WIDTH;
        const capStartY = capNode.y - capDirY * CAP_HALF_WIDTH;
        const capEndX = capNode.x + capDirX * CAP_HALF_WIDTH;
        const capEndY = capNode.y + capDirY * CAP_HALF_WIDTH;
        const capHit = raySegmentIntersect(ox, oy, dx, dy, capStartX, capStartY, capEndX, capEndY, MAX_EXTENSION);
        if (capHit) {
          if (bestCapTForEdge === null || capHit.t < bestCapTForEdge) bestCapTForEdge = capHit.t;
          consider({ t: capHit.t, px: capHit.px, py: capHit.py, info: { kind: 'cap', edge, capNode } });
        }
      }

      // Diagnostic: if a cap landed before the body but the body
      // would have been closer treating F as an infinite line, log
      // the cap-cut-the-line case. This is the "ray went through the
      // body but a cap won" pattern we're investigating.
      if (bestCapTForEdge !== null && bodyInfiniteT !== null && bodyInfiniteT < bestCapTForEdge - 1e-6) {
        const bodyExtT = bodyEffectiveT !== null ? bodyEffectiveT.toFixed(3) : 'unreachable';
        const ebForLog = effectiveBody.get(edge.id);
        const ebDesc = ebForLog
          ? `effBody=[(${ebForLog.sx.toFixed(2)},${ebForLog.sy.toFixed(2)})→(${ebForLog.ex.toFixed(2)},${ebForLog.ey.toFixed(2)})] origNode=[(${startNode.x.toFixed(2)},${startNode.y.toFixed(2)})→(${endNode.x.toFixed(2)},${endNode.y.toFixed(2)})]`
          : 'no effectiveBody record';
        console.warn(
          `extendPairsPass: edge ${edge.id} cap won at t=${bestCapTForEdge.toFixed(3)} but body infinite-t=${bodyInfiniteT.toFixed(3)} (effective-body t=${bodyExtT}). ${ebDesc}. cast from=(${ox.toFixed(2)},${oy.toFixed(2)}) dir=(${dx.toFixed(2)},${dy.toFixed(2)})`,
        );
      }
    }

    // Polygon edges (room + outline) — counted as hit candidates,
    // not blockers. The closest polygon hit competes with skeleton
    // hits.
    for (const pe of polyEdges) {
      const peHit = raySegmentIntersect(ox, oy, dx, dy, pe.sx, pe.sy, pe.ex, pe.ey, MAX_EXTENSION);
      if (peHit) consider({ t: peHit.t, px: peHit.px, py: peHit.py, info: { kind: 'polygon' } });
    }

    return best;
  };

  // ── Resolve a winning hit into graph mutations ───────────────────
  // No merging. Just move the node's coordinates. The caller decides
  // whether to connect via a new edge or insert via a split.
  const moveNodeTo = (node: NodeRec, x: number, y: number): NodeRec => {
    const oldKey = positionKey(node.x, node.y);
    if (nodeIdByKey.get(oldKey) === node.id) nodeIdByKey.delete(oldKey);
    node.x = x;
    node.y = y;
    nodeIdByKey.set(positionKey(x, y), node.id);
    return node;
  };

  const splitEdgeAt = (edge: EdgeRec, x: number, y: number): NodeRec => {
    const startNode = nodes.get(edge.startNodeId);
    const endNode = nodes.get(edge.endNodeId);
    if (!startNode || !endNode) throw new Error('split: invalid edge');
    const splitNode = internNode(x, y);
    // Carry the parent's effective body over to the two sub-edges
    // before we delete it — they share the parent's tangent line, so
    // the same precomputed extension applies. Without this, ray-casts
    // against the sub-edges have no body candidate and fall back to
    // cap-only hits.
    const parentBody = effectiveBody.get(edge.id);
    edges.delete(edge.id);
    effectiveBody.delete(edge.id);
    startNode.edgeIds.delete(edge.id);
    endNode.edgeIds.delete(edge.id);
    // Sub-edges inherit the parent's room context — they're geometrically
    // a slice of the same wall, so any door/window belonging to the
    // parent's room set should still match either sub-edge.
    const subA = splitNode.id !== startNode.id ? addEdge(startNode.id, splitNode.id, edge.matched, edge.roomIds) : null;
    const subB = splitNode.id !== endNode.id ? addEdge(splitNode.id, endNode.id, edge.matched, edge.roomIds) : null;
    if (parentBody) {
      if (subA) effectiveBody.set(subA.id, parentBody);
      if (subB) effectiveBody.set(subB.id, parentBody);
    }
    // Sub-edges are NOT enqueued. We only extend the ORIGINAL pair
    // segments — the connect step is a one-shot operation per
    // original edge. Re-extending split sub-edges would let an
    // original edge's split chain self-propagate, with one split
    // creating more splits, never converging on a clean graph.
    return splitNode;
  };

  // ── Per-endpoint extension ───────────────────────────────────────
  // Queue-based traversal: every edge that exists at the start gets
  // queued, and any edge that's CREATED during the run (via
  // splitEdgeAt) also gets queued so its new endpoints can extend
  // outward too. Cycle protection via `processedEdgeIds`: an edge
  // never processes more than once, with a hard cap on total
  // iterations as a runaway safeguard.
  const queue: EdgeRec[] = Array.from(edges.values());
  const processedEdgeIds = new Set<string>();
  const PROCESS_CAP = 10000;

  const extendEnd = (edge: EdgeRec, side: 'start' | 'end'): void => {
    if (!edges.has(edge.id)) return; // already split out
    const tail = side === 'start' ? nodes.get(edge.startNodeId) : nodes.get(edge.endNodeId);
    const other = side === 'start' ? nodes.get(edge.endNodeId) : nodes.get(edge.startNodeId);
    if (!tail || !other) return;

    // Skip endpoints already at junctions (≥ 2 connections).
    if (tail.edgeIds.size >= 2) return;

    const dx = tail.x - other.x;
    const dy = tail.y - other.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return;
    const tx = dx / len;
    const ty = dy / len;

    const hit = findClosestHit(tail.x, tail.y, tx, ty, edge.id);
    if (!hit) {
      console.debug(`extendPairsPass: edge ${edge.id} side=${side} from=(${tail.x.toFixed(2)},${tail.y.toFixed(2)}) dir=(${tx.toFixed(2)},${ty.toFixed(2)}) — NO HIT`);
      return;
    }
    const hitDesc = hit.info.kind === 'polygon' ? 'polygon' : `${hit.info.kind} of ${hit.info.edge.id}`;
    console.debug(`extendPairsPass: edge ${edge.id} side=${side} hit=${hitDesc} t=${hit.t.toFixed(3)} at=(${hit.px.toFixed(2)},${hit.py.toFixed(2)})`);
    // Dump the top 10 candidates by t so we can see what else was
    // in the running. If the winner looks far when a near target
    // exists, the near target either wasn't tested (not in this
    // list) or was tested but got a higher t (numerical issue).
    const sorted = [...lastConsidered].sort((a, b) => a.t - b.t).slice(0, 10);
    console.debug(`  candidates (top 10 sorted): ` + sorted.map((c) => `${c.kind}@${c.t.toFixed(3)}`).join(', '));

    if (hit.info.kind === 'polygon') {
      // Polygon hit: relocate the tail to the hit position. No new
      // node, no new edge — the existing endpoint just moves.
      moveNodeTo(tail, hit.px, hit.py);
      return;
    }

    if (hit.info.kind === 'body') {
      // Body hit: move the tail to the hit point. Then either insert a
      // node on the hit edge (when the hit lies on the nominal
      // segment) or connect to the nearer endpoint (when the hit lies
      // on the effective-body extension past one nominal endpoint).
      const hitEdge = hit.info.edge;
      const startNode = nodes.get(hitEdge.startNodeId);
      const endNode = nodes.get(hitEdge.endNodeId);
      if (!startNode || !endNode) return;
      const sex = endNode.x - startNode.x;
      const sey = endNode.y - startNode.y;
      const sLen2 = sex * sex + sey * sey;
      if (sLen2 < 1e-12) return;
      const uNominal = ((hit.px - startNode.x) * sex + (hit.py - startNode.y) * sey) / sLen2;
      const ON_LINE_EPS = 1e-3;
      moveNodeTo(tail, hit.px, hit.py);
      // Combine the SOURCE edge's room context with the HIT edge's so
      // the new connector edge carries the union — it's geometrically
      // a junction between two walls and either room's openings can
      // reasonably project through it.
      const connectorRoomIds = new Set<string>([...edge.roomIds, ...hitEdge.roomIds]);
      if (uNominal > ON_LINE_EPS && uNominal < 1 - ON_LINE_EPS) {
        // Hit lies on the nominal segment → split the hit edge at the
        // hit point and connect the moved tail to the new split node.
        const split = splitEdgeAt(hitEdge, hit.px, hit.py);
        addEdge(tail.id, split.id, edge.matched, connectorRoomIds);
      } else {
        // Hit lies on the extension past one nominal endpoint → just
        // connect the moved tail to that endpoint.
        const targetNode = uNominal <= 0.5 ? startNode : endNode;
        addEdge(tail.id, targetNode.id, edge.matched, connectorRoomIds);
      }
      return;
    }

    // Cap hit: move tail to the cap-intersection point, then connect
    // it to F's cap endpoint node. The connecting edge inherits both
    // the source edge's and the hit edge's room context.
    moveNodeTo(tail, hit.px, hit.py);
    const capNode = nodes.get(hit.info.capNode.id) ?? hit.info.capNode;
    const hitEdgeForCap = hit.info.edge;
    const capConnectorRoomIds = new Set<string>([...edge.roomIds, ...hitEdgeForCap.roomIds]);
    addEdge(tail.id, capNode.id, edge.matched, capConnectorRoomIds);
  };

  let iterations = 0;
  while (queue.length > 0) {
    if (++iterations > PROCESS_CAP) {
      console.warn(`extendPairsPass: hit PROCESS_CAP (${PROCESS_CAP}) — aborting to avoid runaway. Remaining queue: ${queue.length}`);
      break;
    }
    const seed = queue.shift();
    if (!seed) break;
    if (processedEdgeIds.has(seed.id)) continue;
    processedEdgeIds.add(seed.id);
    extendEnd(seed, 'start');
    extendEnd(seed, 'end');
  }

  // ── Write results into skeleton.nodes / skeleton.edges ───────────
  // Recompute each node's final roomIds as the union of its incident
  // edges' roomIds — splits and cap connections may have updated the
  // edges after their endpoint nodes were first created, and we want
  // the node-level set to reflect the current graph state.
  for (const n of nodes.values()) {
    n.roomIds.clear();
    for (const eid of n.edgeIds) {
      const e = edges.get(eid);
      if (!e) continue;
      for (const rid of e.roomIds) n.roomIds.add(rid);
    }
  }
  skeleton.nodes.clear();
  skeleton.edges.clear();
  for (const n of nodes.values()) {
    if (n.edgeIds.size === 0) continue;
    skeleton.nodes.set(n.id, {
      id: n.id,
      position: new THREE.Vector3(n.x, n.y, z),
      roomIds: Array.from(n.roomIds),
      edgeIds: Array.from(n.edgeIds),
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
  }
  for (const e of edges.values()) {
    if (!skeleton.nodes.has(e.startNodeId) || !skeleton.nodes.has(e.endNodeId)) continue;
    skeleton.edges.set(e.id, {
      id: e.id,
      startNodeId: e.startNodeId,
      endNodeId: e.endNodeId,
      roomIds: Array.from(e.roomIds),
      leftSize: 0.1,
      rightSize: 0.1,
      wallType: e.matched ? 'INNER' : 'OUTER',
      selected: false,
    });
  }

  return skeleton;
}

/**
 * Merge Pairs: turn the (already grown) `pairedSegments` into a
 * connected node graph.
 *
 *  1. Find every skeleton↔skeleton crossing between segments. Each
 *     crossing point becomes one shared node referenced by both
 *     segments.
 *  2. Split every segment at its intersection points so consecutive
 *     nodes along the segment are linked by sub-edges.
 *  3. Trim grown endpoints whose side has ≥ 1 intersection: walk from
 *     the segment midpoint toward each end; if at least one crossing
 *     exists on that side, the original grown endpoint and the
 *     sub-edge connecting to it are discarded (the outermost
 *     crossing on that side becomes the new leaf).
 *  4. Endpoints with no intersection on their side stay as leaf
 *     nodes (free ends sitting on the polygon walls).
 *
 * Output: writes `skeleton.nodes` + `skeleton.edges` directly so the
 * result is the connected graph the rest of the pipeline expects.
 */
export function mergePairsPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const segments = skeleton.pairedSegments;
  if (!segments || segments.length === 0) return skeleton;

  // Snap small position differences together when keying intersection
  // nodes — two segments that cross at the same geometric point may
  // produce slightly different float results from each pairing.
  const POSITION_EPS = 1e-4;
  const positionKey = (x: number, y: number) => `${Math.round(x / POSITION_EPS)}|${Math.round(y / POSITION_EPS)}`;

  /**
   * Per segment, the list of ordered breakpoints along its tangent
   * (in parameter `t ∈ [0, length]`) along with the node id assigned
   * to each breakpoint. The two endpoints (`t=0` and `t=length`)
   * always start in the list as their own provisional nodes.
   */
  interface SegState {
    segIdx: number;
    /** Cached source endpoints + tangent + length. */
    ax: number;
    ay: number;
    bx: number;
    by: number;
    tx: number;
    ty: number;
    length: number;
    /** Breakpoints along the segment, sorted by t. */
    breakpoints: Array<{ t: number; nodeId: string }>;
  }

  const states: SegState[] = [];
  const nodeIdByKey = new Map<string, string>();
  const nodePositions = new Map<string, { x: number; y: number }>();
  let nodeIdCounter = 0;

  const internNodeAt = (x: number, y: number): string => {
    const key = positionKey(x, y);
    const existing = nodeIdByKey.get(key);
    if (existing) return existing;
    const id = `gc_n_${nodeIdCounter++}`;
    nodeIdByKey.set(key, id);
    nodePositions.set(id, { x, y });
    return id;
  };

  // Seed each segment with its two endpoints as the initial
  // breakpoints. These are the grown endpoints from `growPairsPass`
  // — they may get pruned later if their side has crossings.
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const ax = seg.a.x;
    const ay = seg.a.y;
    const bx = seg.b.x;
    const by = seg.b.y;
    const dx = bx - ax;
    const dy = by - ay;
    const length = Math.hypot(dx, dy);
    if (length < 1e-9) continue;
    const tx = dx / length;
    const ty = dy / length;
    const aId = internNodeAt(ax, ay);
    const bId = internNodeAt(bx, by);
    states.push({
      segIdx: i,
      ax,
      ay,
      bx,
      by,
      tx,
      ty,
      length,
      breakpoints: [
        { t: 0, nodeId: aId },
        { t: length, nodeId: bId },
      ],
    });
  }

  /**
   * Compute the intersection (in t-parameters of each segment) of
   * two infinite lines through the seg states. Returns null if
   * parallel, or if the intersection is outside either segment's
   * (open) range — endpoint-touching crossings aren't intersections
   * we should split on.
   */
  const segmentIntersection = (A: SegState, B: SegState): { tA: number; tB: number; x: number; y: number } | null => {
    const denom = A.tx * B.ty - A.ty * B.tx;
    if (Math.abs(denom) < 1e-10) return null;
    const rx = B.ax - A.ax;
    const ry = B.ay - A.ay;
    const tA = (rx * B.ty - ry * B.tx) / denom;
    const tB = (rx * A.ty - ry * A.tx) / denom;
    const EPS = 1e-4;
    if (tA < EPS || tA > A.length - EPS) return null;
    if (tB < EPS || tB > B.length - EPS) return null;
    return { tA, tB, x: A.ax + A.tx * tA, y: A.ay + A.ty * tA };
  };

  // Pairwise crossing detection. For each pair, allocate a shared
  // node at the intersection and record the breakpoint on each side.
  for (let i = 0; i < states.length; i++) {
    for (let j = i + 1; j < states.length; j++) {
      const A = states[i];
      const B = states[j];
      const hit = segmentIntersection(A, B);
      if (!hit) continue;
      const nodeId = internNodeAt(hit.x, hit.y);
      A.breakpoints.push({ t: hit.tA, nodeId });
      B.breakpoints.push({ t: hit.tB, nodeId });
    }
  }

  // Sort each segment's breakpoints and dedupe nodes that landed on
  // (essentially) the same t — a stray near-endpoint intersection
  // shouldn't split into a zero-length sub-edge.
  for (const S of states) {
    S.breakpoints.sort((p, q) => p.t - q.t);
    const dedup: Array<{ t: number; nodeId: string }> = [];
    for (const bp of S.breakpoints) {
      const prev = dedup[dedup.length - 1];
      if (prev && Math.abs(prev.t - bp.t) < 1e-4) continue;
      dedup.push(bp);
    }
    S.breakpoints = dedup;
  }

  // Trim per side: if a segment has any intersection node strictly
  // between its endpoints (i.e. breakpoints.length > 2), drop the
  // endpoint(s) on whichever side has at least one inner breakpoint.
  // Concretely:
  //   - Inner breakpoints are at indices 1..length-2.
  //   - "Side A" = breakpoints with t < midT; "side B" = t > midT.
  //   - If side A has any inner breakpoint, drop index 0.
  //   - If side B has any inner breakpoint, drop the last index.
  for (const S of states) {
    if (S.breakpoints.length < 3) continue;
    const midT = S.length * 0.5;
    let hasInnerA = false;
    let hasInnerB = false;
    for (let k = 1; k < S.breakpoints.length - 1; k++) {
      const t = S.breakpoints[k].t;
      if (t < midT) hasInnerA = true;
      else if (t > midT) hasInnerB = true;
    }
    if (hasInnerB) S.breakpoints.pop();
    if (hasInnerA) S.breakpoints.shift();
  }

  // Emit nodes + edges. Each segment becomes a chain of sub-edges
  // between consecutive remaining breakpoints. Endpoints that didn't
  // get trimmed remain as leaf nodes (free ends).
  skeleton.nodes.clear();
  skeleton.edges.clear();

  const usedNodeIds = new Set<string>();
  let edgeIdCounter = 0;
  for (const S of states) {
    if (S.breakpoints.length < 2) continue;
    const seg = segments[S.segIdx];
    const roomIds = Array.from(new Set(seg.sources.map((s) => s.roomId)));
    for (let k = 0; k < S.breakpoints.length - 1; k++) {
      const bp0 = S.breakpoints[k];
      const bp1 = S.breakpoints[k + 1];
      if (bp0.nodeId === bp1.nodeId) continue;
      const edgeId = `gc_e_${edgeIdCounter++}`;
      skeleton.edges.set(edgeId, {
        id: edgeId,
        startNodeId: bp0.nodeId,
        endNodeId: bp1.nodeId,
        roomIds,
        leftSize: 0.1,
        rightSize: 0.1,
        wallType: seg.matched ? 'INNER' : 'OUTER',
        selected: false,
      });
      usedNodeIds.add(bp0.nodeId);
      usedNodeIds.add(bp1.nodeId);
    }
  }

  // Materialize only the nodes that are referenced by surviving
  // edges. Trimmed-away endpoints whose intersection node was kept
  // still appear; nodes referenced by no edge get dropped.
  for (const nodeId of usedNodeIds) {
    const pos = nodePositions.get(nodeId);
    if (!pos) continue;
    skeleton.nodes.set(nodeId, {
      id: nodeId,
      position: new THREE.Vector3(pos.x, pos.y, skeleton.floorHeight),
      roomIds: [],
      edgeIds: [],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
  }

  // Backfill each node's edge list from the final edge map so
  // downstream passes that read `node.edgeIds` see a consistent view.
  for (const edge of skeleton.edges.values()) {
    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (startNode) startNode.edgeIds.push(edge.id);
    if (endNode) endNode.edgeIds.push(edge.id);
  }

  return skeleton;
}

/**
 * Create nodes from expanded vertices (one node per vertex, no merging)
 * This is the first pass in the pipeline - creates raw nodes from expanded vertices.
 */
export function createNodesPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.expandedVertices) {
    return skeleton;
  }

  // Clear existing nodes and create fresh mapping
  skeleton.nodes.clear();
  skeleton.vertexToNodeMap = new Map<number, string>();

  let nodeIdCounter = 0;

  for (const vertex of skeleton.expandedVertices) {
    const nodeId = `node_${nodeIdCounter++}`;
    const node: SkeletonNode = {
      id: nodeId,
      position: vertex.position.clone(),
      roomIds: [vertex.roomId],
      edgeIds: [],
      sourceVertices: [{ roomId: vertex.roomId, vertexIndex: vertex.vertexIndexInRoom }],
      probedHits: vertex.probedHits.slice(),
      selected: false,
    };
    skeleton.nodes.set(nodeId, node);
    skeleton.vertexToNodeMap.set(vertex.originalIndex, nodeId);
  }

  return skeleton;
}

/**
 * Merge nodeB into nodeA, updating all references.
 * nodeB is removed from the skeleton after merging.
 */
export function mergeNodes(skeleton: FloorSkeleton, nodeA: SkeletonNode, nodeB: SkeletonNode): void {
  // Update position to weighted centroid. Weight is share of
  // contributing room IDs — but when both sides have NO rooms (e.g.
  // nodes created by `extendPairsPass`, which doesn't populate
  // `roomIds`), the original `weight = nodeB.roomIds.length /
  // (nodeA.roomIds.length + nodeB.roomIds.length)` produced 0/0 =
  // NaN, poisoning nodeA's position and rippling NaN through every
  // subsequent merge. Fall back to a 50/50 midpoint when neither
  // side has room data.
  const totalRoomCount = nodeA.roomIds.length + nodeB.roomIds.length;
  const weight = totalRoomCount > 0 ? nodeB.roomIds.length / totalRoomCount : 0.5;
  nodeA.position.lerp(nodeB.position, weight);

  // Merge room IDs
  for (const roomId of nodeB.roomIds) {
    if (!nodeA.roomIds.includes(roomId)) {
      nodeA.roomIds.push(roomId);
    }
  }

  // Merge source-vertex contributions
  for (const sv of nodeB.sourceVertices) {
    nodeA.sourceVertices.push(sv);
  }

  // Merge probed-hit contributions
  for (const hit of nodeB.probedHits) {
    nodeA.probedHits.push(hit);
  }

  // Redirect edges from nodeB to nodeA
  for (const edgeId of nodeB.edgeIds) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;

    if (edge.startNodeId === nodeB.id) {
      edge.startNodeId = nodeA.id;
    }
    if (edge.endNodeId === nodeB.id) {
      edge.endNodeId = nodeA.id;
    }

    if (!nodeA.edgeIds.includes(edgeId)) {
      nodeA.edgeIds.push(edgeId);
    }

    // Remove self-loops
    if (edge.startNodeId === edge.endNodeId) {
      skeleton.edges.delete(edgeId);
      nodeA.edgeIds = nodeA.edgeIds.filter((id) => id !== edgeId);
    }
  }

  // Update vertexToNodeMap to point to nodeA instead of nodeB
  if (skeleton.vertexToNodeMap) {
    for (const [vertexIndex, nodeId] of skeleton.vertexToNodeMap) {
      if (nodeId === nodeB.id) {
        skeleton.vertexToNodeMap.set(vertexIndex, nodeA.id);
      }
    }
  }

  // Remove duplicate edges
  const edgeSignatures = new Map<string, string>();
  const duplicateEdges: string[] = [];

  for (const edgeId of nodeA.edgeIds) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;

    const sig = [edge.startNodeId, edge.endNodeId].sort().join('-');
    if (edgeSignatures.has(sig)) {
      const existingEdgeId = edgeSignatures.get(sig);
      const existingEdge = existingEdgeId ? skeleton.edges.get(existingEdgeId) : undefined;
      if (existingEdge) {
        for (const roomId of edge.roomIds) {
          if (!existingEdge.roomIds.includes(roomId)) {
            existingEdge.roomIds.push(roomId);
          }
        }
      }
      duplicateEdges.push(edgeId);
    } else {
      edgeSignatures.set(sig, edgeId);
    }
  }

  for (const edgeId of duplicateEdges) {
    const edge = skeleton.edges.get(edgeId);
    if (edge) {
      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);
      if (startNode) {
        startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
      }
      if (endNode) {
        endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
      }
      skeleton.edges.delete(edgeId);
    }
  }

  // Remove nodeB
  skeleton.nodes.delete(nodeB.id);
}

/**
 * Merge close nodes within tolerance distance.
 * This merges nodes that are spatially close, updating the vertexToNodeMap accordingly.
 */
export function mergeCloseNodesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const mergeTolerance = kwargs?.mergeTolerance ?? 0.15;
  const maxIterations = 500;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let merged = false;
    const nodeIds = Array.from(skeleton.nodes.keys());

    for (let i = 0; i < nodeIds.length && !merged; i++) {
      for (let j = i + 1; j < nodeIds.length && !merged; j++) {
        const nodeA = skeleton.nodes.get(nodeIds[i]);
        const nodeB = skeleton.nodes.get(nodeIds[j]);

        if (!nodeA || !nodeB) continue;

        const distance = nodeA.position.distanceTo(nodeB.position);
        if (distance >= mergeTolerance) continue;

        mergeNodes(skeleton, nodeA, nodeB);
        merged = true;
      }
    }

    if (!merged) break;
  }

  return skeleton;
}

/**
 * Remove collinear nodes pass.
 *
 * For each node with exactly two incident edges whose tangents are
 * within `straightenAngleThreshold` degrees of anti-parallel (i.e.
 * the node sits on a straight line between its two neighbors),
 * dissolve the node and replace the two edges with a single edge
 * directly connecting the neighbors.
 *
 * Skipped when:
 *  - the dissolution would create a self-loop (both neighbors are
 *    the same node)
 *  - an edge already exists between the two neighbors (no duplicates)
 *  - one of the edges has additional metadata that can't merge
 *    cleanly (currently nothing — both edges inherit from one side)
 *
 * The merged edge keeps `roomIds` from both originals and preserves
 * the `leftSize` / `rightSize` of whichever edge was longer, since
 * those measurements were taken further from the (now-dissolved)
 * intermediate node.
 *
 * Iterates until no more candidates exist; dissolving one node can
 * make an adjacent node newly degree-2 and collinear.
 */
export function removeCollinearNodesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const angleDeg = kwargs?.straightenAngleThreshold ?? 15;
  // Cosine threshold for "anti-parallel within `angleDeg`": two
  // vectors pointing in opposite directions from the node yield
  // cos ≈ -1. We allow a deviation of `angleDeg` from 180°, so the
  // cosine must be ≤ cos(180° - angleDeg) = -cos(angleDeg).
  const cosThreshold = -Math.cos((angleDeg * Math.PI) / 180);

  // Optional equal-thickness gate: only dissolve when the two incident edges have
  // roughly the same wall thickness (left+right), within this relative tolerance.
  // Default ±3%; ≤0 disables.
  const thicknessTol = kwargs?.removeCollinearThicknessTol ?? 0.03;

  const maxIterations = 1000;
  for (let iter = 0; iter < maxIterations; iter++) {
    let dissolvedAny = false;

    // Snapshot node ids — the map mutates as we dissolve.
    const nodeIds = Array.from(skeleton.nodes.keys());
    for (const nodeId of nodeIds) {
      const node = skeleton.nodes.get(nodeId);
      if (!node || node.edgeIds.length !== 2) continue;

      const e1 = skeleton.edges.get(node.edgeIds[0]);
      const e2 = skeleton.edges.get(node.edgeIds[1]);
      if (!e1 || !e2) {
        continue;
      }

      const other1Id = e1.startNodeId === node.id ? e1.endNodeId : e1.startNodeId;
      const other2Id = e2.startNodeId === node.id ? e2.endNodeId : e2.startNodeId;
      if (other1Id === other2Id) {
        continue;
      }
      const other1 = skeleton.nodes.get(other1Id);
      const other2 = skeleton.nodes.get(other2Id);
      if (!other1 || !other2) {
        continue;
      }

      // Outward direction vectors from the node toward each neighbor.
      const v1x = other1.position.x - node.position.x;
      const v1y = other1.position.y - node.position.y;
      const v2x = other2.position.x - node.position.x;
      const v2y = other2.position.y - node.position.y;
      const len1 = Math.hypot(v1x, v1y);
      const len2 = Math.hypot(v2x, v2y);
      if (len1 < 1e-9 || len2 < 1e-9) {
        continue;
      }
      const cosAngle = (v1x * v2x + v1y * v2y) / (len1 * len2);
      if (cosAngle > cosThreshold) {
        continue;
      }

      // Optional equal-thickness gate: only merge a collinear run when both
      // incident edges represent a wall of (roughly) the same thickness. A change
      // in wall thickness across a node is a real feature worth keeping as a
      // vertex, so dissolving it would smear two distinct walls into one.
      //
      // Check PER SIDE, and try BOTH orientations — the two edges' left/right may
      // be swapped relative to each other (they're stored per edge-direction, so a
      // collinear run can have its sides flipped). The walls match if either the
      // DIRECT pairing (L↔L, R↔R) or the INVERSE pairing (L↔R, R↔L) agrees within
      // tolerance.
      if (thicknessTol > 0) {
        const close = (a: number, b: number): boolean => {
          const denom = Math.max(a, b);
          return denom <= 1e-9 || Math.abs(a - b) / denom <= thicknessTol;
        };
        const direct = close(e1.leftSize, e2.leftSize) && close(e1.rightSize, e2.rightSize);
        const inverse = close(e1.leftSize, e2.rightSize) && close(e1.rightSize, e2.leftSize);
        if (!direct && !inverse) {
          continue;
        }
      }

      // Skip if a direct edge already exists between the two
      // neighbors — we don't want to introduce duplicates.
      if (findEdgeBetweenNodes(skeleton, other1Id, other2Id)) {
        continue;
      }

      // Pick which edge to keep — the longer one tends to have more
      // representative metadata (thickness measurements averaged
      // over more samples, etc.).
      const keepFirst = len1 >= len2;
      const keep = keepFirst ? e1 : e2;
      const drop = keepFirst ? e2 : e1;
      const dropOtherId = keepFirst ? other2Id : other1Id;

      // Rewire the kept edge to point at the OTHER neighbor instead
      // of the dissolved node.
      if (keep.startNodeId === node.id) keep.startNodeId = dropOtherId;
      else keep.endNodeId = dropOtherId;

      // Merge roomIds from the dropped edge into the kept one.
      for (const roomId of drop.roomIds) {
        if (!keep.roomIds.includes(roomId)) keep.roomIds.push(roomId);
      }

      // Detach edge ids from neighbor nodes and delete the dropped
      // edge.
      const dropOtherNode = skeleton.nodes.get(dropOtherId);
      if (dropOtherNode) {
        dropOtherNode.edgeIds = dropOtherNode.edgeIds.filter((id) => id !== drop.id);
        if (!dropOtherNode.edgeIds.includes(keep.id)) dropOtherNode.edgeIds.push(keep.id);
      }
      skeleton.edges.delete(drop.id);

      // Drop the now-orphaned middle node.
      skeleton.nodes.delete(node.id);

      dissolvedAny = true;
    }

    if (!dissolvedAny) break;
  }

  return skeleton;
}

/**
 * Slide collinear nodes onto the wall STEP between their two edges.
 *
 * Where a straight wall run (two near-collinear edges through a degree-2 node)
 * spans a place where the bounding wall JUMPS (the parallel wall the run follows
 * shifts perpendicular by a step), the run's node should sit exactly on that
 * step. We find the step from the source geometry:
 *
 *   - cast perpendicular to the run from the midpoint of the BEFORE edge → the
 *     first room/outline wall hit (the wall that edge follows), and likewise from
 *     the AFTER edge,
 *   - if those two facing walls are PARALLEL but at different offsets (a real
 *     jump), the polygon edge that JOINS them — roughly perpendicular to the run
 *     — is the step,
 *   - SLIDE the node along its run onto that step's line.
 *
 * Pure slide (the run stays straight; edges only grow/shrink). If no jump / no
 * joining step edge is found, the node is left untouched.
 */
export function slideCollinearNodesToCrossingPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity) return skeleton;
  const collinearDeg = kwargs?.straightenAngleThreshold ?? 15;
  const maxSlide = kwargs?.maxRayDistance ?? 1.0;
  const collinearCosMax = -Math.cos((collinearDeg * Math.PI) / 180);

  // Flatten polygon edges once.
  interface PolyEdge {
    ax: number;
    ay: number;
    bx: number;
    by: number;
    ux: number;
    uy: number;
    len: number;
  }
  const polyEdges: PolyEdge[] = [];
  for (const room of skeleton.roomConnectivity) {
    const poly = room.polygon;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      polyEdges.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, ux: dx / len, uy: dy / len, len });
    }
  }

  /** Intersect line (p,dir) with line (q,dir2); null if (near-)parallel. */
  const intersect = (px: number, py: number, dx: number, dy: number, qx: number, qy: number, ex: number, ey: number): { x: number; y: number } | null => {
    const denom = dx * ey - dy * ex;
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((qx - px) * ey - (qy - py) * ex) / denom;
    return { x: px + dx * t, y: py + dy * t };
  };

  /** First room/outline wall hit casting from (px,py) along (dx,dy) up to maxSlide. */
  const cast = (px: number, py: number, dx: number, dy: number): RoomRayCastResult => castRayAgainstRoomPolygons(skeleton, new THREE.Vector3(px, py, skeleton.floorHeight), new THREE.Vector2(dx, dy), maxSlide, 0);

  for (const node of skeleton.nodes.values()) {
    if (node.edgeIds.length !== 2) continue;
    const e1 = skeleton.edges.get(node.edgeIds[0]);
    const e2 = skeleton.edges.get(node.edgeIds[1]);
    if (!e1 || !e2) continue;
    const o1Id = e1.startNodeId === node.id ? e1.endNodeId : e1.startNodeId;
    const o2Id = e2.startNodeId === node.id ? e2.endNodeId : e2.startNodeId;
    const o1 = skeleton.nodes.get(o1Id);
    const o2 = skeleton.nodes.get(o2Id);
    if (!o1 || !o2 || o1Id === o2Id) continue;

    // Run must be near-collinear through this node.
    const v1x = o1.position.x - node.position.x;
    const v1y = o1.position.y - node.position.y;
    const v2x = o2.position.x - node.position.x;
    const v2y = o2.position.y - node.position.y;
    const l1 = Math.hypot(v1x, v1y);
    const l2 = Math.hypot(v2x, v2y);
    if (l1 < 1e-9 || l2 < 1e-9) continue;
    if ((v1x * v2x + v1y * v2y) / (l1 * l2) > collinearCosMax) continue;

    // Run unit direction + normal.
    const rx = l1 >= l2 ? v1x / l1 : -v2x / l2;
    const ry = l1 >= l2 ? v1y / l1 : -v2y / l2;
    const nx = -ry;
    const ny = rx;

    // Midpoints of the before (e1) and after (e2) edges.
    const m1x = (node.position.x + o1.position.x) / 2;
    const m1y = (node.position.y + o1.position.y) / 2;
    const m2x = (node.position.x + o2.position.x) / 2;
    const m2y = (node.position.y + o2.position.y) / 2;

    // On EACH side of the run, cast perpendicular from the before-edge and the
    // after-edge midpoints. If the two hits are DIFFERENT walls (the wall jumps
    // between the two edges), the polygon edge CONNECTING those two hit walls is
    // the step; slide the node along the run onto that step.
    let target: { x: number; y: number } | null = null;
    let targetDist = maxSlide;
    for (const sign of [1, -1] as const) {
      const before = cast(m1x, m1y, nx * sign, ny * sign);
      const after = cast(m2x, m2y, nx * sign, ny * sign);
      if (!before.hit || !after.hit || !before.hitSegStart || !before.hitSegEnd || !after.hitSegStart || !after.hitSegEnd) continue;
      // Same wall on both sides → no jump → nothing to do.
      if (Math.abs((before.distance ?? 0) - (after.distance ?? 0)) < 0.02) continue;

      // The connecting step is a polygon edge that touches BOTH hit walls' lines
      // (one endpoint on each). Find it and slide the node onto its intersection
      // with the run.
      const onLine = (px: number, py: number, segS: THREE.Vector3, segE: THREE.Vector3): boolean => {
        const dx = segE.x - segS.x;
        const dy = segE.y - segS.y;
        const len = Math.hypot(dx, dy) || 1e-9;
        return Math.abs((px - segS.x) * (-dy / len) + (py - segS.y) * (dx / len)) < 0.05;
      };
      for (const pe of polyEdges) {
        const touchesBefore = onLine(pe.ax, pe.ay, before.hitSegStart, before.hitSegEnd) || onLine(pe.bx, pe.by, before.hitSegStart, before.hitSegEnd);
        const touchesAfter = onLine(pe.ax, pe.ay, after.hitSegStart, after.hitSegEnd) || onLine(pe.bx, pe.by, after.hitSegStart, after.hitSegEnd);
        if (!touchesBefore || !touchesAfter) continue; // not the connector between the two walls
        const ip = intersect(node.position.x, node.position.y, rx, ry, pe.ax, pe.ay, pe.ux, pe.uy);
        if (!ip) continue;
        const slide = Math.hypot(ip.x - node.position.x, ip.y - node.position.y);
        if (slide < targetDist) {
          targetDist = slide;
          target = ip;
        }
      }
    }
    if (!target) continue;

    node.position.x = target.x;
    node.position.y = target.y;
  }

  return skeleton;
}

/**
 * Merge collinear zigzags.
 *
 * Real walls are often reconstructed as a stair-step: two long, nearly-
 * collinear wall segments joined by a SHORT perpendicular "connector" edge —
 * the little jog visible where two nodes sit a few centimetres apart. This
 * pass detects that pattern and collapses it into one straight wall:
 *
 *   A ───n1     (jog)            A ─────────────── B
 *          │C        ──▶                (straight)
 *      n2───── B
 *
 * Detection — a connector edge C (nodes n1, n2) qualifies when:
 *   - C is short (< `zigzagMaxConnectorLength`),
 *   - n1 and n2 are each degree-2 (one other incident edge: A at n1, B at n2),
 *   - the two flanking walls A and B are near-collinear
 *     (within `zigzagCollinearAngleDeg` of parallel), and
 *   - C is roughly perpendicular to them (a genuine jog, not a continuation).
 *
 * Collapse — merge n1 into n2 (dropping C), which leaves a single degree-2
 * node where A and B now meet collinearly; that node is dissolved so A and B
 * become ONE edge. The merged wall is set to the THINNEST common width across
 * the involved walls (min `leftSize`, min `rightSize`) so the result is the
 * narrowest wall the step was hiding.
 *
 * Iterates until stable (collapsing one jog can expose another). The caller is
 * responsible for any undo snapshot.
 */
export function mergeCollinearZigzagsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxConnector = kwargs?.zigzagMaxConnectorLength ?? 0.25;
  const collinearAngleDeg = kwargs?.zigzagCollinearAngleDeg ?? 12;
  // Two flank walls count as the "same depth" (→ fuse into one edge) when both
  // their left and right half-thicknesses are within this many metres. When
  // they differ by more, a node is kept so the thickness step is preserved.
  const WALL_THICKNESS_EQ_TOL = kwargs?.zigzagThicknessEqTol ?? 0.03;
  // Walls are "collinear" when |cos(angle between their lines)| ≥ this.
  const collinearCos = Math.cos((collinearAngleDeg * Math.PI) / 180);

  // Blocking polygons — real rooms + synthetic outer-shell rings, but NOT
  // cavity (hole) rings. Same rule as deleteSkeletonEdgesCrossingPolygonsPass:
  // a collapse must not translate a wall across a room boundary or the outer
  // outline (skeleton walls are allowed through fireplaces / courtyards). Built
  // once; empty when there's no connectivity (then no move is ever blocked).
  type Seg = { ax: number; ay: number; bx: number; by: number };
  const polygonSegs: Seg[] = [];
  for (const room of skeleton.roomConnectivity ?? []) {
    if (room.identifier.startsWith('__CAVITY__')) continue;
    const poly = room.polygon;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      if (Math.abs(b.x - a.x) < 1e-9 && Math.abs(b.y - a.y) < 1e-9) continue;
      polygonSegs.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y });
    }
  }

  // Proper segment intersection: true only when the two segments cross at a
  // point strictly interior to both (not at endpoints, not collinear). Shared
  // shape with deleteSkeletonEdgesCrossingPolygonsPass.
  const epsilon = 1e-4;
  const segmentsCross = (p1x: number, p1y: number, p2x: number, p2y: number, q1x: number, q1y: number, q2x: number, q2y: number): boolean => {
    const rx = p2x - p1x;
    const ry = p2y - p1y;
    const sx = q2x - q1x;
    const sy = q2y - q1y;
    const denom = rx * sy - ry * sx;
    if (Math.abs(denom) < 1e-12) return false; // parallel or collinear
    const dx = q1x - p1x;
    const dy = q1y - p1y;
    const t = (dx * sy - dy * sx) / denom;
    const u = (dx * ry - dy * rx) / denom;
    return t > epsilon && t < 1 - epsilon && u > epsilon && u < 1 - epsilon;
  };

  /**
   * Would translating every node in `runNodeIds` by (tx, ty) push any MOVED
   * edge across a blocking polygon? A "moved edge" is any edge with at least
   * one endpoint in `runNodeIds` — that includes the run's own internal edges
   * (both endpoints shift) and the boundary edges that merely connect the run
   * to the rest of the skeleton (one endpoint shifts, so the segment swings).
   * Each moved endpoint is evaluated at its post-translation position; static
   * endpoints stay put. Returns true if any moved edge crosses — i.e. the move
   * is forbidden. With no polygons, nothing ever crosses.
   */
  const moveCrossesPolygon = (runNodeIds: Set<string>, tx: number, ty: number): boolean => {
    if (polygonSegs.length === 0) return false;
    const px = (n: SkeletonNode): number => (runNodeIds.has(n.id) ? n.position.x + tx : n.position.x);
    const py = (n: SkeletonNode): number => (runNodeIds.has(n.id) ? n.position.y + ty : n.position.y);
    const checkedEdges = new Set<string>();
    for (const nid of runNodeIds) {
      const node = skeleton.nodes.get(nid);
      if (!node) continue;
      for (const eid of node.edgeIds) {
        if (checkedEdges.has(eid)) continue;
        checkedEdges.add(eid);
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        const s = skeleton.nodes.get(e.startNodeId);
        const t2 = skeleton.nodes.get(e.endNodeId);
        if (!s || !t2) continue;
        const ax = px(s);
        const ay = py(s);
        const bx = px(t2);
        const by = py(t2);
        if (Math.abs(bx - ax) < 1e-9 && Math.abs(by - ay) < 1e-9) continue;
        for (const seg of polygonSegs) {
          if (segmentsCross(ax, ay, bx, by, seg.ax, seg.ay, seg.bx, seg.by)) return true;
        }
      }
    }
    return false;
  };

  /** Unit direction of `edge` pointing AWAY from `nodeId`, or null. */
  const dirAwayFrom = (edge: SkeletonEdge, nodeId: string): { ux: number; uy: number; farId: string } | null => {
    const farId = edge.startNodeId === nodeId ? edge.endNodeId : edge.startNodeId;
    const here = skeleton.nodes.get(nodeId);
    const far = skeleton.nodes.get(farId);
    if (!here || !far) return null;
    const dx = far.position.x - here.position.x;
    const dy = far.position.y - here.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return null;
    return { ux: dx / len, uy: dy / len, farId };
  };

  /**
   * Collect every node of the COLLINEAR RUN that starts at `startNodeId` and
   * extends through `firstEdge` and any further edges collinear with it. The
   * moved wall is rarely a single segment — it usually continues as a chain of
   * collinear pieces, and the whole chain must translate together or the line
   * breaks. We walk straight continuations only: at each node, follow the one
   * incident edge whose direction stays collinear with the run; stop at a
   * corner, a junction with no collinear continuation, or a dead end. Returns
   * the run's node ids (including `startNodeId`). Never crosses `excludeEdgeId`
   * (the connector) back to the kept side.
   */
  const collinearRunNodes = (startNodeId: string, firstFlank: { ux: number; uy: number; farId: string; edge: SkeletonEdge }, excludeEdgeId: string): Set<string> => {
    const runDirX = firstFlank.ux;
    const runDirY = firstFlank.uy;
    const visited = new Set<string>([startNodeId]);
    let prevEdgeId = firstFlank.edge.id;
    let currentNodeId = firstFlank.farId;
    const maxChain = 1000;
    for (let step = 0; step < maxChain; step++) {
      if (visited.has(currentNodeId)) break;
      visited.add(currentNodeId);
      const node = skeleton.nodes.get(currentNodeId);
      if (!node) break;
      // Find the incident edge (other than the one we came in on, and never the
      // connector) whose direction is most collinear with the run direction.
      let nextEdgeId: string | null = null;
      let nextFarId: string | null = null;
      let bestAbsCos = collinearCos;
      for (const eid of node.edgeIds) {
        if (eid === prevEdgeId || eid === excludeEdgeId) continue;
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        const d = dirAwayFrom(e, currentNodeId);
        if (!d) continue;
        const absCos = Math.abs(d.ux * runDirX + d.uy * runDirY);
        if (absCos > bestAbsCos) {
          bestAbsCos = absCos;
          nextEdgeId = eid;
          nextFarId = d.farId;
        }
      }
      if (!nextEdgeId || !nextFarId) break; // run ends here
      prevEdgeId = nextEdgeId;
      currentNodeId = nextFarId;
    }
    return visited;
  };

  let collapsedCount = 0;
  const maxIterations = 2000;
  for (let iter = 0; iter < maxIterations; iter++) {
    let collapsedAny = false;

    for (const connector of Array.from(skeleton.edges.values())) {
      const n1 = skeleton.nodes.get(connector.startNodeId);
      const n2 = skeleton.nodes.get(connector.endNodeId);
      if (!n1 || !n2 || n1.id === n2.id) continue;

      // Short connector only.
      const cLen = n1.position.distanceTo(n2.position);
      if (cLen < 1e-9 || cLen > maxConnector) continue;

      // Find the flank wall on each side: the pair (one edge at n1, one at n2,
      // neither the connector) whose directions are MOST collinear with each
      // other. At a junction the jog node may also have branch edges, so we
      // can't just take the first edge — we must pick the actual through-wall.
      type Flank = { edge: SkeletonEdge; ux: number; uy: number; farId: string };
      const flanksAt = (nodeId: string): Flank[] => {
        const out: Flank[] = [];
        for (const eid of skeleton.nodes.get(nodeId)?.edgeIds ?? []) {
          if (eid === connector.id) continue;
          const e = skeleton.edges.get(eid);
          if (!e) continue;
          const d = dirAwayFrom(e, nodeId);
          if (d) out.push({ edge: e, ux: d.ux, uy: d.uy, farId: d.farId });
        }
        return out;
      };
      const aFlanks = flanksAt(n1.id);
      const bFlanks = flanksAt(n2.id);
      let flankA: Flank | null = null;
      let flankB: Flank | null = null;
      let bestAbsCos = collinearCos; // must beat the collinearity threshold
      for (const fa of aFlanks) {
        for (const fb of bFlanks) {
          if (fa.edge.id === fb.edge.id || fa.farId === fb.farId) continue;
          const absCos = Math.abs(fa.ux * fb.ux + fa.uy * fb.uy);
          if (absCos > bestAbsCos) {
            bestAbsCos = absCos;
            flankA = fa;
            flankB = fb;
          }
        }
      }
      if (!flankA || !flankB) continue;

      // Do the two flank walls have (nearly) the same thickness? If they do we
      // can fuse them into ONE straight edge. If they DON'T (different wall
      // depths), we must keep a node at the junction so the thickness change is
      // preserved — the zigzag still collapses to a single node, but two edges
      // of different widths meet there.
      const sameThickness = Math.abs(flankA.edge.leftSize - flankB.edge.leftSize) < WALL_THICKNESS_EQ_TOL && Math.abs(flankA.edge.rightSize - flankB.edge.rightSize) < WALL_THICKNESS_EQ_TOL;
      // Common width to use when fusing — the thinnest, so the narrow wall the
      // step was masking wins.
      const minLeft = Math.min(flankA.edge.leftSize, flankB.edge.leftSize, connector.leftSize);
      const minRight = Math.min(flankA.edge.rightSize, flankB.edge.rightSize, connector.rightSize);
      const edgeAId = flankA.edge.id;
      const edgeBId = flankB.edge.id;

      // Collapse the jog by MOVING one flank's line onto the other's: the kept
      // wall stays put as the common reference; the moved side is translated by
      // the connector vector so its jog node lands exactly on the kept wall's
      // jog node — the two points end up on top of each other and then merge.
      //
      // The PREFERRED direction moves the THICKER wall onto the THINNER one
      // (always collapse toward the smallest wall). But that translation may
      // shove the moved run across a room boundary or the outer outline. When
      // it does, we try the OTHER direction (move the thin wall onto the thick
      // one) instead. If BOTH directions cross a polygon, we leave this jog
      // alone — better an uncollapsed step than a wall punched through a room.
      const aThick = flankA.edge.leftSize + flankA.edge.rightSize;
      const bThick = flankB.edge.leftSize + flankB.edge.rightSize;
      const aIsThinner = aThick <= bThick;

      // Each candidate keeps one jog node and translates the collinear run
      // hanging off the other. The moved wall often continues as a CHAIN of
      // collinear segments; moving just the one segment would break the run, so
      // the whole run extending from the moved flank translates together.
      // `collinearRunNodes` walks straight continuations from the moved node
      // through its flank and onward (never back across the connector).
      const buildCandidate = (keepNode: SkeletonNode, moveFromNode: SkeletonNode, moveFlank: Flank) => {
        const tx = keepNode.position.x - moveFromNode.position.x;
        const ty = keepNode.position.y - moveFromNode.position.y;
        const runNodeIds = collinearRunNodes(moveFromNode.id, moveFlank, connector.id);
        return { keepNode, moveFromNode, runNodeIds, tx, ty };
      };
      // Preferred = move thicker onto thinner; fallback = the reverse.
      const preferred = aIsThinner ? buildCandidate(n1, n2, flankB) : buildCandidate(n2, n1, flankA);
      const fallback = aIsThinner ? buildCandidate(n2, n1, flankA) : buildCandidate(n1, n2, flankB);

      let chosen: typeof preferred | null = null;
      if (!moveCrossesPolygon(preferred.runNodeIds, preferred.tx, preferred.ty)) {
        chosen = preferred;
      } else if (!moveCrossesPolygon(fallback.runNodeIds, fallback.tx, fallback.ty)) {
        chosen = fallback;
      }
      if (!chosen) continue; // both directions cross a polygon — leave the jog

      const { keepNode, moveFromNode, runNodeIds, tx, ty } = chosen;
      for (const nid of runNodeIds) {
        const nn = skeleton.nodes.get(nid);
        if (nn) {
          nn.position.x += tx;
          nn.position.y += ty;
        }
      }
      // `moveFromNode` is included in the run; it's now coincident with keepNode.

      // Merge the two now-coincident jog nodes into ONE. Merge the moved node
      // INTO the kept node so the result sits exactly at the kept (common)
      // wall. The merged node is KEPT — the jog offset is gone but the point on
      // the line stays, so no collinear points are dissolved. (This pass only
      // fixes the zigzag; a later cleanup pass can straighten redundant
      // collinear nodes if desired.)
      mergeNodes(skeleton, keepNode, moveFromNode);

      const a2 = skeleton.edges.get(edgeAId);
      const b2 = skeleton.edges.get(edgeBId);

      if (sameThickness) {
        // Equal depth → set the common (thinnest) width on both flank walls so
        // the run reads as one wall of the smallest width.
        if (a2) {
          a2.leftSize = minLeft;
          a2.rightSize = minRight;
        }
        if (b2) {
          b2.leftSize = minLeft;
          b2.rightSize = minRight;
        }
      }
      // else: different depths → each flank keeps its own width; the kept node
      // marks the thickness change.

      collapsedCount++;
      collapsedAny = true;
      break; // restart the scan — the edge map changed underneath us.
    }

    if (!collapsedAny) break;
  }

  console.warn(`mergeCollinearZigzagsPass: collapsed ${collapsedCount} zigzag connector(s) (maxConnector=${maxConnector}m, collinearAngle=${collinearAngleDeg}°)`);
  return skeleton;
}

/**
 * Build the skeleton as the STRAIGHT WALL_SKETCH of the wall region — the
 * polygon-with-holes formed by the space between the rooms and the building
 * outline. The outer-shell polygon (`__OUTER__`) is the outer boundary; every
 * real room and cavity is a hole. The straight skeleton of that region runs
 * down the middle of the walls (the bottom-middle reference shape), so this is
 * an alternative skeleton builder to the wall-pairing pipeline.
 *
 * Replaces `skeleton.nodes` / `skeleton.edges` with the skeleton arcs: arc
 * endpoints that coincide (within a small tolerance) become one shared node, so
 * the result is a connected graph. Edges get default wall sizes; later passes
 * (e.g. measure wall thickness) can refine them.
 *
 * Registered but NOT in the default pass chain — run it on its own from the
 * skeleton debug menu. Requires `roomConnectivity` with an outer shell.
 */
export function straightSkeletonPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity) return skeleton;

  const OUTER_PREFIX = '__OUTER__';
  const toRing = (poly: THREE.Vector3[]): Vec2[] => poly.map((p) => ({ x: p.x, y: p.y }));

  // Partition the polygons: outer shell(s) become the boundary, everything
  // else (rooms + cavities) is a hole carved out of the wall region.
  const outerRings: Vec2[][] = [];
  const holeRings: Vec2[][] = [];
  for (const room of skeleton.roomConnectivity) {
    const ring = toRing(room.polygon);
    if (ring.length < 3) continue;
    if (room.identifier.startsWith(OUTER_PREFIX)) outerRings.push(ring);
    else holeRings.push(ring);
  }

  if (outerRings.length === 0) {
    console.warn('straightSkeletonPass: no outer shell (__OUTER__) polygon — nothing to skeletonize.');
    return skeleton;
  }
  // A single outer shell is the normal case. If several were supplied we
  // skeletonize each independently and union the results (each with all holes
  // it encloses — the algorithm's wedge tests naturally ignore holes outside
  // a given outer ring, so passing all holes to each is safe).
  const arcs: Array<{ a: Vec2; b: Vec2 }> = [];
  for (const outer of outerRings) {
    for (const arc of computeStraightSkeleton({ outer, holes: holeRings })) {
      arcs.push(arc);
    }
  }

  // Materialize arcs into nodes + edges, snapping coincident endpoints into a
  // single shared node so the graph is connected.
  skeleton.nodes.clear();
  skeleton.edges.clear();
  if (skeleton.vertexToNodeMap) skeleton.vertexToNodeMap.clear();

  const z = skeleton.floorHeight;
  const SNAP = 1e-3; // metres; arc endpoints closer than this share a node
  const quant = (v: number) => Math.round(v / SNAP);
  const nodeIdByKey = new Map<string, string>();
  let nodeCounter = 0;
  let edgeCounter = 0;

  const getNode = (p: Vec2): string => {
    const key = `${quant(p.x)},${quant(p.y)}`;
    const existing = nodeIdByKey.get(key);
    if (existing) return existing;
    const id = `ss_n_${nodeCounter++}`;
    nodeIdByKey.set(key, id);
    skeleton.nodes.set(id, {
      id,
      position: new THREE.Vector3(p.x, p.y, z),
      roomIds: [],
      edgeIds: [],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    return id;
  };

  for (const arc of arcs) {
    const startId = getNode(arc.a);
    const endId = getNode(arc.b);
    if (startId === endId) continue; // degenerate after snapping
    const edgeId = `ss_e_${edgeCounter++}`;
    skeleton.edges.set(edgeId, {
      id: edgeId,
      startNodeId: startId,
      endNodeId: endId,
      roomIds: [],
      leftSize: 0.1,
      rightSize: 0.1,
      wallType: 'INNER',
      selected: false,
    });
    skeleton.nodes.get(startId)?.edgeIds.push(edgeId);
    skeleton.nodes.get(endId)?.edgeIds.push(edgeId);
  }

  console.warn(`straightSkeletonPass: built ${skeleton.edges.size} edge(s) from ${arcs.length} arc(s) across ${outerRings.length} outer shell(s), ${holeRings.length} hole(s).`);
  return skeleton;
}

/**
 * Build the skeleton as the RASTER-THINNED medial axis of the wall region (the
 * space between the rooms and the outline). Like {@link straightSkeletonPass}
 * it skeletonizes the outline-minus-rooms region, but instead of the exact
 * straight-skeleton wavefront — which zig-zags and drifts on noisy point-cloud
 * plans — it rasterizes the wall, thins it with Zhang–Suen, then traces and
 * simplifies the 1-pixel centerline (the technique behind skeleton-tracing).
 * The result is a clean, centered spine that follows the wall regardless of how
 * jagged the input polygons are.
 *
 * FIRST pass of the default chain (see skeleton-pipeline-config.json, fixed
 * 2 cm cell). The raster grid is sparse-tiled, so a large floor keeps full
 * resolution — cost scales with wall area, not the floor bounding box.
 * Requires `roomConnectivity` with an outer shell.
 */
export function rasterSkeletonPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity) return skeleton;

  const OUTER_PREFIX = '__OUTER__';
  const toRing = (poly: THREE.Vector3[]): Vec2[] => poly.map((p) => ({ x: p.x, y: p.y }));

  const outerRings: Vec2[][] = [];
  const holeRings: Vec2[][] = [];
  for (const room of skeleton.roomConnectivity) {
    const ring = toRing(room.polygon);
    if (ring.length < 3) continue;
    if (room.identifier.startsWith(OUTER_PREFIX)) outerRings.push(ring);
    else holeRings.push(ring);
  }

  if (outerRings.length === 0) {
    console.warn('rasterSkeletonPass: no outer shell (__OUTER__) polygon — nothing to skeletonize.');
    return skeleton;
  }

  // Capture the raster grid of the FIRST outer shell for the debug overlay
  // (the common single-shell case). Stashed on the skeleton; the editor tool
  // renders it as a textured plane when the debug toggle is on.
  let rasterDebug: RasterDebug | undefined;
  const options = {
    // 0 (or unset) means "auto" for the two distance params, so the debug-panel
    // numeric controls can show a 0 default that the algorithm derives itself.
    cellSize: kwargs?.rasterCellSize && kwargs.rasterCellSize > 0 ? kwargs.rasterCellSize : undefined,
    simplifyTolerance: kwargs?.rasterSimplifyTolerance && kwargs.rasterSimplifyTolerance > 0 ? kwargs.rasterSimplifyTolerance : undefined,
    axisSnapAngleDeg: kwargs?.rasterAxisSnapAngleDeg,
    onDebug: (d: RasterDebug) => {
      if (!rasterDebug) rasterDebug = d;
    },
  };

  const arcs: Array<{ a: Vec2; b: Vec2 }> = [];
  for (const outer of outerRings) {
    for (const arc of computeRasterThinSkeleton({ outer, holes: holeRings }, options)) {
      arcs.push(arc);
    }
  }
  skeleton.rasterDebug = rasterDebug;

  // Materialize arcs into nodes + edges, snapping coincident endpoints into a
  // single shared node so the graph is connected.
  skeleton.nodes.clear();
  skeleton.edges.clear();
  if (skeleton.vertexToNodeMap) skeleton.vertexToNodeMap.clear();

  const z = skeleton.floorHeight;
  const SNAP = 1e-3;
  const quant = (v: number) => Math.round(v / SNAP);
  const nodeIdByKey = new Map<string, string>();
  let nodeCounter = 0;
  let edgeCounter = 0;

  const getNode = (p: Vec2): string => {
    const key = `${quant(p.x)},${quant(p.y)}`;
    const existing = nodeIdByKey.get(key);
    if (existing) return existing;
    const id = `rs_n_${nodeCounter++}`;
    nodeIdByKey.set(key, id);
    skeleton.nodes.set(id, {
      id,
      position: new THREE.Vector3(p.x, p.y, z),
      roomIds: [],
      edgeIds: [],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    return id;
  };

  for (const arc of arcs) {
    const startId = getNode(arc.a);
    const endId = getNode(arc.b);
    if (startId === endId) continue;
    const edgeId = `rs_e_${edgeCounter++}`;
    skeleton.edges.set(edgeId, {
      id: edgeId,
      startNodeId: startId,
      endNodeId: endId,
      roomIds: [],
      leftSize: 0.1,
      rightSize: 0.1,
      wallType: 'INNER',
      selected: false,
    });
    skeleton.nodes.get(startId)?.edgeIds.push(edgeId);
    skeleton.nodes.get(endId)?.edgeIds.push(edgeId);
  }

  console.warn(`rasterSkeletonPass: built ${skeleton.edges.size} edge(s) from ${arcs.length} arc(s) across ${outerRings.length} outer shell(s), ${holeRings.length} hole(s).`);
  return skeleton;
}

// --- Shared geometry helpers for the three center-in-walls passes. ---
/** Two TARGET lines collinear? (parallel + near-zero perpendicular gap) */
function centerLinesCollinear(a: CenterTargetLine, b: CenterTargetLine): boolean {
  if (Math.abs(a.dx * b.dy - a.dy * b.dx) > 0.02) return false; // ~1.1°
  return Math.abs((b.px - a.px) * -a.dy + (b.py - a.py) * a.dx) < 1e-3;
}
/** Perpendicular-project a point onto a target line. */
function centerProjectPerpOnto(x: number, y: number, L: CenterTargetLine): { x: number; y: number } {
  const off = (x - L.px) * -L.dy + (y - L.py) * L.dx; // signed perpendicular distance
  return { x: x - -L.dy * off, y: y - L.dx * off };
}
/** Distance from point (px,py) to segment (ax,ay)-(bx,by). */
function pointSegDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy;
  let t = L2 < 1e-12 ? 0 : ((px - ax) * dx + (py - ay) * dy) / L2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}
/** Minimum distance between two segments P=(p1,p2) and Q=(q1,q2) (2D). */
function segSegDistance(p1x: number, p1y: number, p2x: number, p2y: number, q1x: number, q1y: number, q2x: number, q2y: number): number {
  const rx = p2x - p1x;
  const ry = p2y - p1y;
  const sx = q2x - q1x;
  const sy = q2y - q1y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) > 1e-12) {
    const dx = q1x - p1x;
    const dy = q1y - p1y;
    const t = (dx * sy - dy * sx) / denom;
    const u = (dx * ry - dy * rx) / denom;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return 0;
  }
  return Math.min(pointSegDistance(p1x, p1y, q1x, q1y, q2x, q2y), pointSegDistance(p2x, p2y, q1x, q1y, q2x, q2y), pointSegDistance(q1x, q1y, p1x, p1y, p2x, p2y), pointSegDistance(q2x, q2y, p1x, p1y, p2x, p2y));
}
/**
 * Intersect two target lines; null if (near-)parallel.
 *
 * `dx,dy` are unit vectors, so `denom` is exactly sin(angle between the lines).
 * We reject within the SAME angular tolerance the parallel/collinear test uses
 * (0.02 ≈ 1.1°), not just the exactly-parallel `1e-9` case. Two legs that are
 * nearly — but not quite — parallel "intersect" only at a phantom point tens of
 * metres away (tiny denom → huge `t`). The reconnect pass treated that phantom
 * as a valid shared corner (it trivially lies on both lines) and slid the
 * junction onto it, flinging the node far outside the outline. Rejecting it here
 * lets such a pair fall through to the collinear-bridge / extend-to-meet
 * handling, which keeps the junction local.
 */
function centerIntersectLines(A: CenterTargetLine, B: CenterTargetLine): { x: number; y: number } | null {
  const denom = A.dx * B.dy - A.dy * B.dx;
  if (Math.abs(denom) < 0.02) return null;
  const rx = B.px - A.px;
  const ry = B.py - A.py;
  const t = (rx * B.dy - ry * B.dx) / denom;
  return { x: A.px + A.dx * t, y: A.py + A.dy * t };
}

/**
 * STEP 1 of centering — MATCH MIDLINES.
 *
 * First compute the building's wall midlines with the shared `computeWallMidlines`
 * (every polygon edge paired with its facing parallel wall → one midline per
 * pair). Then, for every skeleton edge, snap it to the BEST matching midline: the
 * nearest midline that runs roughly parallel to the edge and that the edge sits
 * alongside. The edge is SPLIT onto its own two private nodes, perpendicular-
 * projected onto that midline's infinite line — so each matched edge becomes a
 * free-floating, VISIBLE "virtual edge" decoupled from its neighbours. The
 * midlines + virtual-edge bookkeeping (with the original node ids) are stashed on
 * `skeleton.centerWalls` for the combine + reconnect passes.
 *
 * Edges with no nearby parallel midline are left untouched (not split). Original
 * nodes left with no remaining edges are dropped.
 */
export function centerMatchWallsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity) return skeleton;

  const maxAngleRad = ((kwargs?.centerWallsMaxAngleDeg ?? 20) * Math.PI) / 180;
  const minCos = Math.cos(maxAngleRad);

  // --- Compute wall midlines (shared with pairWallsPass). ---
  // NOTE: computeWallMidlines reads skeleton.roomConnectivity (the source
  // polygons), NOT the current edges, so it's safe to call here before we mutate
  // the skeleton graph.
  const midlines = computeWallMidlines(skeleton, kwargs);
  if (midlines.length === 0) {
    skeleton.centerWalls = { midlines: [], virtuals: [] };
    return skeleton;
  }

  // Precompute each midline's unit direction + length for matching.
  interface MidlineGeom {
    ax: number;
    ay: number;
    ux: number;
    uy: number;
    len: number;
  }
  const mgeom: MidlineGeom[] = midlines.map((m) => {
    const dx = m.b.x - m.a.x;
    const dy = m.b.y - m.a.y;
    const len = Math.hypot(dx, dy) || 1e-9;
    return { ax: m.a.x, ay: m.a.y, ux: dx / len, uy: dy / len, len };
  });

  // --- Per-edge: snap to the best matching midline and build a virtual edge. ---
  const virtuals: CenterVirtualEdge[] = [];
  let centerNodeCounter = 0;
  /** Create a fresh private node at (x,y) cloned from an original node.
   *  Skips ids already present in the skeleton: when the pass re-runs on an
   *  already-matched skeleton (per-pass debug runs), a restarted counter
   *  would OVERWRITE a previous run's `cw_n_*` node and teleport every other
   *  edge still referencing it. */
  const makePrivateNode = (orig: SkeletonNode, x: number, y: number): string => {
    let id = `cw_n_${centerNodeCounter++}`;
    while (skeleton.nodes.has(id)) id = `cw_n_${centerNodeCounter++}`;
    skeleton.nodes.set(id, {
      id,
      position: new THREE.Vector3(x, y, orig.position.z),
      roomIds: [...orig.roomIds],
      edgeIds: [],
      sourceVertices: [],
      probedHits: [],
      selected: false,
    });
    return id;
  };

  // --- Guards: centering must not drag an edge too far, into a room, or out of
  // the building. Centering snaps an edge sideways onto its midline; when that
  // sideways move is large or lands the edge in forbidden space, we ROTATE the
  // edge about its midpoint to the midline direction instead (see below). ---
  const OUTER_PREFIX = '__OUTER__';
  const MAX_CENTER_MOVE = 0.15; // 15 cm cap on how far an edge's MIDPOINT may slide

  // Half wall thickness of a midline = perpendicular distance from the midline
  // to its farthest source polygon edge (the midline sits midway between the
  // pair). Used to widen the slide cap: an edge INSIDE its matched wall's band
  // is unambiguously on that wall and may always slide to its center, however
  // thick the wall — the fixed 15 cm cap exists to stop CROSS-wall pulls, and
  // thickness bounds those.
  const rcById = new Map((skeleton.roomConnectivity ?? []).map((r) => [r.identifier, r]));
  const midlineHalfThickness = (idx: number): number => {
    const m = midlines[idx];
    const g = mgeom[idx];
    let best = 0;
    for (const src of m.sources) {
      const rc = rcById.get(src.roomId);
      if (!rc || rc.polygon.length < 2) continue;
      const a = rc.polygon[src.edgeIndex % rc.polygon.length];
      const b = rc.polygon[(src.edgeIndex + 1) % rc.polygon.length];
      const mx = (a.x + b.x) / 2 - g.ax;
      const my = (a.y + b.y) / 2 - g.ay;
      const perp = Math.abs(mx * -g.uy + my * g.ux);
      best = Math.max(best, perp);
    }
    return best;
  };
  const roomRings: THREE.Vector3[][] = [];
  const outlineRings: THREE.Vector3[][] = [];
  for (const room of skeleton.roomConnectivity ?? []) {
    if (room.polygon.length < 3) continue;
    if (room.identifier.startsWith(OUTER_PREFIX)) outlineRings.push(room.polygon);
    else roomRings.push(room.polygon);
  }
  const pointInRing = (px: number, py: number, poly: THREE.Vector3[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  // A small tolerance: a centered point sits on the wall face (= a room edge or
  // the outline), so don't count a point that's merely ON a boundary as a
  // violation. Only points clearly inside a room / outside the outline fail.
  const BOUNDARY_TOL = 0.05;
  const nearAnyEdge = (px: number, py: number, ring: THREE.Vector3[]): boolean => {
    for (let k = 0; k < ring.length; k++) {
      const a = ring[k];
      const b = ring[(k + 1) % ring.length];
      if (pointSegDistance(px, py, a.x, a.y, b.x, b.y) <= BOUNDARY_TOL) return true;
    }
    return false;
  };
  /** A point is forbidden if it's clearly inside a room or clearly outside every outline. */
  const isForbidden = (px: number, py: number): boolean => {
    for (const ring of roomRings) {
      if (pointInRing(px, py, ring) && !nearAnyEdge(px, py, ring)) return true;
    }
    if (outlineRings.length > 0) {
      let insideSome = false;
      for (const ring of outlineRings) {
        if (pointInRing(px, py, ring) || nearAnyEdge(px, py, ring)) {
          insideSome = true;
          break;
        }
      }
      if (!insideSome) return true;
    }
    return false;
  };

  // Small edges are NOT matched: a junction/connector piece choosing its own
  // match group is what tears T-junctions apart — it may land on a different
  // wall than the big edges it connects, dragging the junction or leaving a
  // hole. Smalls keep their original nodes and follow the centered bigs via
  // the Snap Small Segments solver afterwards (collinear-or-90°).
  const minEdgeLen = kwargs?.centerWallsMinEdgeLength ?? 0.5;

  // Iterate a snapshot — we mutate skeleton.edges' endpoints as we go.
  for (const edge of [...skeleton.edges.values()]) {
    const sn = skeleton.nodes.get(edge.startNodeId);
    const en = skeleton.nodes.get(edge.endNodeId);
    if (!sn || !en) continue;
    const ex = en.position.x - sn.position.x;
    const ey = en.position.y - sn.position.y;
    const elen = Math.hypot(ex, ey);
    if (elen < 1e-6) continue;
    if (elen < minEdgeLen) continue; // small connector — follows its neighbors instead
    const eux = ex / elen;
    const euy = ey / elen;

    // Find the BEST midline: roughly parallel to the edge AND closest to it (by
    // segment-to-segment distance, so a midline running alongside this edge wins
    // over one that only passes near the midpoint). No distance gate — the edge
    // takes its nearest parallel midline wherever the building puts it.
    //
    // OVERLAP GATE: a midline must also RUN ALONGSIDE the edge for a minimum
    // shared span. Raw segment-to-segment distance is corner-dominated: at a
    // junction, the NEIGHBOURING wall's midline touches this edge's endpoint
    // (distance ≈ 0) and — within the wide 42° parallel cone — beats the
    // edge's own midline lying a few cm to the side. That mis-match either
    // spun the edge toward the neighbour's direction (pre-rotation-cap) or
    // starves it of centering entirely (post-cap). Projecting the midline
    // onto the edge axis and requiring real overlap kills corner-touchers
    // while leaving genuine alongside-runners untouched.
    const minOverlapFrac = kwargs?.centerWallsMinOverlap ?? 0.25;
    const cands: Array<{ idx: number; dist: number }> = [];
    for (let i = 0; i < mgeom.length; i++) {
      const g = mgeom[i];
      const cos = Math.abs(eux * g.ux + euy * g.uy);
      if (cos < minCos) continue; // must be roughly parallel to the edge
      const m = midlines[i];
      const t1 = (m.a.x - sn.position.x) * eux + (m.a.y - sn.position.y) * euy;
      const t2 = (m.b.x - sn.position.x) * eux + (m.b.y - sn.position.y) * euy;
      const overlapLen = Math.min(Math.max(t1, t2), elen) - Math.max(Math.min(t1, t2), 0);
      if (overlapLen < minOverlapFrac * Math.min(elen, g.len)) continue;
      const dist = segSegDistance(sn.position.x, sn.position.y, en.position.x, en.position.y, m.a.x, m.a.y, m.b.x, m.b.y);
      cands.push({ idx: i, dist });
    }
    cands.sort((a, b) => a.dist - b.dist);

    // REORIENTATION CAP. Every solution below lays the edge along the matched
    // midline's direction — perpendicular projection onto the line reorients
    // the edge exactly like the rotate-in-place fallback does. The match
    // window above is deliberately wide (centerWallsMaxAngleDeg = 42° in the
    // default chain, no distance gate) so a wall pair can still be FOUND for
    // sloppy input; but when a match sits beyond this much stricter rotation
    // cap, the midline almost certainly belongs to a DIFFERENT wall —
    // centering would spin a correctly-placed edge diagonally across the
    // floor plan.
    const maxRotateRad = ((kwargs?.centerWallsMaxRotateDeg ?? 15) * Math.PI) / 180;
    const minRotCos = Math.cos(maxRotateRad);
    let usable = cands.filter((c) => Math.abs(eux * mgeom[c.idx].ux + euy * mgeom[c.idx].uy) >= minRotCos);
    if (usable.length === 0) {
      // BAND-CONTAINMENT OVERRIDE. The rotation cap guards against snapping
      // onto a DIFFERENT wall's midline — but an edge whose BOTH endpoints
      // lie inside one midline's own wall band (laterally within its half
      // thickness) unambiguously belongs to that wall, however skewed it was
      // drawn across the band. Laying it along that midline is the
      // correction, not a cross-wall spin. No longitudinal span check: the
      // overlap gate above already rejects collinear midlines of OTHER wall
      // segments, and face-pair midlines are often shorter than the band
      // they describe.
      usable = cands.filter((c) => {
        const g = mgeom[c.idx];
        const half = midlineHalfThickness(c.idx) + 0.1;
        const within = (px: number, py: number): boolean => {
          const rx = px - g.ax;
          const ry = py - g.ay;
          return Math.abs(-g.uy * rx + g.ux * ry) <= half;
        };
        return within(sn.position.x, sn.position.y) && within(en.position.x, en.position.y);
      });
    }
    if (usable.length === 0) continue; // no plausible midline to snap to

    const isDeadEnd = sn.edgeIds.length === 1 || en.edgeIds.length === 1;
    const midOrigX = (sn.position.x + en.position.x) / 2;
    const midOrigY = (sn.position.y + en.position.y) / 2;

    /** Evaluate one match group: projection + displacement guards.
     *  - Slide cap on the MIDPOINT's move (widened to the matched wall's
     *    half thickness +2 cm snap slack — an edge inside a thick wall's
     *    band must still reach its center). Exempt for dead ends.
     *  - Containment: neither projected endpoint may sit clearly inside a
     *    room or outside the outline. Always enforced. */
    const evaluate = (idx: number): { line: CenterTargetLine; pa: { x: number; y: number }; pb: { x: number; y: number }; capViolated: boolean; containmentViolated: boolean } => {
      const g = mgeom[idx];
      const sign = eux * g.ux + euy * g.uy < 0 ? -1 : 1;
      const line: CenterTargetLine = { px: g.ax, py: g.ay, dx: sign * g.ux, dy: sign * g.uy };
      const pa = centerProjectPerpOnto(sn.position.x, sn.position.y, line);
      const pb = centerProjectPerpOnto(en.position.x, en.position.y, line);
      const centerMove = Math.hypot((pa.x + pb.x) / 2 - midOrigX, (pa.y + pb.y) / 2 - midOrigY);
      const slideCap = Math.max(MAX_CENTER_MOVE, midlineHalfThickness(idx) + 0.02);
      return {
        line,
        pa,
        pb,
        capViolated: !isDeadEnd && centerMove > slideCap,
        containmentViolated: isForbidden(pa.x, pa.y) || isForbidden(pb.x, pb.y),
      };
    };

    // MATCH-GROUP RETRY. A CONNECTED edge (a junction at each end) must not
    // be displaced across the plan: when its nearest match group would drag
    // it beyond the slide cap or into forbidden space, try the NEXT nearest
    // group instead — a different wall that fits where the edge actually
    // is. Dead ends (≤1 connection) matter less and stay on their nearest
    // match (their displacement is unbounded by design).
    let bestIdx = usable[0].idx;
    let ev = evaluate(bestIdx);
    if (!isDeadEnd && (ev.capViolated || ev.containmentViolated)) {
      for (let k = 1; k < usable.length; k++) {
        const alt = evaluate(usable[k].idx);
        if (!alt.capViolated && !alt.containmentViolated) {
          bestIdx = usable[k].idx;
          ev = alt;
          break;
        }
      }
    }
    let { pa, pb } = ev;
    let targetLine = ev.line;
    const line = ev.line;

    if (ev.capViolated || ev.containmentViolated) {
      // ROTATE-IN-PLACE: pivot about the (unmoved) edge midpoint and reorient to
      // the midline direction. Endpoints swing to C ± half·targetDir; the center
      // stays put, so the edge never slides off its position — it only squares up.
      // (The reorientation cap above guarantees this is a small correction.)
      const half = elen / 2;
      pa = { x: midOrigX - line.dx * half, y: midOrigY - line.dy * half };
      pb = { x: midOrigX + line.dx * half, y: midOrigY + line.dy * half };
      // The edge now lies on the line through its own midpoint along the target
      // direction — record THAT as its line so reconnect's on-line checks hold.
      targetLine = { px: midOrigX, py: midOrigY, dx: line.dx, dy: line.dy };
    }

    // Split the edge onto its OWN two private nodes at the resolved positions, and
    // repoint the real edge to them — the edge is now a free-floating, visible
    // segment on its midline (or rotated in place). Remember the original node
    // ids so the reconnect pass can merge the private nodes back.
    const origStart = edge.startNodeId;
    const origEnd = edge.endNodeId;
    const newStart = makePrivateNode(sn, pa.x, pa.y);
    const newEnd = makePrivateNode(en, pb.x, pb.y);
    sn.edgeIds = sn.edgeIds.filter((id) => id !== edge.id);
    en.edgeIds = en.edgeIds.filter((id) => id !== edge.id);
    edge.startNodeId = newStart;
    edge.endNodeId = newEnd;
    skeleton.nodes.get(newStart)?.edgeIds.push(edge.id);
    skeleton.nodes.get(newEnd)?.edgeIds.push(edge.id);

    // Assign room membership from the matched midline. A wall midline sits
    // between two facing polygon edges, so its `sources` carry the rooms on
    // each side of the wall — exactly this edge's rooms. The geometry-built
    // skeleton (straightSkeletonPass) seeds no roomIds, so this match is where
    // they're recovered; downstream consumers (e.g. door/window opening cutters
    // in PointoramaWallSketch.buildOpeningCutters) need them. The private end
    // nodes inherit the same set so fixupEdgeRoomsFromNodesPass stays consistent.
    const matchedRoomIds = Array.from(new Set(midlines[bestIdx].sources.map((s) => s.roomId)));
    if (matchedRoomIds.length > 0) {
      edge.roomIds = matchedRoomIds;
      const ns = skeleton.nodes.get(newStart);
      const ne = skeleton.nodes.get(newEnd);
      if (ns) ns.roomIds = [...matchedRoomIds];
      if (ne) ne.roomIds = [...matchedRoomIds];
    }

    virtuals.push({
      edgeId: edge.id,
      startNodeId: newStart,
      endNodeId: newEnd,
      origStart,
      origEnd,
      line: targetLine,
      midlineIndex: bestIdx,
    });
  }

  // SMALL EDGES MOVE WITH THE LARGE ONES — one very simple rule: a small
  // edge ends up EXACTLY ON the new line of the moved wall it is COLLINEAR
  // with. For every surviving original node we collect the moved lines that
  // claim it — the new lines of matched walls that ran THROUGH the node,
  // plus the new line of whichever moved wall each incident small edge is
  // collinear with (parallel + laterally alongside its OLD position).
  // One claimed line → perpendicular projection onto it (the small lands in
  // line with its wall, rotating only as much as the wall did). Two claimed
  // lines → their intersection (the corner). More → reconnect's exact solve.
  const pristine = new Map<string, { x: number; y: number }>();
  for (const v of virtuals) {
    for (const oid of [v.origStart, v.origEnd]) {
      if (pristine.has(oid)) continue;
      const n = skeleton.nodes.get(oid);
      if (n) pristine.set(oid, { x: n.position.x, y: n.position.y });
    }
  }
  const nodeLines = new Map<string, CenterTargetLine[]>();
  const claimLine = (nid: string, line: CenterTargetLine): void => {
    let arr = nodeLines.get(nid);
    if (!arr) {
      arr = [];
      nodeLines.set(nid, arr);
    }
    // One line per wall direction — a straight run's pieces agree anyway.
    if (arr.some((l) => Math.abs(l.dx * line.dy - l.dy * line.dx) <= 0.17)) return;
    arr.push(line);
  };
  // (a) Walls that ran through the node claim it directly.
  for (const v of virtuals) {
    claimLine(v.origStart, v.line);
    claimLine(v.origEnd, v.line);
  }
  // (b) Each remaining small edge finds the moved wall it is COLLINEAR with
  // (parallel, and laterally alongside the wall's OLD position — sharing a
  // node wins, else nearest within 40 cm) and claims that wall's NEW line
  // for BOTH its endpoints — so the whole small lands in line with it.
  const virtualEdgeIds = new Set(virtuals.map((v) => v.edgeId));
  for (const e of skeleton.edges.values()) {
    if (e.isJunctionEdge || virtualEdgeIds.has(e.id)) continue;
    const sn = skeleton.nodes.get(e.startNodeId);
    const en = skeleton.nodes.get(e.endNodeId);
    if (!sn || !en) continue;
    const ex = en.position.x - sn.position.x;
    const ey = en.position.y - sn.position.y;
    const elen2 = Math.hypot(ex, ey);
    if (elen2 < 1e-6 || elen2 >= minEdgeLen) continue;
    const eux = ex / elen2;
    const euy = ey / elen2;
    const mx = (sn.position.x + en.position.x) / 2;
    const my = (sn.position.y + en.position.y) / 2;
    let best: CenterTargetLine | null = null;
    let bestScore = Infinity;
    for (const v of virtuals) {
      if (Math.abs(eux * v.line.dy - euy * v.line.dx) > 0.26) continue; // not collinear-ish (>~15°)
      const oa = pristine.get(v.origStart);
      const ob = pristine.get(v.origEnd);
      if (!oa || !ob) continue;
      const dist = pointSegDistance(mx, my, oa.x, oa.y, ob.x, ob.y);
      const shares = v.origStart === e.startNodeId || v.origStart === e.endNodeId || v.origEnd === e.startNodeId || v.origEnd === e.endNodeId;
      if (!shares && dist > 0.4) continue;
      const score = shares ? dist * 0.5 : dist;
      if (score < bestScore) {
        bestScore = score;
        best = v.line;
      }
    }
    if (best) {
      claimLine(e.startNodeId, best);
      claimLine(e.endNodeId, best);
    }
  }
  // Apply: project onto the single claimed line, or sit on the corner where
  // two claimed lines cross. Guarded against wild moves.
  for (const [nid, lines] of nodeLines) {
    const n = skeleton.nodes.get(nid);
    if (!n || n.edgeIds.length === 0) continue;
    let tx: number | null = null;
    let ty: number | null = null;
    if (lines.length === 1) {
      const L = lines[0];
      const t = (n.position.x - L.px) * L.dx + (n.position.y - L.py) * L.dy;
      tx = L.px + L.dx * t;
      ty = L.py + L.dy * t;
    } else if (lines.length === 2) {
      const ip = centerIntersectLines(lines[0], lines[1]);
      if (ip) {
        tx = ip.x;
        ty = ip.y;
      }
    }
    if (tx === null || ty === null) continue;
    if (Math.hypot(tx - n.position.x, ty - n.position.y) > 1.5) continue;
    n.position.x = tx;
    n.position.y = ty;
  }

  // Drop any original nodes left with no edges (their incident edges were all
  // split off). Keeps the graph clean and visible as disconnected segments.
  for (const [id, node] of [...skeleton.nodes.values()].map((n) => [n.id, n] as const)) {
    if (id.startsWith('cw_n_')) continue;
    if (node.edgeIds.length === 0) skeleton.nodes.delete(id);
  }

  skeleton.centerWalls = { midlines, virtuals };
  return skeleton;
}

/**
 * STEP 2 of centering — COMBINE EDGES PER SOURCE LINE.
 *
 * Each matched midline originates from a wall pair {S, C} (PairedSegment.sources)
 * — two SOURCE LINES (polygon edges). For every source line we gather the edges
 * whose matched midline references it, pick the member closest to THAT source as
 * the anchor, and pull the others perpendicularly onto the anchor's line
 * (preserving along-line position; proxy nodes updated so it's visible).
 *
 * Grouping per single source — not per edge, midline, or union of shared sources
 * — avoids the transitive over-merge that pulled an edge off its wall: midlines
 * {A,B} and {B,C} only co-process under B, where the anchor is whichever edge is
 * closest to wall B. Groups are processed SMALL → LARGE so a bigger match group
 * (more edges agreeing on a wall) is applied last and takes precedence. A source
 * line with a single edge is left untouched.
 */
export function centerCombineSharedPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const state = skeleton.centerWalls;
  if (!state || state.virtuals.length === 0) return skeleton;
  const { midlines, virtuals } = state;

  // Group per SOURCE LINE (a single source wall), not per edge or per midline.
  // Every midline originates from a wall pair {S, C} (PairedSegment.sources);
  // each source is one polygon edge = one source line. We collect, for each
  // source line, the edges whose matched midline references it, then pull those
  // edges onto the line of the member closest to THAT source. Grouping per
  // single source (rather than unioning across shared sources) avoids the
  // transitive over-merge: midlines {A,B} and {B,C} only co-process under B,
  // where the anchor is whichever edge is closest to wall B — they don't get
  // chained into one cross-corridor blob.
  const sourceKey = (s: SourceEdgeRef): string => `${s.roomId}#${s.edgeIndex}`;

  // Geometry of a source line, looked up from the room polygon.
  const polyByRoom = new Map<string, THREE.Vector3[]>();
  for (const room of skeleton.roomConnectivity ?? []) polyByRoom.set(room.identifier, room.polygon);
  interface SourceLine {
    px: number;
    py: number;
    nx: number; // unit normal
    ny: number;
  }
  const sourceLineCache = new Map<string, SourceLine | null>();
  const sourceLineOf = (s: SourceEdgeRef): SourceLine | null => {
    const k = sourceKey(s);
    const cached = sourceLineCache.get(k);
    if (cached !== undefined) return cached;
    const poly = polyByRoom.get(s.roomId);
    let res: SourceLine | null = null;
    if (poly && poly.length >= 2) {
      const a = poly[s.edgeIndex % poly.length];
      const b = poly[(s.edgeIndex + 1) % poly.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len > 1e-9) res = { px: a.x, py: a.y, nx: -dy / len, ny: dx / len };
    }
    sourceLineCache.set(k, res);
    return res;
  };

  // edges that reference each source line.
  const edgesBySource = new Map<string, { src: SourceEdgeRef; members: CenterVirtualEdge[] }>();
  for (const ve of virtuals) {
    const m = midlines[ve.midlineIndex];
    for (const s of m.sources) {
      const k = sourceKey(s);
      const g = edgesBySource.get(k);
      if (g) g.members.push(ve);
      else edgesBySource.set(k, { src: s, members: [ve] });
    }
  }

  // NEIGHBOURS ONLY: edges that merely center against the SAME source wall are
  // not necessarily connected — two segments 5 m apart on the same wall would get
  // pulled together and one dragged across the building. Combine must only pull
  // edges that are actually adjacent in the ORIGINAL graph (share an original
  // node), so we split each source group into neighbour-connected components and
  // process each component on its own. Two members are neighbours iff they share
  // an original node id.
  const origNodesOf = (ve: CenterVirtualEdge): [string, string] => [ve.origStart, ve.origEnd];
  const neighbourComponents = (members: CenterVirtualEdge[]): CenterVirtualEdge[][] => {
    // Union-find over members keyed by shared original node.
    const nodeToMembers = new Map<string, number[]>();
    members.forEach((ve, i) => {
      for (const nid of origNodesOf(ve)) {
        const l = nodeToMembers.get(nid);
        if (l) l.push(i);
        else nodeToMembers.set(nid, [i]);
      }
    });
    const parent = members.map((_, i) => i);
    const find = (x: number): number => {
      let r = x;
      while (parent[r] !== r) r = parent[r];
      while (parent[x] !== r) {
        const n = parent[x];
        parent[x] = r;
        x = n;
      }
      return r;
    };
    const union = (a: number, b: number): void => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    };
    for (const idxs of nodeToMembers.values()) {
      for (let i = 1; i < idxs.length; i++) union(idxs[0], idxs[i]);
    }
    const byRoot = new Map<number, CenterVirtualEdge[]>();
    members.forEach((ve, i) => {
      const r = find(i);
      const l = byRoot.get(r);
      if (l) l.push(ve);
      else byRoot.set(r, [ve]);
    });
    return [...byRoot.values()];
  };

  // Pull SMALL groups first and BIG groups last, so a larger match group (more
  // edges agreeing on a wall) takes precedence — it's applied after, overriding
  // any earlier small-group move on a shared edge.
  const groups = [...edgesBySource.values()]
    .flatMap(({ src, members }) => neighbourComponents(members).map((comp) => ({ src, members: comp })))
    .filter((g) => g.members.length >= 2)
    .sort((a, b) => a.members.length - b.members.length);

  for (const { src, members } of groups) {
    const SL = sourceLineOf(src);
    if (!SL) continue;
    // Perpendicular distance of a member's MIDPOINT from the SOURCE line.
    const distToSource = (ve: CenterVirtualEdge): number => {
      const a = skeleton.nodes.get(ve.startNodeId);
      const b = skeleton.nodes.get(ve.endNodeId);
      if (!a || !b) return Infinity;
      const cx = (a.position.x + b.position.x) / 2;
      const cy = (a.position.y + b.position.y) / 2;
      return Math.abs((cx - SL.px) * SL.nx + (cy - SL.py) * SL.ny);
    };
    let anchor = members[0];
    for (const ve of members) if (distToSource(ve) < distToSource(anchor)) anchor = ve;
    // Anchor line normal — we measure/apply the move along it.
    const anx = -anchor.line.dy;
    const any = anchor.line.dx;
    // The shared corner where the members meet the anchor: the ORIGINAL node ids
    // the anchor carries. The member's endpoint at that node is the junction that
    // must land ON the anchor line — measuring the slide there (not at the
    // midpoint) keeps the connected corner aligned even when the member isn't
    // perfectly parallel, so reconnect doesn't have to swing the far end across.
    const anchorOrigNodes = new Set<string>([anchor.origStart, anchor.origEnd]);
    for (const ve of members) {
      if (ve === anchor) continue;
      const a = skeleton.nodes.get(ve.startNodeId);
      const b = skeleton.nodes.get(ve.endNodeId);
      if (!a || !b) continue;
      // TRANSLATE the edge perpendicular onto the ANCHOR line by ONE offset, so
      // both endpoints shift by the same amount and the edge keeps its own
      // direction — a pure parallel slide, never a rotation. The anchor is the
      // member already closest to the source wall, so sliding onto its line always
      // moves the edge TOWARD the alignment the other edges share.
      //
      // Measure the offset at the endpoint that shares the corner with the anchor
      // (the junction), falling back to the midpoint when neither end does. This
      // lands the CONNECTED endpoint exactly on the anchor line; an already-aligned
      // junction has off ≈ 0 and does not move.
      const startAtCorner = anchorOrigNodes.has(ve.origStart);
      const endAtCorner = anchorOrigNodes.has(ve.origEnd);
      let rx: number;
      let ry: number;
      if (startAtCorner && !endAtCorner) {
        rx = a.position.x;
        ry = a.position.y;
      } else if (endAtCorner && !startAtCorner) {
        rx = b.position.x;
        ry = b.position.y;
      } else {
        rx = (a.position.x + b.position.x) / 2;
        ry = (a.position.y + b.position.y) / 2;
      }
      const off = (rx - anchor.line.px) * anx + (ry - anchor.line.py) * any; // signed dist to anchor line
      // SAME-WALL SANITY CAP: combine aligns pieces of ONE wall — genuine
      // offsets are centimetres. A member sitting farther out than a wall's
      // thickness is a DIFFERENT parallel wall that merely shares a source
      // polygon edge and a junction node (e.g. a U-shaped run: down, across,
      // back up). Pulling it a metre across the plan is the failure mode;
      // leaving it where it is fits just as well.
      if (Math.abs(off) > 0.3) continue;
      const applied = off;

      a.position.x -= anx * applied;
      a.position.y -= any * applied;
      b.position.x -= anx * applied;
      b.position.y -= any * applied;
      // Keep the virtual edge's line consistent with its moved position (same
      // direction — only the anchoring point shifts).
      ve.line = { px: ve.line.px - anx * applied, py: ve.line.py - any * applied, dx: ve.line.dx, dy: ve.line.dy };
    }
  }

  return skeleton;
}

/**
 * STEP 3 of centering — RECONNECT.
 *
 * The match pass split every centered edge onto its own private nodes, so the
 * graph is a pile of free-floating segments. We stitch it back by the ORIGINAL
 * adjacency: all private endpoints from the same original node form a group.
 *
 * HARD RULE: no edge may ROTATE. An edge endpoint may only slide ALONG that
 * edge's own line (grow/shrink). So a merged node must lie on EVERY incident
 * edge's line. Per group:
 *   - 1 edge → leave its endpoint (nothing to satisfy).
 *   - all lines pass through a COMMON point (within tol) → one shared node there;
 *     every edge slides its endpoint onto it. Covers a clean corner, a collinear
 *     run, and a junction whose legs happen to concur.
 *   - otherwise → for each edge whose endpoint isn't already at the common point,
 *     EXTEND it along its own line to the intersection with its CLOSEST neighbour
 *     edge's line and insert a node there. Inserted points that coincide (within
 *     tol) merge. Edges only ever grow/shrink — never rotate.
 */
export function centerReconnectPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const state = skeleton.centerWalls;
  if (!state || state.virtuals.length === 0) return skeleton;
  const { virtuals } = state;

  /** One private endpoint that traces back to a shared original node. */
  interface Endpoint {
    ve: CenterVirtualEdge;
    /** The private node id this edge contributes (its start or end). */
    nodeId: string;
  }
  const groupsByOrig = new Map<string, Endpoint[]>();
  const add = (origId: string, ep: Endpoint): void => {
    const list = groupsByOrig.get(origId);
    if (list) list.push(ep);
    else groupsByOrig.set(origId, [ep]);
  };
  for (const ve of virtuals) {
    add(ve.origStart, { ve, nodeId: ve.startNodeId });
    add(ve.origEnd, { ve, nodeId: ve.endNodeId });
  }

  const ptOf = (nodeId: string): { x: number; y: number } | null => {
    const n = skeleton.nodes.get(nodeId);
    return n ? { x: n.position.x, y: n.position.y } : null;
  };
  /**
   * A leg's reference point for the reconnect logic: its EDGE MIDPOINT, not its
   * endpoint. All legs in a group lie on infinite lines, so where they MEET is
   * endpoint-independent — but the "closest meeting" pick and the collinear
   * bridge ARE endpoint-biased: anchoring on the endpoint makes each leg prefer a
   * different nearby crossing, so a junction's three legs scatter to three slots
   * joined by connector stubs (the little triangle). Reasoning from each edge's
   * midpoint gives every leg the same central reference, so they converge on one
   * meeting point and the junction collapses cleanly.
   */
  const refOf = (ep: Endpoint): { x: number; y: number } | null => {
    const a = ptOf(ep.ve.startNodeId);
    const b = ptOf(ep.ve.endNodeId);
    if (!a || !b) return ptOf(ep.nodeId); // degenerate — fall back to the endpoint
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };
  /** Signed perpendicular distance of a point from an edge's (infinite) line. */
  const perpFrom = (x: number, y: number, L: CenterTargetLine): number => (x - L.px) * -L.dy + (y - L.py) * L.dx;
  const ON_LINE_TOL = 1e-3;
  const MERGE_TOL = 0.02; // inserted points within 2 cm collapse

  // HARD INVARIANT: a reconnect node may never land outside the building
  // outline. Near-parallel legs intersect at a phantom point far off-building;
  // sliding/extending a leg there is always wrong. Rather than guess a distance
  // threshold, we test candidate points against the `__OUTER__` outline directly
  // and reject any that fall outside — the leg then stays at its current spot
  // (which is in-bounds by construction). Built once from roomConnectivity.
  const OUTER_PREFIX = '__OUTER__';
  const outlineRings: THREE.Vector3[][] = [];
  for (const room of skeleton.roomConnectivity ?? []) {
    if (room.identifier.startsWith(OUTER_PREFIX) && room.polygon.length >= 3) outlineRings.push(room.polygon);
  }
  const pointInRing = (px: number, py: number, poly: THREE.Vector3[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  // A small outward tolerance: a centered corner can sit a hair past the outline
  // (the midline runs through the wall, whose outer face IS the outline), so
  // accept points within ON_OUTLINE_TOL of being inside. If there's no outline
  // ring at all, don't gate (keep legacy behaviour).
  const ON_OUTLINE_TOL = 0.05;
  const insideOutline = (x: number, y: number): boolean => {
    if (outlineRings.length === 0) return true;
    for (const ring of outlineRings) {
      if (pointInRing(x, y, ring)) return true;
      // within tolerance of an edge counts as inside (corner on the wall face)
      for (let k = 0; k < ring.length; k++) {
        const a = ring[k];
        const b = ring[(k + 1) % ring.length];
        if (pointSegDistance(x, y, a.x, a.y, b.x, b.y) <= ON_OUTLINE_TOL) return true;
      }
    }
    return false;
  };

  let nodeCounter = 0;
  // Every node placed by reconnect. Any of them can land in the INTERIOR of a
  // DIFFERENT group's edge (a corner node that another wall runs through, or an
  // extend-to-meet node from the no-common-point branch). Such a node must be
  // inserted INTO the edge it sits on (split it), not left coincident-but-
  // unconnected. Collected here and resolved after all groups are placed.
  const insertedNodeIds = new Set<string>();

  // Move endpoint `nodeId`'s incident edge so it terminates at (x,y), creating a
  // fresh shared node id if `id` is given (else reuse `nodeId`'s node, repositioned).
  const placeEndpointAt = (ep: Endpoint, x: number, y: number, sharedId: string): void => {
    const old = skeleton.nodes.get(ep.nodeId);
    const z = old ? old.position.z : skeleton.floorHeight;
    const roomIds = old ? [...old.roomIds] : [];
    let target = skeleton.nodes.get(sharedId);
    if (!target) {
      target = { id: sharedId, position: new THREE.Vector3(x, y, z), roomIds, edgeIds: [], sourceVertices: [], probedHits: [], selected: false };
      skeleton.nodes.set(sharedId, target);
    } else {
      if (target.edgeIds.length > 0 && Math.hypot(target.position.x - x, target.position.y - y) > 1e-6) {
        // A move ALONG an attached edge is a legal slide (grow/shrink). The
        // SIDEWAYS component would bend the edge — so the edge MOVES WITH the
        // junction instead: translate its far endpoint by the same lateral
        // amount, keeping it parallel while its line now passes through the
        // resolved corner. When some far node can't legally follow (its own
        // edges would rotate), we do NOT bend anything: the old node STEPS
        // ASIDE under a private id — keeping its position and edges intact —
        // the junction gets a FRESH node at the resolved corner, and a short
        // connector preserves connectivity (a small edge the snap stage
        // squares later). Rotation never happens.
        const mdx = x - target.position.x;
        const mdy = y - target.position.y;
        const plans: Array<{ other: SkeletonNode; sxv: number; syv: number }> = [];
        let stuck = false;
        for (const eid of [...target.edgeIds]) {
          const e = skeleton.edges.get(eid);
          if (!e) continue;
          const otherId2 = e.startNodeId === sharedId ? e.endNodeId : e.startNodeId;
          const other = skeleton.nodes.get(otherId2);
          if (!other || otherId2 === sharedId) continue;
          const ox = other.position.x - target.position.x;
          const oy = other.position.y - target.position.y;
          const ol = Math.hypot(ox, oy);
          if (ol < 1e-9) continue;
          // Sideways (perpendicular) component of the move w.r.t. this edge.
          const per = mdx * -(oy / ol) + mdy * (ox / ol);
          if (Math.abs(per) <= 1e-3) continue; // pure slide — nothing to do
          const sxv = per * -(oy / ol);
          const syv = per * (ox / ol);
          const slen = Math.hypot(sxv, syv);
          let legal = true;
          for (const oeid of other.edgeIds) {
            if (oeid === eid) continue;
            const oe = skeleton.edges.get(oeid);
            if (!oe) continue;
            const pa2 = skeleton.nodes.get(oe.startNodeId);
            const pb2 = skeleton.nodes.get(oe.endNodeId);
            if (!pa2 || !pb2) continue;
            const wx = pb2.position.x - pa2.position.x;
            const wy = pb2.position.y - pa2.position.y;
            const wl = Math.hypot(wx, wy);
            if (wl < 1e-9) continue;
            if (Math.abs((sxv / slen) * (wy / wl) - (syv / slen) * (wx / wl)) > 0.17) {
              legal = false;
              break;
            }
          }
          if (legal) {
            plans.push({ other, sxv, syv });
          } else {
            stuck = true;
            break;
          }
        }
        if (stuck) {
          const asideId = `${sharedId}__orig`;
          if (!skeleton.nodes.has(asideId)) {
            skeleton.nodes.delete(sharedId);
            const aside = target;
            (aside as { id: string }).id = asideId;
            skeleton.nodes.set(asideId, aside);
            for (const eid of aside.edgeIds) {
              const e = skeleton.edges.get(eid);
              if (!e) continue;
              if (e.startNodeId === sharedId) e.startNodeId = asideId;
              if (e.endNodeId === sharedId) e.endNodeId = asideId;
            }
            const fresh: SkeletonNode = { id: sharedId, position: new THREE.Vector3(x, y, aside.position.z), roomIds: [...aside.roomIds], edgeIds: [], sourceVertices: [], probedHits: [], selected: false };
            skeleton.nodes.set(sharedId, fresh);
            const cid = `cw_conn_${nodeCounter++}`;
            skeleton.edges.set(cid, { id: cid, startNodeId: asideId, endNodeId: sharedId, roomIds: [], leftSize: 0.1, rightSize: 0.1, wallType: 'INNER', selected: false });
            aside.edgeIds.push(cid);
            fresh.edgeIds.push(cid);
            target = fresh;
          }
        } else {
          for (const p of plans) {
            p.other.position.x += p.sxv;
            p.other.position.y += p.syv;
          }
        }
      }
      target.position.x = x;
      target.position.y = y;
    }
    // ROTATION GUARD: placing this endpoint at (x,y) must keep ITS OWN edge
    // straight — (x,y) must lie on that edge's line (the far endpoint stays put,
    // so an off-line move rotates the edge). Log any violation so we can trace it.
    const offline = Math.abs(perpFrom(x, y, ep.ve.line));
    if (offline > ON_LINE_TOL) {
      console.warn(`[RECONN] edge ${ep.ve.edgeId} endpoint placed ${offline.toFixed(3)}m OFF its own line → rotation. node=${ep.nodeId} -> (${x.toFixed(3)},${y.toFixed(3)})`);
    }
    if (ep.nodeId === sharedId) return;
    if (old) {
      // ROTATION IS A LAST-DITCH EFFORT — and we never take it here. An edge
      // may only be re-pointed to the shared corner when that is a SLIDE
      // along its own line (the direction from its far endpoint barely
      // changes). An off-line re-point would swing the edge into a diagonal;
      // such edges KEEP the old node, which then survives at its position —
      // form, connectivity and angles are preserved, and a longer edge
      // shrinks via its own group's handling instead of rotating.
      const kept: string[] = [];
      for (const eid of [...old.edgeIds]) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        const farId = e.startNodeId === ep.nodeId ? e.endNodeId : e.startNodeId;
        const far = skeleton.nodes.get(farId);
        let slide = true;
        if (far && farId !== ep.nodeId) {
          const vx = x - far.position.x;
          const vy = y - far.position.y;
          const vl = Math.hypot(vx, vy);
          const wx = old.position.x - far.position.x;
          const wy = old.position.y - far.position.y;
          const wl = Math.hypot(wx, wy);
          if (vl > 1e-9 && wl > 1e-9) {
            slide = Math.abs((vx / vl) * (wy / wl) - (vy / vl) * (wx / wl)) <= 0.17; // ≤ ~10° swing
          }
        }
        if (!slide) {
          kept.push(eid);
          continue;
        }
        if (e.startNodeId === ep.nodeId) e.startNodeId = sharedId;
        if (e.endNodeId === ep.nodeId) e.endNodeId = sharedId;
        if (!target.edgeIds.includes(eid)) target.edgeIds.push(eid);
      }
      if (kept.length > 0) {
        old.edgeIds = kept;
      } else {
        skeleton.nodes.delete(ep.nodeId);
      }
    }
  };

  /** The longest surviving NON-virtual edge attached to the group's original
   *  node, as a target line through its far endpoint. These are the small
   *  followers / unmatched edges that must connect at the corner: whenever
   *  the group's placement has freedom ALONG a line (single leg, collinear
   *  run), the corner snaps to the CROSSING with this line so both sides
   *  reach it by pure slides and connect exactly there. */
  const virtualEdgeIdSet = new Set(virtuals.map((v) => v.edgeId));
  const strangerLineAt = (origId: string): CenterTargetLine | null => {
    const n = skeleton.nodes.get(origId);
    if (!n) return null;
    let best: CenterTargetLine | null = null;
    let bestLen = 0;
    for (const eid of n.edgeIds) {
      if (virtualEdgeIdSet.has(eid)) continue;
      const e = skeleton.edges.get(eid);
      if (!e) continue;
      const farId = e.startNodeId === origId ? e.endNodeId : e.startNodeId;
      const far = skeleton.nodes.get(farId);
      if (!far || farId === origId) continue;
      const dx = n.position.x - far.position.x;
      const dy = n.position.y - far.position.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9 || len <= bestLen) continue;
      bestLen = len;
      best = { px: far.position.x, py: far.position.y, dx: dx / len, dy: dy / len };
    }
    return best;
  };

  for (const [origId, eps] of groupsByOrig) {
    if (eps.some((e) => ptOf(e.nodeId) === null)) continue;

    // Single edge: slide it to the crossing with the node's follower edge
    // when one exists (both connect exactly there); else leave its endpoint.
    if (eps.length === 1) {
      const p = ptOf(eps[0].nodeId);
      if (p) {
        let px2 = p.x;
        let py2 = p.y;
        const SL2 = strangerLineAt(origId);
        if (SL2) {
          const ip = centerIntersectLines(eps[0].ve.line, SL2);
          if (ip && Math.hypot(ip.x - p.x, ip.y - p.y) <= 1.5) {
            px2 = ip.x;
            py2 = ip.y;
          }
        }
        placeEndpointAt(eps[0], px2, py2, origId);
      }
      insertedNodeIds.add(origId);
      continue;
    }

    // COMMON-POINT TEST: find a candidate point (intersection of the first
    // non-parallel pair, or — if all parallel/collinear — a point on the shared
    // line), then accept it only if it lies on EVERY incident edge's line.
    let common: { x: number; y: number } | null = null;
    for (let i = 0; i < eps.length && !common; i++) {
      for (let j = i + 1; j < eps.length; j++) {
        const ip = centerIntersectLines(eps[i].ve.line, eps[j].ve.line);
        if (ip) {
          common = ip;
          break;
        }
      }
    }
    if (!common) {
      // All lines parallel: collinear → bridge at the average of the endpoints
      // (which lies on the shared line); truly parallel-but-offset → no common
      // point, handled below.
      const allCollinear = eps.every((e) => centerLinesCollinear(eps[0].ve.line, e.ve.line));
      if (allCollinear) {
        let sx = 0;
        let sy = 0;
        for (const e of eps) {
          const p = refOf(e);
          if (p) {
            sx += p.x;
            sy += p.y;
          }
        }
        common = { x: sx / eps.length, y: sy / eps.length };
        // A collinear run's bridge point may sit ANYWHERE on the shared line
        // — a crossing follower edge fixes where it belongs: at the crossing
        // of the two lines, so run and follower connect exactly there.
        const SL2 = strangerLineAt(origId);
        if (SL2) {
          const ip = centerIntersectLines(eps[0].ve.line, SL2);
          if (ip && Math.hypot(ip.x - common.x, ip.y - common.y) <= 1.5) {
            common = { x: ip.x, y: ip.y };
          }
        }
      }
    }
    const cpt = common;
    const commonOnAll = cpt !== null && eps.every((e) => Math.abs(perpFrom(cpt.x, cpt.y, e.ve.line)) < ON_LINE_TOL);

    // The common point must also lie INSIDE the outline. A shallow-angle pair
    // (just past the parallel-reject threshold above) still crosses tens of
    // metres away, off-building; accepting that as the corner would slide the
    // whole junction outside the outline. When the common point is outside,
    // reject it and fall through to extend-to-meet, which keeps the legs local.
    const commonInside = cpt !== null && insideOutline(cpt.x, cpt.y);

    if (common && commonOnAll && commonInside) {
      // One shared node on every line — every edge just slides onto it.
      for (const ep of eps) placeEndpointAt(ep, common.x, common.y, origId);
      insertedNodeIds.add(origId);
      continue;
    }

    // NO COMMON POINT — extend each edge along its OWN line to meet its closest
    // neighbour's line, inserting a node there. Inserted points that coincide
    // (within MERGE_TOL) share a node, so a clean junction still collapses.
    const inserted: Array<{ x: number; y: number; id: string }> = [];
    for (const ep of eps) {
      // Closest neighbour line that this edge's line actually intersects, scored
      // from the edge MIDPOINT (refOf) — not the endpoint — so every leg in the
      // group judges "closest" from the same central reference and they converge
      // on one meeting point instead of scattering into the connector triangle.
      let best: { x: number; y: number } | null = null;
      let bestD = Infinity;
      const mid = refOf(ep); // distance reference (edge midpoint)
      const endHere = ptOf(ep.nodeId); // no-meeting fallback (stay put)
      if (!mid || !endHere) continue;
      for (const other of eps) {
        if (other === ep) continue;
        const ip = centerIntersectLines(ep.ve.line, other.ve.line);
        if (!ip) continue; // parallel — can't meet by sliding
        // A meeting point OUTSIDE the outline is the shallow-angle phantom (the
        // lines cross only tens of metres out, off-building); extending the leg
        // there would fling the node clear of the building. Treat it as
        // non-meeting so the leg stays put below instead of rotating off.
        if (!insideOutline(ip.x, ip.y)) continue;
        // Score "closest" from the edge MIDPOINT so all legs share one reference.
        const d = Math.hypot(ip.x - mid.x, ip.y - mid.y);
        if (d < bestD) {
          bestD = d;
          best = ip;
        }
      }
      // No intersecting neighbour (e.g. a lone parallel leg) → keep the endpoint
      // where it is. Staying put is a no-op slide; no rotation.
      const tgt = best ?? endHere;
      // Reuse a nearby inserted node so coincident meetings collapse — but ONLY
      // if that node lies on THIS edge's own line. Snapping an endpoint to a
      // nearby off-line slot would move it off its line = a ROTATION (forbidden);
      // those go to their own slot and get joined by a connector below.
      let slot = inserted.find((s) => Math.hypot(s.x - tgt.x, s.y - tgt.y) < MERGE_TOL && Math.abs(perpFrom(s.x, s.y, ep.ve.line)) < ON_LINE_TOL);
      if (!slot) {
        const id = inserted.length === 0 ? origId : `${origId}__x${nodeCounter++}`;
        slot = { x: tgt.x, y: tgt.y, id };
        inserted.push(slot);
      }
      placeEndpointAt(ep, slot.x, slot.y, slot.id);
      insertedNodeIds.add(slot.id);
    }

    // CONNECT THE PIECES — all eps came from ONE original node, so they must stay
    // joined. If the group landed on MORE THAN ONE slot, bridge each extra slot
    // to the first with a short connector edge so nothing is left disconnected.
    if (inserted.length > 1) {
      const base = inserted[0];
      for (let s = 1; s < inserted.length; s++) {
        const slot = inserted[s];
        if (Math.hypot(slot.x - base.x, slot.y - base.y) < 1e-6) continue; // coincident
        if (findEdgeBetweenNodes(skeleton, base.id, slot.id)) continue;
        const cid = `cw_conn_${nodeCounter++}`;
        skeleton.edges.set(cid, { id: cid, startNodeId: base.id, endNodeId: slot.id, roomIds: [], leftSize: 0.1, rightSize: 0.1, wallType: 'INNER', selected: false });
        skeleton.nodes.get(base.id)?.edgeIds.push(cid);
        skeleton.nodes.get(slot.id)?.edgeIds.push(cid);
      }
    }
  }

  // Insert each placed node INTO an edge it was extended to MEET, but ONLY an
  // edge from its OWN original group: split that edge at the node so the two are
  // actually connected, not just coincident. The node was placed at the
  // intersection of line-pairs, but the matched edge's SEGMENT can end a little
  // short of that point, so the node sits just off the segment — use a forgiving
  // split tolerance.
  //
  // SAME-GROUP ONLY: reconnect must re-pin legs that were connected in the
  // ORIGINAL graph, never invent a new connection. A node landing near an
  // unrelated wall used to get spliced into it, producing a long diagonal across
  // the building. We restrict the split to edges in the node's own group so no
  // never-existed connection is created.
  const SPLIT_TOL = 0.1;
  // A node/edge's original group: every reconnect node id is `origId` or
  // `origId__x<n>`; every group-internal connector id is `cw_conn_<n>` (no group
  // affinity — skip those as split targets). For a normal edge, its group is the
  // union of its two endpoints' groups.
  const groupOfNode = (id: string): string | null => {
    if (id.startsWith('cw_n_') || id.startsWith('cw_conn_')) return null;
    const xi = id.indexOf('__x');
    return xi >= 0 ? id.slice(0, xi) : id;
  };
  const groupsOfEdge = (e: SkeletonEdge): Set<string> => {
    const s = new Set<string>();
    const ga = groupOfNode(e.startNodeId);
    const gb = groupOfNode(e.endNodeId);
    if (ga) s.add(ga);
    if (gb) s.add(gb);
    return s;
  };
  const MIN_SPLIT_EDGE = 0.05; // don't split an edge shorter than this (5 cm)
  const MIN_SPLIT_PIECE = 0.02; // and don't create a piece shorter than this (2 cm)
  let splitCounter = 0;
  for (const nid of insertedNodeIds) {
    const node = skeleton.nodes.get(nid);
    if (!node) continue;
    // If another reconnect node already sits on top of this one (a near-coincident
    // junction slot, e.g. rs_n_83 vs rs_n_83__x52 ~6 mm apart), splitting here just
    // makes a tangle of sub-cm stubs. Skip — the connector edge already joins them.
    let coincident = false;
    for (const other of insertedNodeIds) {
      if (other === nid) continue;
      const on = skeleton.nodes.get(other);
      if (on && Math.hypot(on.position.x - node.position.x, on.position.y - node.position.y) < MIN_SPLIT_EDGE) {
        coincident = true;
        break;
      }
    }
    if (coincident) continue;

    // Insert the node into the nearest edge whose SEGMENT it lies on (the edge it
    // was extended to MEET): split that edge at the node so they connect. We do
    // NOT merge nodes here — sliding/centering already placed everything, the
    // node only needs to be inserted into the edge it's sitting on. Skip targets
    // that are already tiny, or where the split would leave a near-zero stub —
    // those produce the junction tangle rather than a useful connection.
    const myGroup = groupOfNode(nid);
    let bestEdgeId: string | null = null;
    let bestDist = SPLIT_TOL;
    for (const edge of skeleton.edges.values()) {
      if (edge.startNodeId === nid || edge.endNodeId === nid) continue;
      // SAME-GROUP ONLY: never splice into an edge from a different original
      // junction — that would be a connection the original graph never had.
      if (myGroup === null || !groupsOfEdge(edge).has(myGroup)) continue;
      const a = skeleton.nodes.get(edge.startNodeId);
      const b = skeleton.nodes.get(edge.endNodeId);
      if (!a || !b) continue;
      const segLen = a.position.distanceTo(b.position);
      if (segLen < MIN_SPLIT_EDGE) continue; // target too short to split usefully
      const { distance, t } = closestPointOnSegment(node.position, a.position, b.position);
      if (distance >= bestDist) continue; // farther than the current best (or beyond tol)
      if (t * segLen < MIN_SPLIT_PIECE || (1 - t) * segLen < MIN_SPLIT_PIECE) continue; // would leave a near-zero stub
      bestEdgeId = edge.id;
      bestDist = distance;
    }
    if (!bestEdgeId) continue;
    const edge = skeleton.edges.get(bestEdgeId);
    if (!edge) continue;
    // Split at the node's CURRENT position — do NOT snap it onto `edge`'s line.
    // The node is also an endpoint of its own group's edge(s); moving it
    // perpendicular onto this edge would pull those off their line = a ROTATION,
    // which is forbidden. It's already within SPLIT_TOL of `edge`, so splitting
    // at its current spot keeps `edge` effectively straight without rotating
    // anything else.
    const e1Id = `cw_split_${splitCounter++}`;
    const e2Id = `cw_split_${splitCounter++}`;
    skeleton.edges.set(e1Id, { id: e1Id, startNodeId: edge.startNodeId, endNodeId: nid, roomIds: [...edge.roomIds], leftSize: edge.leftSize, rightSize: edge.rightSize, wallType: edge.wallType, selected: false });
    skeleton.edges.set(e2Id, { id: e2Id, startNodeId: nid, endNodeId: edge.endNodeId, roomIds: [...edge.roomIds], leftSize: edge.leftSize, rightSize: edge.rightSize, wallType: edge.wallType, selected: false });
    skeleton.edges.delete(bestEdgeId);
  }

  // Rebuild every node's `edgeIds` from the actual edges. The merges above
  // repoint edges and delete private nodes, which can leave a node carrying a
  // STALE edge id (an edge that no longer references it) — a phantom degree that
  // made downstream passes (e.g. remove-collinear) skip a genuine degree-2 node.
  // Recomputing from ground truth fixes the counts and drops now-orphaned nodes.
  for (const node of skeleton.nodes.values()) node.edgeIds = [];
  for (const e of skeleton.edges.values()) {
    skeleton.nodes.get(e.startNodeId)?.edgeIds.push(e.id);
    if (e.endNodeId !== e.startNodeId) skeleton.nodes.get(e.endNodeId)?.edgeIds.push(e.id);
  }
  for (const [id, node] of [...skeleton.nodes.entries()]) {
    if (node.edgeIds.length === 0) skeleton.nodes.delete(id);
  }

  // ENFORCE THE NO-ROTATION RULE. The merges/splices above can leave a
  // centered edge's endpoint slightly OFF its centered line (an off-line
  // splice up to SPLIT_TOL, or a cross-group node reuse) — a visible BEND at
  // the junction. Repair it: slide the offending endpoint perpendicular back
  // ONTO the centered line, but ONLY when that slide is itself legal — it
  // must run ALONG every other edge at that node (at a perpendicular corner,
  // pushing the corner node onto the vertical's line slides it along the
  // horizontal: both stay dead straight and simply meet at the true
  // intersection). Mixed-direction nodes it cannot legally move are left for
  // the diagnostic below.
  for (const ve of virtuals) {
    const eObj = skeleton.edges.get(ve.edgeId);
    if (!eObj) continue;
    for (const nid of [eObj.startNodeId, eObj.endNodeId]) {
      const n = skeleton.nodes.get(nid);
      if (!n) continue;
      const off = (n.position.x - ve.line.px) * -ve.line.dy + (n.position.y - ve.line.py) * ve.line.dx;
      if (Math.abs(off) < ON_LINE_TOL || Math.abs(off) > SPLIT_TOL + 0.05) continue;
      // The repair move runs along the line's NORMAL — legal only if every
      // OTHER edge at this node is parallel to that normal (i.e. roughly
      // perpendicular to the centered line).
      let legal = true;
      for (const eid of n.edgeIds) {
        if (eid === ve.edgeId) continue;
        const oe = skeleton.edges.get(eid);
        if (!oe) continue;
        const oa = skeleton.nodes.get(oe.startNodeId);
        const ob = skeleton.nodes.get(oe.endNodeId);
        if (!oa || !ob) continue;
        const ox = ob.position.x - oa.position.x;
        const oy = ob.position.y - oa.position.y;
        const ol = Math.hypot(ox, oy);
        if (ol < 1e-9) continue;
        if (Math.abs((ox / ol) * ve.line.dx + (oy / ol) * ve.line.dy) > 0.17) {
          legal = false;
          break;
        }
      }
      if (!legal) continue;
      n.position.x -= off * -ve.line.dy;
      n.position.y -= off * ve.line.dx;
    }
  }

  // DIAGNOSTIC: a virtual edge must keep the DIRECTION it was centered to. If its
  // final endpoints span a different direction, something rotated it — whether a
  // node moved off-line OR an endpoint got repointed to the wrong node. Log it so
  // the cause is visible (catches mis-reconnections the move-only guard misses).
  for (const ve of virtuals) {
    const a = skeleton.nodes.get(ve.startNodeId);
    const b = skeleton.nodes.get(ve.endNodeId);
    if (!a || !b) continue;
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    const cross = Math.abs((dx / len) * ve.line.dy - (dy / len) * ve.line.dx);
    if (cross > 0.02) {
      // ~1.1° — beyond float noise.
      console.warn(
        `[RECONN] edge ${ve.edgeId} ROTATED: now (${a.position.x.toFixed(2)},${a.position.y.toFixed(2)})->(${b.position.x.toFixed(2)},${b.position.y.toFixed(2)}) vs centered dir (${ve.line.dx.toFixed(3)},${ve.line.dy.toFixed(3)})  start=${ve.startNodeId} end=${ve.endNodeId}`,
      );
    }
  }

  // HARD INVARIANT, final enforcement: no node may sit outside the outline. The
  // branch-level outline gates above keep the reconnect MATH from inventing
  // out-of-bounds corners, but a private endpoint can already be outside when it
  // ARRIVES here — the match/combine passes snap edges onto wall midlines, and a
  // midline near the building edge can run slightly outside the outline. For any
  // node still outside, slide it back ALONG an incident edge (toward that edge's
  // other, in-bounds endpoint) to the first in-bounds point — a move along the
  // edge line, so the edge only shortens, never rotates.
  if (outlineRings.length > 0) {
    for (const node of skeleton.nodes.values()) {
      if (insideOutline(node.position.x, node.position.y)) continue;
      // Find an incident edge whose OTHER endpoint is inside — march toward it.
      let anchor: { x: number; y: number } | null = null;
      for (const eid of node.edgeIds) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        const otherId = e.startNodeId === node.id ? e.endNodeId : e.startNodeId;
        const other = skeleton.nodes.get(otherId);
        if (other && insideOutline(other.position.x, other.position.y)) {
          anchor = { x: other.position.x, y: other.position.y };
          break;
        }
      }
      if (!anchor) continue; // no in-bounds anchor to slide toward — leave as-is
      // Binary-search the segment [node → anchor] for the outline crossing: t=0
      // is outside, t=1 is inside; find the smallest in-bounds t.
      let loT = 0; // outside
      let hiT = 1; // inside
      for (let iter = 0; iter < 24; iter++) {
        const midT = (loT + hiT) / 2;
        const mx = node.position.x + (anchor.x - node.position.x) * midT;
        const my = node.position.y + (anchor.y - node.position.y) * midT;
        if (insideOutline(mx, my)) hiT = midT;
        else loT = midT;
      }
      node.position.x = node.position.x + (anchor.x - node.position.x) * hiT;
      node.position.y = node.position.y + (anchor.y - node.position.y) * hiT;
    }
  }

  // The virtual-edge state is consumed — clear it so a re-run starts fresh.
  skeleton.centerWalls = undefined;
  return skeleton;
}

/**
 * Center each skeleton edge between the two real walls it sits in.
 *
 * Convenience wrapper that runs the three centering passes in order — match,
 * combine, reconnect. Registered as a single debug-menu entry and used by the
 * default chain / tests; each sub-pass is also registered separately so they can
 * be run and inspected one at a time.
 */
/**
 * PRUNE UNMATCHED WALLS — delete skeleton traces with no parallel wall-pair
 * support, WITHOUT breaking the skeleton graph.
 *
 * The raster skeleton traces EVERY dense line in the scan: stair treads,
 * furniture, the medial axis of a narrow shaft/cavity. The walls, though, are
 * exactly where the room/outline polygon edges are — so EVERY polygon edge
 * (long walls and short steps/jogs alike) picks its single best-matching
 * trace and LOCKS it: outline/room edge → wall trace, one match each, and
 * "no match" is not an option. Everything else is pruned. There are NO hard
 * match gates — every trace within 3× `pruneUnmatchedMaxDistance` competes,
 * and the best LINE-PROPERTY score wins: coverage-of-the-polygon-edge ×
 * alignment² × proximity² (ties → closer), with `pruneUnmatchedMaxDistance`
 * as the proximity scale. Distance dominates deliberately: walls hug their
 * faces, so a depth-duplicate or corner jog loses its face's election to
 * the true trace even when it covers more span.
 *
 * Lack of support alone is NOT enough to delete: short corner jogs and
 * junction connectors have no midline of their own either but hold real wall
 * chains together. So unsupported edges are grouped into connected CLUSTERS
 * and handled structurally:
 *  - clusters touching the supported graph at ≤1 node (dead-end branches,
 *    isolated islands) are DELETED;
 *  - clusters with a total length up to `pruneUnmatchedConnectorKeepLength`
 *    that bridge ≥2 attachment points are COLLAPSED — every cluster node
 *    merges into one corner node at the attachments' centroid, so the walls
 *    it joined now share a node instead of detouring through a noise jog.
 *    Run BEFORE Center Match Walls, this lets the centering passes extend
 *    the joined legs into a sharp corner (reconnect only stitches legs that
 *    share an original node);
 *  - longer bridging clusters whose ends CONTINUE the attached supported
 *    walls (collinear within `pruneUnmatchedContinuationAngleDeg` at ≥2
 *    attachments) are KEPT — a straight continuation is a gap in a real wall
 *    run where polygon evidence happens to be missing;
 *  - remaining long bridging clusters are DELETED only if the graph keeps
 *    the SAME number of connected components (their attachment points stay
 *    mutually reachable some other way — true for noise chains criss-crossing
 *    rooms inside a looped wall network, false for a genuine sole connection).
 *
 * Junction ring edges are exempt. No room/outline polygons → no information →
 * nothing is pruned — and the same rule applies LOCALLY: edges outside every
 * polygon and farther than `pruneUnmatchedCoverageDistance` from every
 * polygon segment are beyond the drawn geometry (no midline can exist there)
 * and are kept, so real wall traces in undrawn areas (corridors, unselected
 * rooms) survive while noise inside rooms is still pruned.
 */
export function pruneUnmatchedWallsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity || skeleton.roomConnectivity.length === 0) return skeleton;
  const maxDist = kwargs?.pruneUnmatchedMaxDistance ?? 0.5;
  const connectorKeepLength = kwargs?.pruneUnmatchedConnectorKeepLength ?? 1.0;
  const coverageDist = kwargs?.pruneUnmatchedCoverageDistance ?? 0.5;
  const contCos = Math.cos(((kwargs?.pruneUnmatchedContinuationAngleDeg ?? 35) * Math.PI) / 180);
  const promCos = Math.cos(((kwargs?.pruneUnmatchedPromotionAngleDeg ?? 15) * Math.PI) / 180);

  // Evidence base for the LOCAL no-information rule: an edge is IN COVERAGE
  // when it lies inside any room/outline polygon (noise mid-room is still
  // covered — the room's own edges are its midline evidence) or within
  // `coverageDist` of a polygon segment (the wall band around the rings).
  // Everything farther out is beyond the drawn geometry — no midline can
  // exist there, so lack of support carries no signal and those edges are
  // exempt from pruning (the per-edge analogue of the global "no polygons →
  // nothing pruned" rule).
  const polySegs: { ax: number; ay: number; bx: number; by: number; ux: number; uy: number; len: number }[] = [];
  for (const room of skeleton.roomConnectivity) {
    const poly = room.polygon;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      polySegs.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, ux: dx / len, uy: dy / len, len });
    }
  }
  const pointInRing = (px: number, py: number, poly: THREE.Vector3[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const yi = poly[i].y;
      const yj = poly[j].y;
      if (yi > py === yj > py) continue;
      const xCross = poly[j].x + ((py - yj) / (yi - yj)) * (poly[i].x - poly[j].x);
      if (px < xCross) inside = !inside;
    }
    return inside;
  };
  const isInCoverage = (e: SkeletonEdge): boolean => {
    const sn = skeleton.nodes.get(e.startNodeId);
    const en = skeleton.nodes.get(e.endNodeId);
    if (!sn || !en) return true;
    const mx = (sn.position.x + en.position.x) / 2;
    const my = (sn.position.y + en.position.y) / 2;
    for (const room of skeleton.roomConnectivity ?? []) {
      if (pointInRing(mx, my, room.polygon) || pointInRing(sn.position.x, sn.position.y, room.polygon) || pointInRing(en.position.x, en.position.y, room.polygon)) return true;
    }
    for (const s of polySegs) {
      if (segSegDistance(sn.position.x, sn.position.y, en.position.x, en.position.y, s.ax, s.ay, s.bx, s.by) <= coverageDist) return true;
    }
    return false;
  };
  const edgeLen = (e: SkeletonEdge): number => {
    const sn = skeleton.nodes.get(e.startNodeId);
    const en = skeleton.nodes.get(e.endNodeId);
    if (!sn || !en) return 0;
    return Math.hypot(en.position.x - sn.position.x, en.position.y - sn.position.y);
  };
  // --- LOCK PHASE: every polygon edge locks exactly ONE trace. ---
  // Dead simple, evidence-driven: each room/outline polygon edge — long
  // walls AND short steps/jogs alike — picks its single best-matching trace
  // by LINE PROPERTIES (coverage of the polygon edge × alignment ×
  // proximity) and LOCKS it. One polygon edge, one match — that is the whole
  // model. Everything not locked by any polygon edge is pruned (subject to
  // the structural rules below). No midlines, no pairing, no ray casts, no
  // span bookkeeping — the polygon edges themselves are the walls, and each
  // names its trace.
  // Precomputed geometry for every prunable edge.
  const edgeGeom = new Map<string, { sx: number; sy: number; ex: number; ey: number; ux: number; uy: number; len: number }>();
  for (const e of skeleton.edges.values()) {
    if (e.isJunctionEdge) continue;
    const sn = skeleton.nodes.get(e.startNodeId);
    const en = skeleton.nodes.get(e.endNodeId);
    if (!sn || !en) continue;
    const dx = en.position.x - sn.position.x;
    const dy = en.position.y - sn.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    edgeGeom.set(e.id, { sx: sn.position.x, sy: sn.position.y, ex: en.position.x, ey: en.position.y, ux: dx / len, uy: dy / len, len });
  }

  // Matching visualization: one perpendicular TIE per lock decision
  // (evidence point → trace), green for locked, red for rejected candidates
  // (occluded / span already claimed). Rendered by the skeleton debug menu's
  // "Show Prune Matches" toggle. Own labels are cleared first so pipeline
  // re-runs on the same skeleton don't accumulate stale ties.
  const debugRays = (skeleton.debugRays = (skeleton.debugRays ?? []).filter((r) => r.label !== 'prune-match'));
  const pushTie = (px: number, py: number, qx: number, qy: number, lockedTie: boolean): void => {
    const dx = qx - px;
    const dy = qy - py;
    const d = Math.hypot(dx, dy);
    if (d < 1e-6) return;
    debugRays.push({ origin: new THREE.Vector3(px, py, 0), direction: new THREE.Vector3(dx / d, dy / d, 0), distance: d, hit: lockedTie, label: 'prune-match' });
  };

  // The polygon edge is the one doing the matching: outline/room edge →
  // wall trace, never the reverse. NO hard gates — "no match" is not an
  // option: every trace inside the search radius competes, and angle,
  // coverage and distance are smooth SCORE factors instead of cutoffs. (The
  // gates were the failure mode: a wall trace 5 cm past the distance cutoff
  // was excluded from its face's election, letting a corner jog win it, and
  // step edges with no in-gate candidate locked nothing at all.)
  const searchDist = maxDist * 3;
  const locked = new Set<string>();
  for (const E of polySegs) {
    // Score = coverage-of-the-polygon-edge × alignment² × proximity² — how
    // much of this edge the trace runs along, how parallel it is, how close
    // it hugs. Proximity is squared: walls hug their faces, and distance is
    // the strongest wall signal. The tie (evidence point at footprint
    // midpoint → closest point on the trace) feeds the match visualization.
    const cands: Array<{ id: string; dist: number; score: number; px: number; py: number; qx: number; qy: number }> = [];
    for (const [id, g] of edgeGeom) {
      const dist = segSegDistance(g.sx, g.sy, g.ex, g.ey, E.ax, E.ay, E.bx, E.by);
      if (dist > searchDist) continue;
      const cosA = Math.abs(g.ux * E.ux + g.uy * E.uy);
      const u1 = (g.sx - E.ax) * E.ux + (g.sy - E.ay) * E.uy;
      const u2 = (g.ex - E.ax) * E.ux + (g.ey - E.ay) * E.uy;
      const lo = Math.max(Math.min(u1, u2), 0);
      const hi = Math.min(Math.max(u1, u2), E.len);
      const overlapFrac = hi > lo ? (hi - lo) / E.len : 0;
      const prox = 1 / (1 + dist / (maxDist + 1e-9));
      const score = (overlapFrac + 0.1) * cosA * cosA * prox * prox;
      const tm = hi > lo ? (lo + hi) / 2 : E.len / 2;
      const px = E.ax + E.ux * tm;
      const py = E.ay + E.uy * tm;
      let s = (px - g.sx) * g.ux + (py - g.sy) * g.uy;
      s = Math.max(0, Math.min(g.len, s));
      cands.push({ id, dist, score, px, py, qx: g.sx + g.ux * s, qy: g.sy + g.uy * s });
    }
    if (cands.length === 0) continue;

    // ONE match per polygon edge: the best property score wins (ties →
    // closer); every other candidate is rejected. Depth-duplicates and
    // corner-cutting shortcuts lose to the trace that genuinely fits. Red
    // ties are only drawn for RELEVANT losers (≥30% of the winner's score)
    // so the visualization stays readable.
    cands.sort((a, b) => b.score - a.score || a.dist - b.dist);
    const winner = cands[0];
    locked.add(winner.id);
    pushTie(winner.px, winner.py, winner.qx, winner.qy, true);
    for (let i = 1; i < cands.length; i++) {
      if (cands[i].score < 0.3 * winner.score) break;
      pushTie(cands[i].px, cands[i].py, cands[i].qx, cands[i].qy, false);
    }
  }

  const unsupported = new Set<string>();
  for (const e of skeleton.edges.values()) {
    if (e.isJunctionEdge) continue;
    if (!edgeGeom.has(e.id)) continue; // malformed/degenerate → leave alone
    // Unlocked edges beyond coverage stay untouched — no evidence exists
    // out there, so not being locked carries no signal.
    if (!locked.has(e.id) && isInCoverage(e)) unsupported.add(e.id);
  }
  if (unsupported.size === 0) return skeleton;

  const dirAt = (edgeId: string, fromNodeId: string): { x: number; y: number } | null => {
    const ed = skeleton.edges.get(edgeId);
    if (!ed) return null;
    const a = skeleton.nodes.get(ed.startNodeId);
    const b = skeleton.nodes.get(ed.endNodeId);
    if (!a || !b) return null;
    const [p, q] = ed.startNodeId === fromNodeId ? [a, b] : [b, a];
    const dx = q.position.x - p.position.x;
    const dy = q.position.y - p.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return null;
    return { x: dx / len, y: dy / len };
  };

  // CHAIN PROMOTION — one polygon edge locks ONE trace, but a wall is often
  // traced in several collinear pieces (splits at junctions, gaps at
  // openings). Walk outward from every locked edge: an unsupported edge that
  // collinearly (within the strict PROMOTION angle) continues a supported
  // edge at a shared node is the same wall — promote it, and keep walking
  // until stable. Corner jogs and noise branches kink harder than that and
  // stay unsupported (jogs then collapse into clean corner nodes below).
  let promoted = true;
  while (promoted) {
    promoted = false;
    for (const eid of [...unsupported]) {
      const e = skeleton.edges.get(eid);
      if (!e) continue;
      for (const nid of [e.startNodeId, e.endNodeId]) {
        const n = skeleton.nodes.get(nid);
        if (!n) continue;
        const cd = dirAt(eid, nid);
        if (!cd) continue;
        let continues = false;
        for (const se of n.edgeIds) {
          if (se === eid || unsupported.has(se) || !skeleton.edges.has(se)) continue;
          const sd = dirAt(se, nid);
          if (!sd) continue;
          if (Math.abs(cd.x * sd.x + cd.y * sd.y) >= promCos) {
            continues = true;
            break;
          }
        }
        if (continues) {
          unsupported.delete(eid);
          promoted = true;
          break;
        }
      }
    }
  }
  if (unsupported.size === 0) return skeleton;

  // --- Cluster the unsupported edges (connected via shared nodes). ---
  const clusterOf = new Map<string, number>();
  let clusterCount = 0;
  for (const eid of unsupported) {
    if (clusterOf.has(eid)) continue;
    const cluster = clusterCount++;
    const stack = [eid];
    clusterOf.set(eid, cluster);
    while (stack.length > 0) {
      const cur = skeleton.edges.get(stack.pop() as string);
      if (!cur) continue;
      for (const nid of [cur.startNodeId, cur.endNodeId]) {
        const n = skeleton.nodes.get(nid);
        if (!n) continue;
        for (const nb of n.edgeIds) {
          if (!unsupported.has(nb) || clusterOf.has(nb)) continue;
          clusterOf.set(nb, cluster);
          stack.push(nb);
        }
      }
    }
  }
  const clusters: string[][] = Array.from({ length: clusterCount }, () => []);
  for (const [eid, c] of clusterOf) clusters[c].push(eid);

  // --- Connected-component count of the graph over a given edge set. ---
  const componentCount = (excluded: ReadonlySet<string>): number => {
    const parent = new Map<string, string>();
    const find = (x: string): string => {
      let r = x;
      while (parent.get(r) !== undefined && parent.get(r) !== r) r = parent.get(r) as string;
      parent.set(x, r);
      return r;
    };
    const nodesSeen = new Set<string>();
    for (const e of skeleton.edges.values()) {
      if (excluded.has(e.id)) continue;
      nodesSeen.add(e.startNodeId);
      nodesSeen.add(e.endNodeId);
      const ra = find(e.startNodeId);
      const rb = find(e.endNodeId);
      if (ra !== rb) parent.set(ra, rb);
    }
    const roots = new Set<string>();
    for (const n of nodesSeen) roots.add(find(n));
    return roots.size;
  };

  const baseComponents = componentCount(new Set());
  const removed = new Set<string>();

  // Largest clusters first: big noise chains go before small ones are judged,
  // so a small cluster attached only to a doomed big one counts as dead-end.
  const totalLen = (ids: string[]): number => ids.reduce((s, id) => s + edgeLen(skeleton.edges.get(id) as SkeletonEdge), 0);
  clusters.sort((a, b) => totalLen(b) - totalLen(a));

  /** Attachment nodes where a cluster edge collinearly (within the
   *  continuation angle) CONTINUES a surviving supported edge. */
  const countContinuations = (clusterSet: ReadonlySet<string>, attachments: ReadonlySet<string>, removedSet: ReadonlySet<string>): number => {
    let continuations = 0;
    for (const nid of attachments) {
      const n = skeleton.nodes.get(nid);
      if (!n) continue;
      let continues = false;
      for (const ce of n.edgeIds) {
        if (!clusterSet.has(ce)) continue;
        const cd = dirAt(ce, nid);
        if (!cd) continue;
        for (const se of n.edgeIds) {
          if (clusterSet.has(se) || removedSet.has(se) || unsupported.has(se) || !skeleton.edges.has(se)) continue;
          const sd = dirAt(se, nid);
          if (!sd) continue;
          if (Math.abs(cd.x * sd.x + cd.y * sd.y) >= contCos) {
            continues = true;
            break;
          }
        }
        if (continues) break;
      }
      if (continues) continuations++;
    }
    return continuations;
  };

  for (const cluster of clusters) {
    const clusterSet = new Set(cluster.filter((id) => !removed.has(id)));
    if (clusterSet.size === 0) continue;
    // Attachment nodes: cluster nodes also used by a surviving non-cluster edge.
    const attachments = new Set<string>();
    for (const eid of clusterSet) {
      const e = skeleton.edges.get(eid);
      if (!e) continue;
      for (const nid of [e.startNodeId, e.endNodeId]) {
        const n = skeleton.nodes.get(nid);
        if (!n) continue;
        if (n.edgeIds.some((other) => !clusterSet.has(other) && !removed.has(other) && skeleton.edges.has(other))) attachments.add(nid);
      }
    }
    let prune = false;
    if (attachments.size <= 1) {
      // Dead-end branch or isolated island — removal can't disconnect
      // anything. (Collinear chain pieces were already promoted above, so
      // whatever dangles here really is a stray.)
      prune = true;
    } else if (totalLen([...clusterSet]) <= connectorKeepLength) {
      // Short bridge between supported walls = a corner jog / junction detour.
      // COLLAPSE it: merge every cluster node into ONE corner node at the
      // attachments' centroid, so the joined walls share a node and the
      // centering passes can later square the corner up properly.
      const clusterNodes = new Set<string>();
      for (const eid of clusterSet) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        clusterNodes.add(e.startNodeId);
        clusterNodes.add(e.endNodeId);
      }
      let cx = 0;
      let cy = 0;
      for (const nid of attachments) {
        const n = skeleton.nodes.get(nid);
        if (!n) continue;
        cx += n.position.x;
        cy += n.position.y;
      }
      cx /= attachments.size;
      cy /= attachments.size;
      const survivorId = [...attachments][0];
      const survivor = skeleton.nodes.get(survivorId);
      if (survivor) {
        survivor.position.x = cx;
        survivor.position.y = cy;
        // Drop the cluster's own edges first.
        for (const eid of clusterSet) {
          const e = skeleton.edges.get(eid);
          if (!e) continue;
          skeleton.edges.delete(eid);
          for (const nid of [e.startNodeId, e.endNodeId]) {
            const n = skeleton.nodes.get(nid);
            if (n) n.edgeIds = n.edgeIds.filter((id) => id !== eid);
          }
        }
        // Re-point every other cluster node's remaining edges to the survivor.
        for (const nid of clusterNodes) {
          if (nid === survivorId) continue;
          const n = skeleton.nodes.get(nid);
          if (!n) continue;
          for (const eid of [...n.edgeIds]) {
            const e = skeleton.edges.get(eid);
            if (!e) continue;
            if (e.startNodeId === nid) e.startNodeId = survivorId;
            if (e.endNodeId === nid) e.endNodeId = survivorId;
            if (e.startNodeId === e.endNodeId) {
              // Degenerate after the merge — drop it.
              skeleton.edges.delete(eid);
              survivor.edgeIds = survivor.edgeIds.filter((id) => id !== eid);
            } else if (!survivor.edgeIds.includes(eid)) {
              survivor.edgeIds.push(eid);
            }
          }
          skeleton.nodes.delete(nid);
        }
        if (survivor.edgeIds.length === 0) skeleton.nodes.delete(survivorId);
      }
      continue; // handled in place — nothing to add to `removed`
    } else {
      // Long bridging chain. CONTEXT check first: when the cluster's ends
      // CONTINUE the supported walls they attach to (roughly collinear at
      // ≥2 attachments), this is a gap in a real wall run — the polygon
      // evidence is simply missing there — not a noise chain criss-crossing
      // a room (those attach at arbitrary angles). Keep it. Otherwise fall
      // back to the connectivity rule: remove only if the graph stays
      // exactly as connected without it.
      if (countContinuations(clusterSet, attachments, removed) >= 2) {
        prune = false;
      } else {
        const excluded = new Set([...removed, ...clusterSet]);
        prune = componentCount(excluded) <= baseComponents;
      }
    }
    if (prune) for (const eid of clusterSet) removed.add(eid);
  }

  for (const eid of removed) {
    const edge = skeleton.edges.get(eid);
    if (!edge) continue;
    skeleton.edges.delete(eid);
    for (const nid of [edge.startNodeId, edge.endNodeId]) {
      const n = skeleton.nodes.get(nid);
      if (!n) continue;
      n.edgeIds = n.edgeIds.filter((id) => id !== eid);
      if (n.edgeIds.length === 0) skeleton.nodes.delete(nid);
    }
  }
  return skeleton;
}

/**
 * SNAP SMALL SEGMENTS — regularize short edges against their big neighbors.
 *
 * Raster traces express corners and steps as short segments at slightly-off
 * angles. Every edge SHORTER than `snapSmallMaxLength` must end up either
 * COLLINEAR with or PERPENDICULAR to the big-edge grid — those are the only
 * two permitted orientations — while big edges NEVER rotate: nodes shared
 * with big edges may only SLIDE along those edges' own lines (a node where
 * two crossing bigs meet is pinned; a T-junction's collinear through-pair
 * counts as one slide axis).
 *
 * Two stages:
 *  1. CORNER SOLVE — a run of small segments joining exactly two big axes
 *     that meet at an angle collapses into their INTERSECTION point, both
 *     big endpoints sliding along their own lines onto it: an exact corner.
 *  2. AXIS SOLVER — every remaining run is solved as a constraint system
 *     (Gauss–Seidel, like the layout editor's orthogonalize solver): rotate
 *     each small edge about its midpoint onto its target axis, project the
 *     endpoints back onto their slide-line/pinned constraints, iterate.
 */
export function snapSmallSegmentsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxLen = kwargs?.snapSmallMaxLength ?? 0.5;

  const nodePos = (id: string): THREE.Vector3 | null => skeleton.nodes.get(id)?.position ?? null;
  const edgeLen = (e: SkeletonEdge): number => {
    const a = nodePos(e.startNodeId);
    const b = nodePos(e.endNodeId);
    if (!a || !b) return 0;
    return Math.hypot(b.x - a.x, b.y - a.y);
  };
  const neighborsAt = (nid: string): string[] => skeleton.nodes.get(nid)?.edgeIds ?? [];

  // "Processed" edges have authoritative directions: every big edge, plus
  // each small edge once it has been snapped.
  const processed = new Set<string>();
  const smalls = new Set<string>();
  for (const e of skeleton.edges.values()) {
    if (e.isJunctionEdge) continue;
    const len = edgeLen(e);
    if (len < 1e-9) continue;
    if (len >= maxLen) processed.add(e.id);
    else smalls.add(e.id);
  }
  if (smalls.size === 0) return skeleton;

  /** Connected clusters of the CURRENT small-edge set. */
  const buildClusters = (): string[][] => {
    const clusterOf = new Map<string, number>();
    let clusterCount = 0;
    for (const sid of smalls) {
      if (clusterOf.has(sid)) continue;
      const cluster = clusterCount++;
      const stack = [sid];
      clusterOf.set(sid, cluster);
      while (stack.length > 0) {
        const cur = skeleton.edges.get(stack.pop() as string);
        if (!cur) continue;
        for (const nid of [cur.startNodeId, cur.endNodeId]) {
          for (const nb of neighborsAt(nid)) {
            if (!smalls.has(nb) || clusterOf.has(nb)) continue;
            clusterOf.set(nb, cluster);
            stack.push(nb);
          }
        }
      }
    }
    const clusters: string[][] = Array.from({ length: clusterCount }, () => []);
    for (const [eid, c] of clusterOf) clusters[c].push(eid);
    return clusters;
  };

  /** Common axis of the big edges at an attachment node: the longest one's
   *  line through the node — valid only when ALL of them are parallel to
   *  it (a T-junction's through-wall is two collinear edges; sliding the
   *  node along their shared axis preserves every direction). Mixed
   *  directions → null (any move would rotate one). */
  const axisOf = (bigs: SkeletonEdge[], nid: string): { px: number; py: number; ux: number; uy: number; len: number } | null => {
    let best: SkeletonEdge | null = null;
    let bestLen = 0;
    for (const b of bigs) {
      const l = edgeLen(b);
      if (l > bestLen) {
        best = b;
        bestLen = l;
      }
    }
    if (!best) return null;
    const otherId = best.startNodeId === nid ? best.endNodeId : best.startNodeId;
    const p = nodePos(otherId);
    const n = nodePos(nid);
    if (!p || !n) return null;
    const len = Math.hypot(n.x - p.x, n.y - p.y);
    if (len < 1e-9) return null;
    const ux = (n.x - p.x) / len;
    const uy = (n.y - p.y) / len;
    for (const b of bigs) {
      if (b.id === best.id) continue;
      const ba = nodePos(b.startNodeId);
      const bb = nodePos(b.endNodeId);
      if (!ba || !bb) continue;
      const bl = Math.hypot(bb.x - ba.x, bb.y - ba.y);
      if (bl < 1e-9) continue;
      if (Math.abs(((bb.x - ba.x) / bl) * uy - ((bb.y - ba.y) / bl) * ux) > 0.17) return null;
    }
    return { px: p.x, py: p.y, ux, uy, len };
  };

  // --- MULTI-PART CORNER SOLVE ---
  // A connected run of small segments joining exactly TWO big edges that
  // meet at an angle is a corner artifact: solve it by INTERSECTING the two
  // big edges' axes — both big endpoints slide along their own lines onto
  // the intersection (directions preserved, only lengths change) and the
  // whole run collapses into that single corner node. Runs that don't fit
  // the shape (near-parallel bigs, ≠2 attachments, intersection too far)
  // fall through to the per-edge snap below.
  {
    const clusters = buildClusters();
    for (const cluster of clusters) {
      const clusterSet = new Set(cluster);
      const clusterNodes = new Set<string>();
      for (const eid of clusterSet) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        clusterNodes.add(e.startNodeId);
        clusterNodes.add(e.endNodeId);
      }
      const attachments = [...clusterNodes].filter((nid) => neighborsAt(nid).some((oid) => !clusterSet.has(oid) && processed.has(oid)));
      if (attachments.length !== 2) continue;
      const bigsAt = (nid: string): SkeletonEdge[] => neighborsAt(nid).flatMap((oid) => (!clusterSet.has(oid) && processed.has(oid) && skeleton.edges.has(oid) ? [skeleton.edges.get(oid) as SkeletonEdge] : []));
      const [naId, nbId] = attachments;
      const bigsA = bigsAt(naId);
      const bigsB = bigsAt(nbId);
      if (bigsA.length === 0 || bigsB.length === 0) continue;
      if (bigsA.some((a) => bigsB.some((b) => a.id === b.id))) continue;
      const na = skeleton.nodes.get(naId);
      const nb = skeleton.nodes.get(nbId);
      if (!na || !nb) continue;
      const axisA = axisOf(bigsA, naId);
      const axisB = axisOf(bigsB, nbId);
      if (!axisA || !axisB) continue;
      const cross = axisA.ux * axisB.uy - axisA.uy * axisB.ux;
      if (Math.abs(cross) < 0.5) continue; // < ~30° apart — not a corner, snap per edge instead
      // pA + t·dA = pB + u·dB → both parameters via the cross-product solve.
      const t = ((axisB.px - axisA.px) * axisB.uy - (axisB.py - axisA.py) * axisB.ux) / cross;
      const u = ((axisB.px - axisA.px) * axisA.uy - (axisB.py - axisA.py) * axisA.ux) / cross;
      // The corner must extend/trim each big axis without flipping it, and
      // stay near the run it replaces.
      if (t < 0.05 || u < 0.05) continue;
      if (Math.abs(t - axisA.len) > 1.5 || Math.abs(u - axisB.len) > 1.5) continue;
      const px = axisA.px + axisA.ux * t;
      const py = axisA.py + axisA.uy * t;

      // Collapse: drop the run's edges, land `na` on the intersection, and
      // re-point everything still on `nb` (the other big edge) to `na`.
      for (const eid of clusterSet) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        skeleton.edges.delete(eid);
        smalls.delete(eid);
        for (const nid of [e.startNodeId, e.endNodeId]) {
          const n = skeleton.nodes.get(nid);
          if (n) n.edgeIds = n.edgeIds.filter((id) => id !== eid);
        }
      }
      na.position.x = px;
      na.position.y = py;
      for (const eid of [...nb.edgeIds]) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        if (e.startNodeId === nbId) e.startNodeId = naId;
        if (e.endNodeId === nbId) e.endNodeId = naId;
        if (e.startNodeId === e.endNodeId) {
          skeleton.edges.delete(eid);
          na.edgeIds = na.edgeIds.filter((id) => id !== eid);
        } else if (!na.edgeIds.includes(eid)) {
          na.edgeIds.push(eid);
        }
      }
      skeleton.nodes.delete(nbId);
      for (const nid of clusterNodes) {
        if (nid === naId) continue;
        const n = skeleton.nodes.get(nid);
        if (n && n.edgeIds.length === 0) skeleton.nodes.delete(nid);
      }
    }
  }
  if (smalls.size === 0) return skeleton;

  // --- AXIS SOLVER ---
  // The remaining small runs are solved as a CONSTRAINT SYSTEM per cluster:
  // every small edge must land EXACTLY collinear with or perpendicular to
  // the cluster's big-edge grid (the only two permitted orientations), while
  // nodes shared with big edges may only SLIDE along those edges' lines —
  // big directions are hard constraints, and a node on two crossing bigs is
  // pinned where it is. Gauss–Seidel iteration (same strategy as the layout
  // editor's orthogonalize solver): rotate each small edge about its
  // midpoint onto its target axis, project every endpoint back onto its
  // constraint, repeat until stable.
  interface NodeConstraint {
    kind: 'free' | 'fixed' | 'line';
    px?: number;
    py?: number;
    ux?: number;
    uy?: number;
  }
  for (const cluster of buildClusters()) {
    const clusterSet = new Set(cluster);
    const nodeIds = new Set<string>();
    for (const eid of clusterSet) {
      const e = skeleton.edges.get(eid);
      if (!e) continue;
      nodeIds.add(e.startNodeId);
      nodeIds.add(e.endNodeId);
    }

    // Grid orientation: the longest big edge touching the cluster. No big
    // anywhere → no reference orientation → leave the run untouched.
    let grid: { ux: number; uy: number } | null = null;
    let gridLen = 0;
    for (const nid of nodeIds) {
      for (const oid of neighborsAt(nid)) {
        if (clusterSet.has(oid) || !processed.has(oid)) continue;
        const o = skeleton.edges.get(oid);
        if (!o) continue;
        const ol = edgeLen(o);
        if (ol <= gridLen) continue;
        const a = nodePos(o.startNodeId);
        const b = nodePos(o.endNodeId);
        if (!a || !b) continue;
        gridLen = ol;
        grid = { ux: (b.x - a.x) / ol, uy: (b.y - a.y) / ol };
      }
    }
    if (!grid) continue;

    // Per-node constraints from the adjacent big edges.
    const constraints = new Map<string, NodeConstraint>();
    for (const nid of nodeIds) {
      const bigs: SkeletonEdge[] = [];
      for (const oid of neighborsAt(nid)) {
        if (clusterSet.has(oid) || !processed.has(oid)) continue;
        const o = skeleton.edges.get(oid);
        if (o) bigs.push(o);
      }
      if (bigs.length === 0) {
        constraints.set(nid, { kind: 'free' });
        continue;
      }
      const axis = axisOf(bigs, nid);
      if (axis) {
        constraints.set(nid, { kind: 'line', px: axis.px, py: axis.py, ux: axis.ux, uy: axis.uy });
      } else {
        const n = nodePos(nid);
        constraints.set(nid, n ? { kind: 'fixed', px: n.x, py: n.y } : { kind: 'free' });
      }
    }

    // Per-edge target axis, decided ONCE from the initial direction so the
    // iteration can't oscillate between the two permitted orientations.
    const targets = new Map<string, { ax: number; ay: number }>();
    for (const eid of clusterSet) {
      const e = skeleton.edges.get(eid);
      if (!e) continue;
      const a = nodePos(e.startNodeId);
      const b = nodePos(e.endNodeId);
      if (!a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dPar = dx * grid.ux + dy * grid.uy;
      const dPerp = dx * -grid.uy + dy * grid.ux;
      targets.set(eid, Math.abs(dPar) >= Math.abs(dPerp) ? { ax: grid.ux, ay: grid.uy } : { ax: -grid.uy, ay: grid.ux });
    }

    const project = (c: NodeConstraint, x: number, y: number): { x: number; y: number } => {
      if (c.kind === 'fixed') return { x: c.px as number, y: c.py as number };
      if (c.kind === 'line') {
        const px = c.px as number;
        const py = c.py as number;
        const ux = c.ux as number;
        const uy = c.uy as number;
        const t = (x - px) * ux + (y - py) * uy;
        return { x: px + ux * t, y: py + uy * t };
      }
      return { x, y };
    };

    for (let iter = 0; iter < 60; iter++) {
      let maxMove = 0;
      for (const eid of clusterSet) {
        const e = skeleton.edges.get(eid);
        const t = targets.get(eid);
        if (!e || !t) continue;
        const an = skeleton.nodes.get(e.startNodeId);
        const bn = skeleton.nodes.get(e.endNodeId);
        if (!an || !bn) continue;
        const dx = bn.position.x - an.position.x;
        const dy = bn.position.y - an.position.y;
        const half = Math.hypot(dx, dy) / 2;
        if (half < 1e-9) continue;
        const sign = dx * t.ax + dy * t.ay >= 0 ? 1 : -1;
        const mx = (an.position.x + bn.position.x) / 2;
        const my = (an.position.y + bn.position.y) / 2;
        const pa = project(constraints.get(e.startNodeId) ?? { kind: 'free' }, mx - t.ax * half * sign, my - t.ay * half * sign);
        const pb = project(constraints.get(e.endNodeId) ?? { kind: 'free' }, mx + t.ax * half * sign, my + t.ay * half * sign);
        maxMove = Math.max(maxMove, Math.abs(pa.x - an.position.x), Math.abs(pa.y - an.position.y), Math.abs(pb.x - bn.position.x), Math.abs(pb.y - bn.position.y));
        an.position.x = pa.x;
        an.position.y = pa.y;
        bn.position.x = pb.x;
        bn.position.y = pb.y;
      }
      if (maxMove < 1e-5) break;
    }
  }
  return skeleton;
}

/**
 * NUDGE WALLS INTO BOUNDS — walls accidentally sitting clearly INSIDE a room
 * or OUTSIDE every outline are translated laterally back into the wall band.
 *
 * ANGLES ARE SACRED: edges move only as rigid, COLLINEAR CHAINS translated
 * along their common normal, and only when every edge hanging off the chain
 * is parallel to that normal — the junction nodes then slide ALONG those
 * edges (grow/shrink, never a rotation). When no legal, sign-consistent push
 * within `nudgeMaxDistance` exists, the chain simply does not move.
 *
 * Violations are judged at interior sample points (¼, ½, ¾ of each edge)
 * with a boundary tolerance, so a line ON a wall face never counts. The push
 * lands the wall in the MIDDLE of its band — between the violated face and
 * the next boundary behind it (outline or neighboring room) — falling back
 * to just past the face when no plausible far side exists.
 */
export function nudgeWallsIntoBoundsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const rc = skeleton.roomConnectivity ?? [];
  if (rc.length === 0) return skeleton;
  const maxNudge = kwargs?.nudgeMaxDistance ?? 1.0;
  const CLEARANCE = 0.02;
  // Violation sensitivity: 1 cm — enough to reject boundary jitter while
  // still catching walls hugging a face just inside the room (the common
  // real-world case; a 5 cm tolerance silently ignored those).
  const BOUND_TOL = 0.01;
  const OUTER_PREFIX = '__OUTER__';

  const roomRings: THREE.Vector3[][] = [];
  const outlineRings: THREE.Vector3[][] = [];
  for (const room of rc) {
    if (room.polygon.length < 3) continue;
    if (room.identifier.startsWith(OUTER_PREFIX)) outlineRings.push(room.polygon);
    else roomRings.push(room.polygon);
  }
  if (roomRings.length === 0 && outlineRings.length === 0) return skeleton;

  const pointInRing = (px: number, py: number, poly: THREE.Vector3[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const yi = poly[i].y;
      const yj = poly[j].y;
      if (yi > py === yj > py) continue;
      const xCross = poly[j].x + ((py - yj) / (yi - yj)) * (poly[i].x - poly[j].x);
      if (px < xCross) inside = !inside;
    }
    return inside;
  };
  const nearRing = (px: number, py: number, ring: THREE.Vector3[]): boolean => {
    for (let k = 0; k < ring.length; k++) {
      const a = ring[k];
      const b = ring[(k + 1) % ring.length];
      if (pointSegDistance(px, py, a.x, a.y, b.x, b.y) <= BOUND_TOL) return true;
    }
    return false;
  };
  /** Ring the point is CLEARLY inside (rooms), or null. */
  const violatedRoom = (px: number, py: number): THREE.Vector3[] | null => {
    for (const ring of roomRings) {
      if (pointInRing(px, py, ring) && !nearRing(px, py, ring)) return ring;
    }
    return null;
  };
  /** Clearly outside every outline (when outlines exist at all). */
  const violatesOutline = (px: number, py: number): boolean => {
    if (outlineRings.length === 0) return false;
    for (const ring of outlineRings) {
      if (pointInRing(px, py, ring) || nearRing(px, py, ring)) return false;
    }
    return true;
  };

  // --- Collinear chains (union-find over connected, parallel edges). ---
  interface Geom {
    e: SkeletonEdge;
    ux: number;
    uy: number;
    len: number;
  }
  const geoms = new Map<string, Geom>();
  for (const e of skeleton.edges.values()) {
    if (e.isJunctionEdge) continue;
    const a = skeleton.nodes.get(e.startNodeId);
    const b = skeleton.nodes.get(e.endNodeId);
    if (!a || !b) continue;
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    geoms.set(e.id, { e, ux: dx / len, uy: dy / len, len });
  }
  const ids = [...geoms.keys()];
  const parent = new Map<string, string>(ids.map((id) => [id, id]));
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r) as string;
    parent.set(x, r);
    return r;
  };
  const nodeEdges = new Map<string, string[]>();
  for (const [id, g] of geoms) {
    for (const nid of [g.e.startNodeId, g.e.endNodeId]) {
      const l = nodeEdges.get(nid);
      if (l) l.push(id);
      else nodeEdges.set(nid, [id]);
    }
  }
  for (const list of nodeEdges.values()) {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const ga = geoms.get(list[i]) as Geom;
        const gb = geoms.get(list[j]) as Geom;
        if (Math.abs(ga.ux * gb.uy - ga.uy * gb.ux) > 0.1) continue; // not collinear (~6°)
        const ra = find(list[i]);
        const rb = find(list[j]);
        if (ra !== rb) parent.set(ra, rb);
      }
    }
  }
  const chains = new Map<string, Geom[]>();
  for (const id of ids) {
    const r = find(id);
    const l = chains.get(r);
    if (l) l.push(geoms.get(id) as Geom);
    else chains.set(r, [geoms.get(id) as Geom]);
  }

  // --- END-TRIM FIRST: a FREE edge end that overshoots across a face (a
  // degenerate tail poking into a room or out of the outline) is SHORTENED
  // back along its own direction — angle-preserving — instead of letting the
  // overshoot drag its whole chain sideways. Only the offending tail moves;
  // exact boundary crossings are used, so even a sub-sample overshoot the
  // ¼/½/¾ probes below would miss gets cleaned up.
  for (const g of geoms.values()) {
    for (const [endId, otherId] of [
      [g.e.endNodeId, g.e.startNodeId],
      [g.e.startNodeId, g.e.endNodeId],
    ] as const) {
      const endNode = skeleton.nodes.get(endId);
      const otherNode = skeleton.nodes.get(otherId);
      if (!endNode || !otherNode) continue;
      if (endNode.edgeIds.length !== 1) continue; // only free ends may shrink
      const dx = endNode.position.x - otherNode.position.x;
      const dy = endNode.position.y - otherNode.position.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      const ux = dx / len;
      const uy = dy / len;
      const ox = otherNode.position.x;
      const oy = otherNode.position.y;
      // All boundary crossings along other→end, ordered.
      const cuts: number[] = [];
      for (const ringPoly of [...roomRings, ...outlineRings]) {
        for (let k = 0; k < ringPoly.length; k++) {
          const sa = ringPoly[k];
          const sb = ringPoly[(k + 1) % ringPoly.length];
          const svx = sb.x - sa.x;
          const svy = sb.y - sa.y;
          const denom = ux * svy - uy * svx;
          if (Math.abs(denom) < 1e-9) continue;
          const rvx = sa.x - ox;
          const rvy = sa.y - oy;
          const t = (rvx * svy - rvy * svx) / denom;
          const u = (rvx * uy - rvy * ux) / denom;
          if (t <= 1e-6 || t >= len - 1e-6) continue;
          if (u < 0 || u > 1) continue;
          cuts.push(t);
        }
      }
      if (cuts.length === 0) continue;
      cuts.sort((a2, b2) => a2 - b2);
      // The first inter-crossing interval that violates marks where the edge
      // leaves legal wall space — cut just before it.
      const bounds = [0, ...cuts, len];
      let cutAt: number | null = null;
      for (let i = 0; i + 1 < bounds.length; i++) {
        const mid = (bounds[i] + bounds[i + 1]) / 2;
        if (violatedRoom(ox + ux * mid, oy + uy * mid) || violatesOutline(ox + ux * mid, oy + uy * mid)) {
          cutAt = bounds[i];
          break;
        }
      }
      if (cutAt === null) continue; // crossings but no actual violation
      const tNew = cutAt - CLEARANCE;
      // Refuse when the edge violates from its very start (nothing left to
      // keep) or the bite exceeds the nudge budget — the translate logic
      // below still gets its chance on those.
      if (tNew < 0.02 || len - tNew > maxNudge) continue;
      endNode.position.x = ox + ux * tNew;
      endNode.position.y = oy + uy * tNew;
      g.len = tNew;
    }
  }

  for (const chain of chains.values()) {
    // Chain frame: the longest edge's direction and its normal.
    let longest = chain[0];
    for (const g of chain) if (g.len > longest.len) longest = g;
    const cux = longest.ux;
    const cuy = longest.uy;
    const nx = -cuy;
    const ny = cux;

    // Required push: for every violating sample, the nearest crossing of a
    // roughly-parallel ring segment along ±normal (+ clearance). All samples
    // must agree on the sign; the largest magnitude wins.
    let required: number | null = null;
    let blocked = false;
    for (const g of chain) {
      const a = skeleton.nodes.get(g.e.startNodeId);
      const b = skeleton.nodes.get(g.e.endNodeId);
      if (!a || !b) continue;
      for (const t of [0.25, 0.5, 0.75]) {
        const px = a.position.x + (b.position.x - a.position.x) * t;
        const py = a.position.y + (b.position.y - a.position.y) * t;
        let rings: THREE.Vector3[][] | null = null;
        const room = violatedRoom(px, py);
        if (room) rings = [room];
        else if (violatesOutline(px, py)) rings = outlineRings;
        if (!rings) continue;
        /** Crossings of parallel-ish segments of `search` along ±normal. */
        const crossingsOf = (search: THREE.Vector3[][]): number[] => {
          const out: number[] = [];
          for (const ringPoly of search) {
            for (let k = 0; k < ringPoly.length; k++) {
              const sa = ringPoly[k];
              const sb = ringPoly[(k + 1) % ringPoly.length];
              const sx = sb.x - sa.x;
              const sy = sb.y - sa.y;
              const slen = Math.hypot(sx, sy);
              if (slen < 1e-9) continue;
              const sux = sx / slen;
              const suy = sy / slen;
              if (Math.abs(cux * sux + cuy * suy) < 0.866) continue; // not parallel-ish (~30°)
              const denom = nx * suy - ny * sux;
              if (Math.abs(denom) < 1e-6) continue;
              const cross = (sa.x - px) * suy - (sa.y - py) * sux;
              const tt = cross / denom;
              const u = ((px - sa.x) * ny - (py - sa.y) * nx) / (sux * ny - suy * nx);
              if (u < -0.2 || u > slen + 0.2) continue; // crossing beyond the segment
              out.push(tt);
            }
          }
          return out;
        };
        // Nearest crossing of the VIOLATED boundary — the face to escape over.
        let bestT: number | null = null;
        for (const tt of crossingsOf(rings)) {
          if (bestT === null || Math.abs(tt) < Math.abs(bestT)) bestT = tt;
        }
        if (bestT === null) {
          blocked = true;
          break;
        }
        // The wall BAND's far side: the next crossing beyond the face, over
        // ANY ring (outline or a neighboring room). Land the wall in the
        // MIDDLE of the band; without a plausible far side, land just past
        // the face instead.
        const MAX_BAND = 1.0;
        let farT: number | null = null;
        for (const tt of crossingsOf([...roomRings, ...outlineRings])) {
          if (Math.sign(tt) !== Math.sign(bestT) || Math.abs(tt) <= Math.abs(bestT) + 1e-6) continue;
          if (farT === null || Math.abs(tt) < Math.abs(farT)) farT = tt;
        }
        const push = farT !== null && Math.abs(farT - bestT) <= MAX_BAND ? (bestT + farT) / 2 : bestT + Math.sign(bestT) * CLEARANCE;
        if (required === null) {
          required = push;
        } else if (Math.sign(push) !== Math.sign(required)) {
          blocked = true;
          break;
        } else if (Math.abs(push) > Math.abs(required)) {
          required = push;
        }
      }
      if (blocked) break;
    }
    if (blocked || required === null || Math.abs(required) > maxNudge) continue;

    // Junction handling: a chain node with NON-chain neighbors doesn't just
    // translate — it SLIDES ALONG the neighbors' shared line onto the pushed
    // chain line (their intersection), so every neighbor keeps its exact
    // angle whatever that angle is (perpendicular, diagonal, anything).
    // Blocked only when a neighbor runs (near-)parallel to the chain —
    // sliding along it can never reach the pushed line — when neighbors at
    // one node disagree on direction, or when the slide would fling the node
    // (shallow angles). Then the whole chain stays put.
    const chainIds = new Set(chain.map((g) => g.e.id));
    const chainNodes = new Set<string>();
    for (const g of chain) {
      chainNodes.add(g.e.startNodeId);
      chainNodes.add(g.e.endNodeId);
    }
    const moves: Array<{ node: SkeletonNode; x: number; y: number }> = [];
    let legal = true;
    for (const nid of chainNodes) {
      const node = skeleton.nodes.get(nid);
      if (!node) continue;
      const neighbors: Geom[] = [];
      for (const eid of node.edgeIds) {
        if (chainIds.has(eid)) continue;
        const og = geoms.get(eid);
        if (og) neighbors.push(og);
      }
      const tx = node.position.x + nx * required;
      const ty = node.position.y + ny * required;
      if (neighbors.length === 0) {
        moves.push({ node, x: tx, y: ty });
        continue;
      }
      // All neighbors pass through this node — pairwise parallel means one
      // shared line to slide along; mixed directions can't all be satisfied.
      const nb = neighbors[0];
      let mixed = false;
      for (const o of neighbors) {
        if (Math.abs(o.ux * nb.uy - o.uy * nb.ux) > 0.17) {
          mixed = true;
          break;
        }
      }
      if (mixed) {
        legal = false;
        break;
      }
      // Slide distance s along the neighbor line to land on the pushed chain
      // line: node + s·û must satisfy cross(P − T, chainDir) = 0.
      const denom = nb.ux * cuy - nb.uy * cux; // cross(û, chainDir)
      if (Math.abs(denom) < 0.17) {
        legal = false; // neighbor ~parallel to the chain — unreachable
        break;
      }
      const s = -required / denom;
      if (Math.abs(s) > maxNudge * 4) {
        legal = false; // shallow angle — the slide would fling the node
        break;
      }
      moves.push({ node, x: node.position.x + nb.ux * s, y: node.position.y + nb.uy * s });
    }
    if (!legal) continue;

    for (const m of moves) {
      m.node.position.x = m.x;
      m.node.position.y = m.y;
    }
  }
  return skeleton;
}

export function centerEdgesInWallsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  centerMatchWallsPass(skeleton, kwargs);
  centerCombineSharedPass(skeleton, kwargs);
  centerReconnectPass(skeleton, kwargs);
  return skeleton;
}

/**
 * Create edges between nodes based on room connectivity.
 * This connects nodes according to the original room polygon edges.
 */
export function createEdgesPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity || !skeleton.vertexToNodeMap) {
    return skeleton;
  }

  // Clear existing edges
  skeleton.edges.clear();
  for (const node of skeleton.nodes.values()) {
    node.edgeIds = [];
  }

  let edgeIdCounter = 0;

  for (const room of skeleton.roomConnectivity) {
    const vertexCount = room.polygon.length;

    for (let i = 0; i < vertexCount; i++) {
      const currentIndex = room.vertexStartIndex + i;
      const nextIndex = room.vertexStartIndex + ((i + 1) % vertexCount);

      const startNodeId = skeleton.vertexToNodeMap.get(currentIndex);
      const endNodeId = skeleton.vertexToNodeMap.get(nextIndex);

      if (!startNodeId || !endNodeId || startNodeId === endNodeId) continue;

      // Check if edge already exists
      const existingEdge = findEdgeBetweenNodes(skeleton, startNodeId, endNodeId);

      if (existingEdge) {
        if (!existingEdge.roomIds.includes(room.identifier)) {
          existingEdge.roomIds.push(room.identifier);
        }
      } else {
        const edgeId = `edge_${edgeIdCounter++}`;
        const edge: SkeletonEdge = {
          id: edgeId,
          startNodeId,
          endNodeId,
          roomIds: [room.identifier],
          leftSize: 0.1,
          rightSize: 0.1,
          wallType: 'INNER',
          selected: false,
        };
        skeleton.edges.set(edgeId, edge);

        const startNode = skeleton.nodes.get(startNodeId);
        const endNode = skeleton.nodes.get(endNodeId);
        if (startNode) startNode.edgeIds.push(edgeId);
        if (endNode) endNode.edgeIds.push(edgeId);
      }
    }
  }

  return skeleton;
}

/**
 * Find all edge crossings in the skeleton
 */
function findEdgeCrossings(skeleton: FloorSkeleton): Array<{ edge1Id: string; edge2Id: string; intersection: THREE.Vector3 }> {
  const crossings: Array<{ edge1Id: string; edge2Id: string; intersection: THREE.Vector3 }> = [];
  const edgeIds = Array.from(skeleton.edges.keys());

  for (let i = 0; i < edgeIds.length; i++) {
    for (let j = i + 1; j < edgeIds.length; j++) {
      const edge1 = skeleton.edges.get(edgeIds[i]);
      const edge2 = skeleton.edges.get(edgeIds[j]);
      if (!edge1 || !edge2) continue;

      // Skip if edges share a node
      if (edge1.startNodeId === edge2.startNodeId || edge1.startNodeId === edge2.endNodeId || edge1.endNodeId === edge2.startNodeId || edge1.endNodeId === edge2.endNodeId) {
        continue;
      }

      const node1Start = skeleton.nodes.get(edge1.startNodeId);
      const node1End = skeleton.nodes.get(edge1.endNodeId);
      const node2Start = skeleton.nodes.get(edge2.startNodeId);
      const node2End = skeleton.nodes.get(edge2.endNodeId);
      if (!node1Start || !node1End || !node2Start || !node2End) continue;

      const intersection = segmentIntersection2D(node1Start.position, node1End.position, node2Start.position, node2End.position);

      if (intersection) {
        crossings.push({
          edge1Id: edgeIds[i],
          edge2Id: edgeIds[j],
          intersection,
        });
      }
    }
  }

  return crossings;
}

/**
 * Delete skeleton edges that cross any non-skeleton polygon edge — i.e. the
 * edges of real rooms and the synthetic outer-shell rings, but NOT cavity
 * (hole) rings (skeleton edges are allowed to pass through fireplaces /
 * courtyards / light wells).
 *
 * "Cross" means proper segment intersection: the two segments meet at a point
 * strictly interior to both, so edges that share an endpoint with a polygon
 * edge or run collinear along it are not deleted.
 */
export function deleteSkeletonEdgesCrossingPolygonsPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  if (!skeleton.roomConnectivity) return skeleton;

  // Collect all polygon segments to test against, excluding cavity rings.
  type Seg = { ax: number; ay: number; bx: number; by: number };
  const polygonSegs: Seg[] = [];
  for (const room of skeleton.roomConnectivity) {
    if (room.identifier.startsWith('__CAVITY__')) continue;
    const poly = room.polygon;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      if (Math.abs(b.x - a.x) < 1e-9 && Math.abs(b.y - a.y) < 1e-9) continue;
      polygonSegs.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y });
    }
  }
  if (polygonSegs.length === 0) return skeleton;

  // Proper segment intersection: returns true only when the two segments
  // cross at a point strictly interior to both (not at endpoints, not collinear).
  const epsilon = 1e-4;
  const segmentsCross = (p1x: number, p1y: number, p2x: number, p2y: number, q1x: number, q1y: number, q2x: number, q2y: number): boolean => {
    const rx = p2x - p1x;
    const ry = p2y - p1y;
    const sx = q2x - q1x;
    const sy = q2y - q1y;
    const denom = rx * sy - ry * sx;
    if (Math.abs(denom) < 1e-12) return false; // parallel or collinear
    const dx = q1x - p1x;
    const dy = q1y - p1y;
    const t = (dx * sy - dy * sx) / denom;
    const u = (dx * ry - dy * rx) / denom;
    return t > epsilon && t < 1 - epsilon && u > epsilon && u < 1 - epsilon;
  };

  const edgeIdsToRemove: string[] = [];
  for (const edge of skeleton.edges.values()) {
    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (!startNode || !endNode) continue;
    const ex1 = startNode.position.x;
    const ey1 = startNode.position.y;
    const ex2 = endNode.position.x;
    const ey2 = endNode.position.y;

    for (const seg of polygonSegs) {
      if (segmentsCross(ex1, ey1, ex2, ey2, seg.ax, seg.ay, seg.bx, seg.by)) {
        edgeIdsToRemove.push(edge.id);
        break;
      }
    }
  }

  for (const edgeId of edgeIdsToRemove) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;
    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (startNode) startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
    if (endNode) endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
    skeleton.edges.delete(edgeId);
  }

  return skeleton;
}

/**
 * Split skeleton edges at nearby nodes WITHOUT moving the nodes.
 *
 * Iterates per NODE: for each node, finds the closest skeleton edge it isn't
 * already an endpoint of (within `snapTolerance` perpendicular distance, and
 * with the projection further than `endpointMargin` from either endpoint),
 * and splits that edge at the node's exact position. The two new sub-edges
 * form a slight V-bend through the node — unlike `snapNodesToEdgesPass`,
 * the node is not relocated onto the edge line.
 *
 * Per-node ownership (vs. per-edge) means a dangling node always pairs with
 * its single closest edge, so doubled parallel edges don't compete for the
 * same node.
 */
export function splitEdgesThroughNearbyNodesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const snapTolerance = kwargs?.snapTolerance ?? 0.1;
  const endpointMargin = kwargs?.endpointMargin ?? 0.05;
  let newEdgeIdCounter = skeleton.edges.size;

  // Iterate until a full pass over all nodes produces no further splits.
  let changed = true;
  let safety = 0;
  while (changed && safety < 100) {
    changed = false;
    safety++;
    const nodeIds = Array.from(skeleton.nodes.keys());

    for (const nodeId of nodeIds) {
      const node = skeleton.nodes.get(nodeId);
      if (!node) continue;

      // Find the nearest qualifying edge that this node is NOT already part of.
      let bestEdgeId: string | null = null;
      let bestDist = snapTolerance;
      for (const edge of skeleton.edges.values()) {
        if (edge.startNodeId === nodeId || edge.endNodeId === nodeId) continue;
        const startNode = skeleton.nodes.get(edge.startNodeId);
        const endNode = skeleton.nodes.get(edge.endNodeId);
        if (!startNode || !endNode) continue;
        const segLen = startNode.position.distanceTo(endNode.position);
        if (segLen < 1e-6) continue;
        const { distance, t } = closestPointOnSegment(node.position, startNode.position, endNode.position);
        if (distance >= bestDist) continue;
        const distFromStart = t * segLen;
        const distFromEnd = (1 - t) * segLen;
        if (distFromStart < endpointMargin || distFromEnd < endpointMargin) continue;
        bestEdgeId = edge.id;
        bestDist = distance;
      }

      if (!bestEdgeId) continue;

      const edge = skeleton.edges.get(bestEdgeId);
      if (!edge) continue;
      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);
      if (!startNode || !endNode) continue;

      // Split: create startNode → node and node → endNode, drop the original edge.
      const e1Id = `edge_split_${newEdgeIdCounter++}`;
      const e2Id = `edge_split_${newEdgeIdCounter++}`;
      const e1: SkeletonEdge = {
        id: e1Id,
        startNodeId: edge.startNodeId,
        endNodeId: node.id,
        roomIds: [...edge.roomIds],
        leftSize: edge.leftSize,
        rightSize: edge.rightSize,
        wallType: edge.wallType,
        selected: false,
      };
      const e2: SkeletonEdge = {
        id: e2Id,
        startNodeId: node.id,
        endNodeId: edge.endNodeId,
        roomIds: [...edge.roomIds],
        leftSize: edge.leftSize,
        rightSize: edge.rightSize,
        wallType: edge.wallType,
        selected: false,
      };

      // Remove old edge from its endpoints' edgeIds
      startNode.edgeIds = startNode.edgeIds.filter((id) => id !== bestEdgeId);
      endNode.edgeIds = endNode.edgeIds.filter((id) => id !== bestEdgeId);
      skeleton.edges.delete(bestEdgeId);

      // Wire in new edges
      skeleton.edges.set(e1Id, e1);
      skeleton.edges.set(e2Id, e2);
      startNode.edgeIds.push(e1Id);
      node.edgeIds.push(e1Id, e2Id);
      endNode.edgeIds.push(e2Id);

      // Inherit the original edge's source rooms onto the node for downstream
      // passes that reason about node provenance.
      for (const roomId of edge.roomIds) {
        if (!node.roomIds.includes(roomId)) node.roomIds.push(roomId);
      }

      changed = true;
    }
  }

  return skeleton;
}

/**
 * Delete duplicate skeleton edges — i.e. multiple edges connecting the same
 * pair of nodes (direction-agnostic). Keeps the first encountered, drops the
 * rest. No room-id merging — the kept edge keeps its own provenance.
 */
export function deleteDuplicateEdgesPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const seen = new Map<string, string>(); // node-pair signature -> kept edge id
  const toRemove: string[] = [];

  for (const edge of skeleton.edges.values()) {
    const a = edge.startNodeId;
    const b = edge.endNodeId;
    const sig = a < b ? `${a}|${b}` : `${b}|${a}`;
    if (seen.has(sig)) {
      toRemove.push(edge.id);
    } else {
      seen.set(sig, edge.id);
    }
  }

  for (const edgeId of toRemove) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;
    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (startNode) startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
    if (endNode) endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
    skeleton.edges.delete(edgeId);
  }

  return skeleton;
}

/**
 * Rewrite every edge's `roomIds` to the intersection of its two
 * endpoint nodes' `roomIds`. Defines an edge as "belonging to" room R
 * exactly when both its endpoints touch R — the wall-along-shared-corner
 * invariant that holds for real floor plans.
 *
 * Earlier passes seed node `roomIds` from the room polygons that
 * contributed each vertex; subsequent merges + splits then push edges
 * out of sync because they propagate metadata from sibling edges
 * without re-deriving from the post-mutation endpoints. Running this
 * after structural cleanup re-establishes the invariant.
 *
 * Skips edges with a missing endpoint — those are structurally broken
 * and other passes will drop them.
 */
export function fixupEdgeRoomsFromNodesPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  for (const edge of skeleton.edges.values()) {
    const start = skeleton.nodes.get(edge.startNodeId);
    const end = skeleton.nodes.get(edge.endNodeId);
    if (!start || !end) continue;
    const endRooms = new Set(end.roomIds);
    // Walk `start.roomIds` first so the resulting order is deterministic
    // and stable across runs on identical input.
    const intersection: string[] = [];
    for (const id of start.roomIds) if (endRooms.has(id)) intersection.push(id);
    edge.roomIds = intersection;
  }
  return skeleton;
}

/**
 * Find all triangles in the skeleton graph (triplets of mutually connected
 * nodes). For each triangle whose circumscribed circle radius is above the
 * configured threshold, delete the longest of its three edges. Smaller
 * triangles are left alone — they're handled by the small-triangle collapser.
 */
export function deleteLongestLegOfLargeTrianglesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const minCircumradius = kwargs?.triangleMinCircumradius ?? 0.5;

  // Build adjacency: nodeId -> Set of neighbour nodeIds (with the connecting edge id).
  const neighbours = new Map<string, Map<string, string>>();
  for (const node of skeleton.nodes.values()) neighbours.set(node.id, new Map());
  for (const edge of skeleton.edges.values()) {
    neighbours.get(edge.startNodeId)?.set(edge.endNodeId, edge.id);
    neighbours.get(edge.endNodeId)?.set(edge.startNodeId, edge.id);
  }

  const edgesToRemove = new Set<string>();
  const seenTriangles = new Set<string>();

  for (const [aId, aNeighbours] of neighbours) {
    for (const [bId] of aNeighbours) {
      if (bId <= aId) continue;
      const bNeighbours = neighbours.get(bId);
      if (!bNeighbours) continue;
      for (const [cId] of bNeighbours) {
        if (cId <= bId) continue;
        // Triangle if A-C is also an edge.
        const eAC = aNeighbours.get(cId);
        if (!eAC) continue;
        const eAB = aNeighbours.get(bId);
        const eBC = bNeighbours.get(cId);
        if (!eAB || !eBC) continue;

        // Skip duplicate triangles (we sort the three node ids and dedupe).
        const triKey = [aId, bId, cId].sort().join('|');
        if (seenTriangles.has(triKey)) continue;
        seenTriangles.add(triKey);

        const a = skeleton.nodes.get(aId);
        const b = skeleton.nodes.get(bId);
        const c = skeleton.nodes.get(cId);
        if (!a || !b || !c) continue;

        const ab = a.position.distanceTo(b.position);
        const bc = b.position.distanceTo(c.position);
        const ac = a.position.distanceTo(c.position);

        // Circumradius via R = (abc) / (4 * area). Skip degenerate.
        // Area via 2D cross product on edges from a.
        const ux = b.position.x - a.position.x;
        const uy = b.position.y - a.position.y;
        const vx = c.position.x - a.position.x;
        const vy = c.position.y - a.position.y;
        const cross = ux * vy - uy * vx;
        const area = Math.abs(cross) / 2;
        if (area < 1e-9) continue;
        const circumradius = (ab * bc * ac) / (4 * area);
        if (circumradius < minCircumradius) continue;

        // Pick the longest edge of the three.
        let longestEdgeId = eAB;
        let longestLen = ab;
        if (bc > longestLen) {
          longestEdgeId = eBC;
          longestLen = bc;
        }
        if (ac > longestLen) {
          longestEdgeId = eAC;
          longestLen = ac;
        }
        edgesToRemove.add(longestEdgeId);
      }
    }
  }

  for (const edgeId of edgesToRemove) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;
    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (startNode) startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
    if (endNode) endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
    skeleton.edges.delete(edgeId);
  }

  return skeleton;
}

/**
 * Snap nodes that are close to edges (T-junctions) onto those edges.
 * Runs iteratively until no more snaps are possible.
 */
export function snapNodesToEdgesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const snapTolerance = kwargs?.snapTolerance ?? 0.3;
  const maxIterations = 100;
  let newEdgeIdCounter = skeleton.edges.size;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let snappedThisIteration = false;

    for (const node of skeleton.nodes.values()) {
      let bestEdge: SkeletonEdge | null = null;
      let bestDistance = snapTolerance;
      let bestClosestPoint: THREE.Vector3 | null = null;

      for (const edge of skeleton.edges.values()) {
        if (edge.startNodeId === node.id || edge.endNodeId === node.id) {
          continue;
        }

        const startNode = skeleton.nodes.get(edge.startNodeId);
        const endNode = skeleton.nodes.get(edge.endNodeId);
        if (!startNode || !endNode) continue;

        const { closestPoint, distance, t } = closestPointOnSegment(node.position, startNode.position, endNode.position);

        if (distance < bestDistance && t > 0.05 && t < 0.95) {
          bestEdge = edge;
          bestDistance = distance;
          bestClosestPoint = closestPoint;
        }
      }

      if (bestEdge && bestClosestPoint) {
        // Snap this node and split the edge
        node.position.copy(bestClosestPoint);

        const oldEdgeId = bestEdge.id;
        const oldStartNodeId = bestEdge.startNodeId;
        const oldEndNodeId = bestEdge.endNodeId;
        const oldRoomIds = [...bestEdge.roomIds];

        // Remove the old edge
        const oldStartNode = skeleton.nodes.get(oldStartNodeId);
        const oldEndNode = skeleton.nodes.get(oldEndNodeId);

        if (oldStartNode) {
          oldStartNode.edgeIds = oldStartNode.edgeIds.filter((id) => id !== oldEdgeId);
        }
        if (oldEndNode) {
          oldEndNode.edgeIds = oldEndNode.edgeIds.filter((id) => id !== oldEdgeId);
        }
        skeleton.edges.delete(oldEdgeId);

        // Create two new edges
        const edge1: SkeletonEdge = {
          id: `edge_snap_${newEdgeIdCounter++}`,
          startNodeId: oldStartNodeId,
          endNodeId: node.id,
          roomIds: [...oldRoomIds],
          leftSize: bestEdge.leftSize,
          rightSize: bestEdge.rightSize,
          wallType: bestEdge.wallType,
          selected: false,
        };

        const edge2: SkeletonEdge = {
          id: `edge_snap_${newEdgeIdCounter++}`,
          startNodeId: node.id,
          endNodeId: oldEndNodeId,
          roomIds: [...oldRoomIds],
          leftSize: bestEdge.leftSize,
          rightSize: bestEdge.rightSize,
          wallType: bestEdge.wallType,
          selected: false,
        };

        skeleton.edges.set(edge1.id, edge1);
        skeleton.edges.set(edge2.id, edge2);

        // Update node edge references
        if (oldStartNode && !oldStartNode.edgeIds.includes(edge1.id)) {
          oldStartNode.edgeIds.push(edge1.id);
        }
        if (oldEndNode && !oldEndNode.edgeIds.includes(edge2.id)) {
          oldEndNode.edgeIds.push(edge2.id);
        }
        node.edgeIds.push(edge1.id, edge2.id);

        // Add room IDs to the snapped node
        for (const roomId of oldRoomIds) {
          if (!node.roomIds.includes(roomId)) {
            node.roomIds.push(roomId);
          }
        }

        snappedThisIteration = true;
        break; // Restart iteration to re-evaluate with updated edges
      }
    }

    if (!snappedThisIteration) break;
  }

  return skeleton;
}

/**
 * Resolve edge crossings by moving the closest node to the intersection
 * or by creating a new node at the intersection point.
 *
 * @param kwargs.moveNodes - If true (default), moves existing nodes to resolve crossings.
 *                           If false, creates new nodes at intersection points.
 */
export function resolveEdgeCrossingsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const moveNodes = kwargs?.moveNodes ?? true;
  const maxIterations = 100;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const crossings = findEdgeCrossings(skeleton);

    if (crossings.length === 0) break;

    const crossing = crossings[0];
    const edge1 = skeleton.edges.get(crossing.edge1Id);
    const edge2 = skeleton.edges.get(crossing.edge2Id);

    if (!edge1 || !edge2) continue;

    const node1Start = skeleton.nodes.get(edge1.startNodeId);
    const node1End = skeleton.nodes.get(edge1.endNodeId);
    const node2Start = skeleton.nodes.get(edge2.startNodeId);
    const node2End = skeleton.nodes.get(edge2.endNodeId);
    if (!node1Start || !node1End || !node2Start || !node2End) continue;

    if (moveNodes) {
      // Move existing node to intersection
      const dist1Start = node1Start.position.distanceTo(crossing.intersection);
      const dist1End = node1End.position.distanceTo(crossing.intersection);
      const closestOnEdge1 = dist1Start < dist1End ? node1Start : node1End;
      const closestDist1 = Math.min(dist1Start, dist1End);

      const dist2Start = node2Start.position.distanceTo(crossing.intersection);
      const dist2End = node2End.position.distanceTo(crossing.intersection);
      const closestOnEdge2 = dist2Start < dist2End ? node2Start : node2End;
      const closestDist2 = Math.min(dist2Start, dist2End);

      const nodeToMove = closestDist1 < closestDist2 ? closestOnEdge1 : closestOnEdge2;
      const edgeContainingMovedNode = closestDist1 < closestDist2 ? edge1 : edge2;
      const otherEdge = closestDist1 < closestDist2 ? edge2 : edge1;
      const otherEdgeStart = closestDist1 < closestDist2 ? node2Start : node1Start;
      const otherEdgeEnd = closestDist1 < closestDist2 ? node2End : node1End;

      nodeToMove.position.copy(crossing.intersection);

      const oldEdgeId = otherEdge.id;
      otherEdgeStart.edgeIds = otherEdgeStart.edgeIds.filter((id) => id !== oldEdgeId);
      otherEdgeEnd.edgeIds = otherEdgeEnd.edgeIds.filter((id) => id !== oldEdgeId);
      skeleton.edges.delete(oldEdgeId);

      const timestamp = Date.now();
      const newEdge1Id = `edge_fix_${timestamp}_${iteration}_1`;
      const newEdge2Id = `edge_fix_${timestamp}_${iteration}_2`;

      const newEdge1: SkeletonEdge = {
        id: newEdge1Id,
        startNodeId: otherEdgeStart.id,
        endNodeId: nodeToMove.id,
        roomIds: [...otherEdge.roomIds],
        leftSize: otherEdge.leftSize,
        rightSize: otherEdge.rightSize,
        wallType: otherEdge.wallType,
        selected: false,
      };

      const newEdge2: SkeletonEdge = {
        id: newEdge2Id,
        startNodeId: nodeToMove.id,
        endNodeId: otherEdgeEnd.id,
        roomIds: [...otherEdge.roomIds],
        leftSize: otherEdge.leftSize,
        rightSize: otherEdge.rightSize,
        wallType: otherEdge.wallType,
        selected: false,
      };

      skeleton.edges.set(newEdge1Id, newEdge1);
      skeleton.edges.set(newEdge2Id, newEdge2);

      otherEdgeStart.edgeIds.push(newEdge1Id);
      nodeToMove.edgeIds.push(newEdge1Id);
      nodeToMove.edgeIds.push(newEdge2Id);
      otherEdgeEnd.edgeIds.push(newEdge2Id);

      for (const roomId of otherEdge.roomIds) {
        if (!nodeToMove.roomIds.includes(roomId)) {
          nodeToMove.roomIds.push(roomId);
        }
      }

      for (const roomId of edgeContainingMovedNode.roomIds) {
        if (!nodeToMove.roomIds.includes(roomId)) {
          nodeToMove.roomIds.push(roomId);
        }
      }
    } else {
      // Don't move nodes - just skip this crossing
      // The crossing will remain unresolved but no new nodes are created
      continue;
    }
  }

  return skeleton;
}

/** Configuration for the parallel edge merge pass */
const PARALLEL_EDGE_ANGLE_THRESHOLD = Math.PI / 36; // ~5 degrees

/**
 * Find pairs of nearly parallel edges originating from the same node.
 */
function findParallelEdgePairs(
  skeleton: FloorSkeleton,
  angleThreshold: number,
): Array<{
  nodeId: string;
  shorterEdgeId: string;
  longerEdgeId: string;
  shorterEndpointId: string;
  longerEndpointId: string;
  angle: number;
}> {
  const result: Array<{
    nodeId: string;
    shorterEdgeId: string;
    longerEdgeId: string;
    shorterEndpointId: string;
    longerEndpointId: string;
    angle: number;
  }> = [];

  for (const [nodeId, node] of skeleton.nodes) {
    if (node.edgeIds.length < 2) continue;

    const edgeData: Array<{
      edgeId: string;
      direction: THREE.Vector2;
      length: number;
      endpointId: string;
    }> = [];

    for (const edgeId of node.edgeIds) {
      const edge = skeleton.edges.get(edgeId);
      if (!edge) continue;

      const endpointId = edge.startNodeId === nodeId ? edge.endNodeId : edge.startNodeId;
      const endpoint = skeleton.nodes.get(endpointId);
      if (!endpoint) continue;

      const dx = endpoint.position.x - node.position.x;
      const dy = endpoint.position.y - node.position.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length < 0.001) continue;

      const direction = new THREE.Vector2(dx / length, dy / length);
      edgeData.push({ edgeId, direction, length, endpointId });
    }

    for (let i = 0; i < edgeData.length; i++) {
      for (let j = i + 1; j < edgeData.length; j++) {
        const edge1 = edgeData[i];
        const edge2 = edgeData[j];

        const dot = edge1.direction.x * edge2.direction.x + edge1.direction.y * edge2.direction.y;
        const clampedDot = Math.max(-1, Math.min(1, dot));
        const angle = Math.acos(clampedDot);

        if (angle < angleThreshold) {
          const [shorter, longer] = edge1.length <= edge2.length ? [edge1, edge2] : [edge2, edge1];

          result.push({
            nodeId,
            shorterEdgeId: shorter.edgeId,
            longerEdgeId: longer.edgeId,
            shorterEndpointId: shorter.endpointId,
            longerEndpointId: longer.endpointId,
            angle,
          });
        }
      }
    }
  }

  result.sort((a, b) => a.angle - b.angle);

  return result;
}

/**
 * Merge nearly parallel edges originating from the same node.
 */
export function mergeParallelEdgesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const angleThreshold = kwargs?.angleThreshold ?? PARALLEL_EDGE_ANGLE_THRESHOLD;
  const maxIterations = 100;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const parallelPairs = findParallelEdgePairs(skeleton, angleThreshold);

    if (parallelPairs.length === 0) break;

    const pair = parallelPairs[0];

    const originNode = skeleton.nodes.get(pair.nodeId);
    const shorterEdge = skeleton.edges.get(pair.shorterEdgeId);
    const longerEdge = skeleton.edges.get(pair.longerEdgeId);
    const shorterEndpoint = skeleton.nodes.get(pair.shorterEndpointId);
    const longerEndpoint = skeleton.nodes.get(pair.longerEndpointId);

    if (!originNode || !shorterEdge || !longerEdge || !shorterEndpoint || !longerEndpoint) continue;

    // Check if there's already an edge between shorterEndpoint and longerEndpoint
    const existingEdge = findEdgeBetweenNodes(skeleton, pair.shorterEndpointId, pair.longerEndpointId);
    if (existingEdge) {
      // Merge the endpoints since they represent the same junction
      mergeNodes(skeleton, shorterEndpoint, longerEndpoint);
    } else {
      // Reconnect the longer edge from originNode to shorterEndpoint
      if (longerEdge.startNodeId === pair.nodeId) {
        longerEdge.startNodeId = pair.shorterEndpointId;
      } else {
        longerEdge.endNodeId = pair.shorterEndpointId;
      }

      originNode.edgeIds = originNode.edgeIds.filter((id) => id !== pair.longerEdgeId);

      if (!shorterEndpoint.edgeIds.includes(pair.longerEdgeId)) {
        shorterEndpoint.edgeIds.push(pair.longerEdgeId);
      }

      for (const roomId of longerEdge.roomIds) {
        if (!shorterEndpoint.roomIds.includes(roomId)) {
          shorterEndpoint.roomIds.push(roomId);
        }
      }

      // Handle self-loop case
      if (longerEdge.startNodeId === longerEdge.endNodeId) {
        skeleton.edges.delete(pair.longerEdgeId);
        shorterEndpoint.edgeIds = shorterEndpoint.edgeIds.filter((id) => id !== pair.longerEdgeId);
      }
    }
  }

  return skeleton;
}

/** Result of a ray cast against room polygons */
interface RoomRayCastResult {
  hit: boolean;
  hitPoint?: THREE.Vector3;
  distance?: number;
  roomId?: string;
  /** Start of the hit wall segment (so the caller can compute incidence angle). */
  hitSegStart?: THREE.Vector3;
  /** End of the hit wall segment. */
  hitSegEnd?: THREE.Vector3;
  /** True when the hit landed only on the VIRTUAL extension of the wall (past its
   *  real endpoints), not the real segment — callers should de-prioritise these. */
  extended?: boolean;
}

/** Result of a ray cast against skeleton edges */
interface SkeletonRayCastResult {
  hit: boolean;
  hitPoint?: THREE.Vector3;
  hitEdgeId?: string;
  distance?: number;
}

/**
 * Cast a ray from a point in a direction and find the closest room polygon intersection.
 *
 * If `extendEdgesBy` is set, every polygon edge is virtually extended
 * along its tangent by that amount at each end (so a 1 m wall becomes
 * a `1 + 2 * extendEdgesBy` m wall for ray-cast purposes only). This
 * lets a probe that would otherwise slip past the end of a wall —
 * through a small corner gap — still register a hit on the
 * locally-correct wall rather than overshooting to a far one. Used by
 * the wall-thickness pass to keep glancing probes from latching onto
 * far-out edges.
 */
function castRayAgainstRoomPolygons(
  skeleton: FloorSkeleton,
  origin: THREE.Vector3,
  direction: THREE.Vector2,
  maxDistance: number,
  extendEdgesBy = 0,
  parallelGate?: { dir: THREE.Vector2; minCos: number },
): RoomRayCastResult {
  if (!skeleton.roomConnectivity) {
    return { hit: false };
  }

  let closestHit: RoomRayCastResult = { hit: false };
  let closestDistance = maxDistance;

  const rayEnd = new THREE.Vector3(origin.x + direction.x * maxDistance, origin.y + direction.y * maxDistance, origin.z);

  for (const room of skeleton.roomConnectivity) {
    const polygon = room.polygon;
    const vertexCount = polygon.length;

    for (let i = 0; i < vertexCount; i++) {
      const segStart = polygon[i];
      const segEnd = polygon[(i + 1) % vertexCount];

      // Parallel-gated casts only consider segments running alongside the
      // given direction — the wall-thickness probes use this to measure the
      // nearest PARALLEL face, looking straight past slanted corners/curved
      // boundaries that would otherwise occlude it with a fake distance.
      if (parallelGate) {
        const gdx = segEnd.x - segStart.x;
        const gdy = segEnd.y - segStart.y;
        const glen = Math.hypot(gdx, gdy);
        if (glen < 1e-9) continue;
        if (Math.abs((gdx / glen) * parallelGate.dir.x + (gdy / glen) * parallelGate.dir.y) < parallelGate.minCos) continue;
      }

      let testStart: THREE.Vector3 = segStart;
      let testEnd: THREE.Vector3 = segEnd;
      if (extendEdgesBy > 0) {
        const dx = segEnd.x - segStart.x;
        const dy = segEnd.y - segStart.y;
        const len = Math.hypot(dx, dy);
        if (len > 1e-9) {
          const ux = dx / len;
          const uy = dy / len;
          testStart = new THREE.Vector3(segStart.x - ux * extendEdgesBy, segStart.y - uy * extendEdgesBy, segStart.z);
          testEnd = new THREE.Vector3(segEnd.x + ux * extendEdgesBy, segEnd.y + uy * extendEdgesBy, segEnd.z);
        }
      }

      const intersection = segmentIntersection2D(origin, rayEnd, testStart, testEnd);

      if (intersection) {
        const distance = origin.distanceTo(intersection);
        if (distance < closestDistance && distance > 0.0001) {
          // Did the hit land on the REAL segment, or only on the virtual
          // extension past an endpoint? Project the hit onto the segment's
          // tangent and check it's within [0, len].
          let extended = false;
          if (extendEdgesBy > 0) {
            const dx = segEnd.x - segStart.x;
            const dy = segEnd.y - segStart.y;
            const len = Math.hypot(dx, dy);
            if (len > 1e-9) {
              const tproj = ((intersection.x - segStart.x) * dx + (intersection.y - segStart.y) * dy) / len;
              extended = tproj < -1e-6 || tproj > len + 1e-6;
            }
          }
          closestDistance = distance;
          closestHit = {
            hit: true,
            hitPoint: intersection,
            distance,
            roomId: room.identifier,
            // Report the ORIGINAL (un-extended) segment so the
            // perpendicularity gate uses the real wall's tangent.
            hitSegStart: segStart,
            hitSegEnd: segEnd,
            extended,
          };
        }
      }
    }
  }

  return closestHit;
}

/**
 * Cast for a facing wall, PREFERRING a hit on a real wall segment over one that
 * only lands on a virtual extension. We cast twice: first with NO extension
 * (real walls only) and, only if that misses, again WITH `extendEdgesBy` to
 * bridge a small corner gap. This stops a neighbouring wall's phantom extension
 * from winning over the edge's own real wall — the extension is a last resort,
 * not an equal candidate.
 */
function castFacingWall(skeleton: FloorSkeleton, origin: THREE.Vector3, direction: THREE.Vector2, maxDistance: number, extendEdgesBy: number, parallelGate?: { dir: THREE.Vector2; minCos: number }): RoomRayCastResult {
  const real = castRayAgainstRoomPolygons(skeleton, origin, direction, maxDistance, 0, parallelGate);
  if (real.hit) return real;
  if (extendEdgesBy > 0) return castRayAgainstRoomPolygons(skeleton, origin, direction, maxDistance, extendEdgesBy, parallelGate);
  return real;
}

/**
 * Ray-segment intersection for skeleton detection.
 * Returns intersection point if ray hits the segment, null otherwise.
 *
 * Uses the same formulation as segmentIntersection2D but treats the ray as
 * a segment from rayOrigin to rayOrigin + rayDir * maxDistance.
 */
function raySegmentIntersection2D(rayOrigin: THREE.Vector3, rayDir: THREE.Vector2, maxDistance: number, segStart: THREE.Vector3, segEnd: THREE.Vector3): THREE.Vector3 | null {
  // Treat ray as segment: a1 = rayOrigin, a2 = rayOrigin + rayDir * maxDistance
  // Target segment: b1 = segStart, b2 = segEnd
  // This uses the exact same math as the working segmentIntersection2D

  const a1x = rayOrigin.x;
  const a1y = rayOrigin.y;
  const a2x = rayOrigin.x + rayDir.x * maxDistance;
  const a2y = rayOrigin.y + rayDir.y * maxDistance;

  const d1x = a2x - a1x;
  const d1y = a2y - a1y;
  const d2x = segEnd.x - segStart.x;
  const d2y = segEnd.y - segStart.y;

  const cross = d1x * d2y - d1y * d2x;

  // Parallel lines
  if (Math.abs(cross) < 0.0001) return null;

  const dx = segStart.x - a1x;
  const dy = segStart.y - a1y;

  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // t in (0, 1] means the hit is along the ray within maxDistance (exclude origin, include max)
  // u in [0, 1] means the hit is on the segment (include endpoints)
  // Use very small epsilon (0.0001 = 0.01% of ray length) to detect close intersections
  // For maxDistance=5m, this means we can detect hits as close as 0.5mm from ray origin
  const epsilon = 0.0001;
  if (t > epsilon && t <= 1.0 && u >= 0 && u <= 1.0) {
    return new THREE.Vector3(a1x + t * d1x, a1y + t * d1y, rayOrigin.z);
  }

  return null;
}

/**
 * Cast a ray from a point in a direction and find the closest skeleton edge intersection.
 * Used to determine if we're hitting a connected edge (inner wall case).
 */
function castRayAgainstSkeletonEdges(skeleton: FloorSkeleton, origin: THREE.Vector3, direction: THREE.Vector2, maxDistance: number, excludeEdgeIds: string[]): SkeletonRayCastResult {
  let closestHit: SkeletonRayCastResult = { hit: false };
  let closestDistance = maxDistance;

  for (const [edgeId, edge] of skeleton.edges) {
    if (excludeEdgeIds.includes(edgeId)) continue;

    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (!startNode || !endNode) continue;

    const intersection = raySegmentIntersection2D(origin, direction, maxDistance, startNode.position, endNode.position);

    if (intersection) {
      const distance = origin.distanceTo(intersection);
      // Use small minimum distance to avoid self-intersection issues
      if (distance < closestDistance && distance > 0.0001) {
        closestDistance = distance;
        closestHit = {
          hit: true,
          hitPoint: intersection,
          hitEdgeId: edgeId,
          distance,
        };
      }
    }
  }

  return closestHit;
}

/**
 * Straighten edges by casting rays perpendicular to each edge and centering them.
 * Runs iteratively until displacements converge.
 *
 * Edge types:
 * - Normal: Both sides hit room polygon edges. Center between them.
 * - Inner wall: One or both sides hit another skeleton edge first. Leave untouched.
 * - Outer wall: One side hits nothing. Offset by half outer wall thickness from the hit side.
 */
export function straightenEdgesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxRayDistance = kwargs?.maxRayDistance ?? 5.0;
  const maxIterations = kwargs?.straightenMaxIterations ?? 50;
  const convergenceThreshold = 0.001;

  // Initialize debug rays array (will be populated on last iteration)
  skeleton.debugRays = [];

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    // Clear debug rays on each iteration, keep only the last one
    skeleton.debugRays = [];

    // Collect displacement vectors for each node
    const nodeDisplacements = new Map<string, THREE.Vector3[]>();

    for (const [edgeId, edge] of skeleton.edges) {
      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);
      if (!startNode || !endNode) continue;

      // Calculate edge direction and perpendicular
      const edgeDir = new THREE.Vector2(endNode.position.x - startNode.position.x, endNode.position.y - startNode.position.y).normalize();

      const perpLeft = new THREE.Vector2(-edgeDir.y, edgeDir.x);
      const perpRight = new THREE.Vector2(edgeDir.y, -edgeDir.x);

      // Sample points along the edge (excluding endpoints to avoid hitting connected edges)
      const sampleCount = 5;
      for (let i = 1; i < sampleCount; i++) {
        const t = i / sampleCount;
        const samplePoint = new THREE.Vector3(startNode.position.x + t * (endNode.position.x - startNode.position.x), startNode.position.y + t * (endNode.position.y - startNode.position.y), startNode.position.z);

        // Cast rays against room polygons (actual walls)
        const leftRoomHit = castRayAgainstRoomPolygons(skeleton, samplePoint, perpLeft, maxRayDistance);
        const rightRoomHit = castRayAgainstRoomPolygons(skeleton, samplePoint, perpRight, maxRayDistance);

        // Cast rays against skeleton edges to detect inner walls
        // Only exclude the current edge being processed
        const leftSkeletonHit = castRayAgainstSkeletonEdges(skeleton, samplePoint, perpLeft, maxRayDistance, [edgeId]);
        const rightSkeletonHit = castRayAgainstSkeletonEdges(skeleton, samplePoint, perpRight, maxRayDistance, [edgeId]);

        // Check if either side hits a skeleton edge at all (inner wall detection)
        const leftHitsSkeleton = leftSkeletonHit.hit;
        const rightHitsSkeleton = rightSkeletonHit.hit;

        // Add debug rays
        skeleton.debugRays.push({
          origin: samplePoint.clone(),
          direction: new THREE.Vector3(perpLeft.x, perpLeft.y, 0),
          distance: leftHitsSkeleton ? (leftSkeletonHit.distance ?? maxRayDistance) : leftRoomHit.hit ? (leftRoomHit.distance ?? maxRayDistance) : maxRayDistance,
          hit: leftHitsSkeleton || leftRoomHit.hit,
          label: leftHitsSkeleton ? 'straighten-left-inner' : 'straighten-left',
        });
        skeleton.debugRays.push({
          origin: samplePoint.clone(),
          direction: new THREE.Vector3(perpRight.x, perpRight.y, 0),
          distance: rightHitsSkeleton ? (rightSkeletonHit.distance ?? maxRayDistance) : rightRoomHit.hit ? (rightRoomHit.distance ?? maxRayDistance) : maxRayDistance,
          hit: rightHitsSkeleton || rightRoomHit.hit,
          label: rightHitsSkeleton ? 'straighten-right-inner' : 'straighten-right',
        });

        // Determine edge type and calculate displacement
        let displacement: THREE.Vector2 | null = null;

        // INNER WALL: If either side hits a skeleton edge at all, leave untouched
        if (leftHitsSkeleton || rightHitsSkeleton) {
          continue;
        }

        // Now we know neither side hits skeleton first, check room hits
        const leftHasRoomHit = leftRoomHit.hit;
        const rightHasRoomHit = rightRoomHit.hit;

        if (leftHasRoomHit && rightHasRoomHit) {
          // NORMAL: Both sides hit room polygons - find midpoint and move there
          const leftDist = leftRoomHit.distance ?? 0;
          const rightDist = rightRoomHit.distance ?? 0;

          // The midpoint between two hits is at distance (rightDist - leftDist) / 2 from current position
          // along the perpRight direction (positive = move right, negative = move left)
          // This simplifies to: if rightDist > leftDist, move right; if leftDist > rightDist, move left
          const moveAmount = (rightDist - leftDist) / 2;

          // perpRight points in the "right" direction
          displacement = new THREE.Vector2(perpRight.x * moveAmount, perpRight.y * moveAmount);
        } else {
          // No hits on either side - skip this sample
          continue;
        }

        if (displacement) {
          // Distribute displacement to nodes based on sample position
          const startWeight = 1 - t;
          const endWeight = t;

          if (startWeight > 0.01) {
            let startDisps = nodeDisplacements.get(edge.startNodeId);
            if (!startDisps) {
              startDisps = [];
              nodeDisplacements.set(edge.startNodeId, startDisps);
            }
            startDisps.push(new THREE.Vector3(displacement.x * startWeight, displacement.y * startWeight, 0));
          }

          if (endWeight > 0.01) {
            let endDisps = nodeDisplacements.get(edge.endNodeId);
            if (!endDisps) {
              endDisps = [];
              nodeDisplacements.set(edge.endNodeId, endDisps);
            }
            endDisps.push(new THREE.Vector3(displacement.x * endWeight, displacement.y * endWeight, 0));
          }
        }
      }
    }

    // Apply averaged displacements to nodes and track max displacement
    let maxDisplacement = 0;

    for (const [nodeId, displacements] of nodeDisplacements) {
      const node = skeleton.nodes.get(nodeId);
      if (!node || displacements.length === 0) continue;

      const avgDisplacement = new THREE.Vector3();
      for (const d of displacements) {
        avgDisplacement.add(d);
      }
      avgDisplacement.divideScalar(displacements.length);

      const displacementMagnitude = avgDisplacement.length();
      if (displacementMagnitude > maxDisplacement) {
        maxDisplacement = displacementMagnitude;
      }

      node.position.add(avgDisplacement);
    }

    // Check for convergence
    if (maxDisplacement < convergenceThreshold) {
      break;
    }
  }

  return skeleton;
}

/**
 * Merge nodes with distinct origins when no conflicting origins are nearby.
 */
export function mergeDistinctOriginsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const mergeTolerance = kwargs?.mergeTolerance ?? 0.15;
  const maxIterations = 100;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let merged = false;

    const nodeIds = Array.from(skeleton.nodes.keys());

    for (let i = 0; i < nodeIds.length && !merged; i++) {
      for (let j = i + 1; j < nodeIds.length && !merged; j++) {
        const nodeA = skeleton.nodes.get(nodeIds[i]);
        const nodeB = skeleton.nodes.get(nodeIds[j]);

        if (!nodeA || !nodeB) continue;

        const distance = nodeA.position.distanceTo(nodeB.position);
        if (distance > mergeTolerance) continue;

        const hasOverlap = nodeA.roomIds.some((roomId) => nodeB.roomIds.includes(roomId));
        if (hasOverlap) continue;

        const combinedRoomIds = new Set([...nodeA.roomIds, ...nodeB.roomIds]);

        let hasConflict = false;

        for (const [otherId, otherNode] of skeleton.nodes) {
          if (otherId === nodeA.id || otherId === nodeB.id) continue;

          const midpoint = new THREE.Vector3().addVectors(nodeA.position, nodeB.position).multiplyScalar(0.5);
          const otherDistance = otherNode.position.distanceTo(midpoint);

          if (otherDistance > mergeTolerance * 3) continue;

          const sharesRoomId = otherNode.roomIds.some((roomId) => combinedRoomIds.has(roomId));
          if (sharesRoomId) {
            hasConflict = true;
            break;
          }
        }

        if (hasConflict) continue;

        const midpoint = new THREE.Vector3().addVectors(nodeA.position, nodeB.position).multiplyScalar(0.5);
        nodeA.position.copy(midpoint);

        for (const roomId of nodeB.roomIds) {
          if (!nodeA.roomIds.includes(roomId)) {
            nodeA.roomIds.push(roomId);
          }
        }

        for (const edgeId of nodeB.edgeIds) {
          const edge = skeleton.edges.get(edgeId);
          if (!edge) continue;

          if (edge.startNodeId === nodeB.id) {
            edge.startNodeId = nodeA.id;
          }
          if (edge.endNodeId === nodeB.id) {
            edge.endNodeId = nodeA.id;
          }

          if (!nodeA.edgeIds.includes(edgeId)) {
            nodeA.edgeIds.push(edgeId);
          }

          if (edge.startNodeId === edge.endNodeId) {
            skeleton.edges.delete(edgeId);
            nodeA.edgeIds = nodeA.edgeIds.filter((id) => id !== edgeId);
          }
        }

        const edgeSignatures = new Map<string, string>();
        const duplicateEdges: string[] = [];

        for (const edgeId of nodeA.edgeIds) {
          const edge = skeleton.edges.get(edgeId);
          if (!edge) continue;

          const sig = [edge.startNodeId, edge.endNodeId].sort().join('-');
          if (edgeSignatures.has(sig)) {
            const existingEdgeId = edgeSignatures.get(sig);
            const existingEdge = existingEdgeId ? skeleton.edges.get(existingEdgeId) : undefined;
            if (existingEdge) {
              for (const roomId of edge.roomIds) {
                if (!existingEdge.roomIds.includes(roomId)) {
                  existingEdge.roomIds.push(roomId);
                }
              }
            }
            duplicateEdges.push(edgeId);
          } else {
            edgeSignatures.set(sig, edgeId);
          }
        }

        for (const edgeId of duplicateEdges) {
          const edge = skeleton.edges.get(edgeId);
          if (edge) {
            const startNode = skeleton.nodes.get(edge.startNodeId);
            const endNode = skeleton.nodes.get(edge.endNodeId);
            if (startNode) {
              startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
            }
            if (endNode) {
              endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
            }
            skeleton.edges.delete(edgeId);
          }
        }

        skeleton.nodes.delete(nodeB.id);

        merged = true;
      }
    }

    if (!merged) break;
  }

  return skeleton;
}

/**
 * Collapse small edges that are part of triangle structures.
 */
export function collapseSmallTrianglesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxEdgeLength = kwargs?.maxEdgeLength ?? 0.3;
  const maxIterations = 100;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    // Find small edges in triangles
    const smallEdges: Array<{ edgeId: string; nodeIds: [string, string] }> = [];

    for (const [edgeId, edge] of skeleton.edges) {
      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);

      if (!startNode || !endNode) continue;

      const distance = startNode.position.distanceTo(endNode.position);
      if (distance > maxEdgeLength) continue;

      const startNeighbors = new Set<string>();
      for (const eid of startNode.edgeIds) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        const neighbor = e.startNodeId === startNode.id ? e.endNodeId : e.startNodeId;
        if (neighbor !== endNode.id) {
          startNeighbors.add(neighbor);
        }
      }

      const endNeighbors = new Set<string>();
      for (const eid of endNode.edgeIds) {
        const e = skeleton.edges.get(eid);
        if (!e) continue;
        const neighbor = e.startNodeId === endNode.id ? e.endNodeId : e.startNodeId;
        if (neighbor !== startNode.id) {
          endNeighbors.add(neighbor);
        }
      }

      let hasCommonNeighbor = false;
      for (const neighbor of startNeighbors) {
        if (endNeighbors.has(neighbor)) {
          hasCommonNeighbor = true;
          break;
        }
      }

      if (hasCommonNeighbor) {
        smallEdges.push({ edgeId, nodeIds: [edge.startNodeId, edge.endNodeId] });
      }
    }

    if (smallEdges.length === 0) break;

    const { edgeId, nodeIds } = smallEdges[0];
    const [nodeIdA, nodeIdB] = nodeIds;

    const nodeA = skeleton.nodes.get(nodeIdA);
    const nodeB = skeleton.nodes.get(nodeIdB);
    const edgeToRemove = skeleton.edges.get(edgeId);

    if (!nodeA || !nodeB || !edgeToRemove) continue;

    const targetNode = nodeA.edgeIds.length >= nodeB.edgeIds.length ? nodeA : nodeB;
    const nodeToRemove = targetNode === nodeA ? nodeB : nodeA;

    const midpoint = new THREE.Vector3().addVectors(nodeA.position, nodeB.position).multiplyScalar(0.5);
    targetNode.position.copy(midpoint);

    for (const roomId of nodeToRemove.roomIds) {
      if (!targetNode.roomIds.includes(roomId)) {
        targetNode.roomIds.push(roomId);
      }
    }

    for (const eid of nodeToRemove.edgeIds) {
      if (eid === edgeId) continue;

      const edge = skeleton.edges.get(eid);
      if (!edge) continue;

      if (edge.startNodeId === nodeToRemove.id) {
        edge.startNodeId = targetNode.id;
      }
      if (edge.endNodeId === nodeToRemove.id) {
        edge.endNodeId = targetNode.id;
      }

      if (!targetNode.edgeIds.includes(eid)) {
        targetNode.edgeIds.push(eid);
      }

      if (edge.startNodeId === edge.endNodeId) {
        skeleton.edges.delete(eid);
        targetNode.edgeIds = targetNode.edgeIds.filter((id) => id !== eid);
      }
    }

    skeleton.edges.delete(edgeId);
    targetNode.edgeIds = targetNode.edgeIds.filter((id) => id !== edgeId);

    skeleton.nodes.delete(nodeToRemove.id);

    // Clean up duplicate edges
    const edgeSignatures = new Map<string, string>();
    const duplicateEdges: string[] = [];

    for (const eid of targetNode.edgeIds) {
      const edge = skeleton.edges.get(eid);
      if (!edge) continue;

      const sig = [edge.startNodeId, edge.endNodeId].sort().join('-');
      if (edgeSignatures.has(sig)) {
        const existingEdgeId = edgeSignatures.get(sig);
        const existingEdge = existingEdgeId ? skeleton.edges.get(existingEdgeId) : undefined;
        if (existingEdge) {
          for (const roomId of edge.roomIds) {
            if (!existingEdge.roomIds.includes(roomId)) {
              existingEdge.roomIds.push(roomId);
            }
          }
        }
        duplicateEdges.push(eid);
      } else {
        edgeSignatures.set(sig, eid);
      }
    }

    for (const eid of duplicateEdges) {
      const edge = skeleton.edges.get(eid);
      if (edge) {
        const startNode = skeleton.nodes.get(edge.startNodeId);
        const endNode = skeleton.nodes.get(edge.endNodeId);
        if (startNode) {
          startNode.edgeIds = startNode.edgeIds.filter((id) => id !== eid);
        }
        if (endNode) {
          endNode.edgeIds = endNode.edgeIds.filter((id) => id !== eid);
        }
        skeleton.edges.delete(eid);
      }
    }
  }

  return skeleton;
}

/**
 * Straighten collinear edges pass.
 *
 * For nodes where two edges are almost 180° apart (forming an almost-straight line),
 * this pass projects the node onto the line between the two neighbors to make
 * the edges perfectly collinear.
 */
export function straightenCollinearEdgesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const angleThreshold = kwargs?.straightenAngleThreshold ?? 15; // degrees deviation from 180°
  const thresholdRad = (angleThreshold * Math.PI) / 180;
  const minAngle = Math.PI - thresholdRad; // e.g., 165° if threshold is 15°

  const maxIterations = 100;
  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let straightenedAny = false;

    for (const node of skeleton.nodes.values()) {
      // Look for pairs of edges that are almost collinear (180°)
      for (let i = 0; i < node.edgeIds.length; i++) {
        for (let j = i + 1; j < node.edgeIds.length; j++) {
          const edge1 = skeleton.edges.get(node.edgeIds[i]);
          const edge2 = skeleton.edges.get(node.edgeIds[j]);
          if (!edge1 || !edge2) continue;

          const neighbor1Id = edge1.startNodeId === node.id ? edge1.endNodeId : edge1.startNodeId;
          const neighbor2Id = edge2.startNodeId === node.id ? edge2.endNodeId : edge2.startNodeId;

          const neighbor1 = skeleton.nodes.get(neighbor1Id);
          const neighbor2 = skeleton.nodes.get(neighbor2Id);
          if (!neighbor1 || !neighbor2) continue;

          // Vectors from node to neighbors
          const v1 = new THREE.Vector2(neighbor1.position.x - node.position.x, neighbor1.position.y - node.position.y);
          const v2 = new THREE.Vector2(neighbor2.position.x - node.position.x, neighbor2.position.y - node.position.y);

          const len1 = v1.length();
          const len2 = v2.length();
          if (len1 < 0.001 || len2 < 0.001) continue;

          const v1Norm = v1.clone().divideScalar(len1);
          const v2Norm = v2.clone().divideScalar(len2);

          const dot = v1Norm.dot(v2Norm);
          const clampedDot = Math.max(-1, Math.min(1, dot));
          const angle = Math.acos(clampedDot);

          // Check if angle is close to 180° (almost straight)
          if (angle < minAngle) continue;

          // Project node onto the line between neighbor1 and neighbor2
          const lineDir = new THREE.Vector2(neighbor2.position.x - neighbor1.position.x, neighbor2.position.y - neighbor1.position.y);
          const lineLength = lineDir.length();
          if (lineLength < 0.001) continue;
          lineDir.divideScalar(lineLength);

          const toNode = new THREE.Vector2(node.position.x - neighbor1.position.x, node.position.y - neighbor1.position.y);

          const projLength = toNode.dot(lineDir);
          // Clamp to stay between neighbors
          const margin = 0.01;
          const clampedProj = Math.max(margin * lineLength, Math.min((1 - margin) * lineLength, projLength));

          const newPosition = new THREE.Vector3(neighbor1.position.x + lineDir.x * clampedProj, neighbor1.position.y + lineDir.y * clampedProj, node.position.z);

          const displacement = node.position.distanceTo(newPosition);
          if (displacement > 0.0001) {
            node.position.copy(newPosition);
            straightenedAny = true;
            break;
          }
        }
        if (straightenedAny) break;
      }
    }

    if (!straightenedAny) break;
  }

  return skeleton;
}

/**
 * Straighten almost-90° angles by sliding the node along collinear edges.
 *
 * For each node, finds pairs of edges that are close to 90°. The node can only
 * slide along edges that are part of a "straight line" (i.e., have a collinear
 * partner edge on the other side). The slide distance is the minimum needed
 * across all 90° edge pairs to avoid large displacements.
 */
export function straightenAnglesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const angleThreshold = kwargs?.straightenAngleThreshold ?? 15; // degrees deviation from 90°
  const thresholdRad = (angleThreshold * Math.PI) / 180;
  const collinearThreshold = (15 * Math.PI) / 180; // edges within 15° of 180° are considered collinear

  const maxIterations = 100;
  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let straightenedAny = false;

    for (const node of skeleton.nodes.values()) {
      if (node.edgeIds.length < 2) continue;

      // Build edge data
      interface EdgeData {
        edgeId: string;
        neighborId: string;
        neighbor: SkeletonNode;
        dir: THREE.Vector2;
        length: number;
      }
      const edges: EdgeData[] = [];

      for (const edgeId of node.edgeIds) {
        const edge = skeleton.edges.get(edgeId);
        if (!edge) continue;

        const neighborId = edge.startNodeId === node.id ? edge.endNodeId : edge.startNodeId;
        const neighbor = skeleton.nodes.get(neighborId);
        if (!neighbor) continue;

        const dx = neighbor.position.x - node.position.x;
        const dy = neighbor.position.y - node.position.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length < 0.001) continue;

        edges.push({
          edgeId,
          neighborId,
          neighbor,
          dir: new THREE.Vector2(dx / length, dy / length),
          length,
        });
      }

      if (edges.length < 2) continue;

      // Find collinear edge pairs (edges that form a straight line through the node)
      // An edge is "slideable" if it has a collinear partner
      const slideableEdgeIndices = new Set<number>();

      for (let i = 0; i < edges.length; i++) {
        for (let j = i + 1; j < edges.length; j++) {
          const dot = edges[i].dir.dot(edges[j].dir);
          const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
          // Check if they're collinear (close to 180°)
          if (angle > Math.PI - collinearThreshold) {
            slideableEdgeIndices.add(i);
            slideableEdgeIndices.add(j);
          }
        }
      }

      // If no slideable edges, skip this node
      if (slideableEdgeIndices.size === 0) continue;

      // Find all edge pairs that are close to 90° where at least one edge is slideable
      // Collect the required slide amounts
      interface SlideCandidate {
        slideEdgeIdx: number;
        fixedEdgeIdx: number;
        slideAmount: number; // signed: positive = toward neighbor, negative = away
      }
      const slideCandidates: SlideCandidate[] = [];

      for (let i = 0; i < edges.length; i++) {
        for (let j = i + 1; j < edges.length; j++) {
          const dot = edges[i].dir.dot(edges[j].dir);
          const angle = Math.acos(Math.max(-1, Math.min(1, dot)));

          // Check if close to 90°
          const angleDiff = Math.abs(angle - Math.PI / 2);
          if (angleDiff > thresholdRad) continue;

          // Determine which edge to slide along (must be slideable)
          // Prefer the one that requires less movement
          const canSlideI = slideableEdgeIndices.has(i);
          const canSlideJ = slideableEdgeIndices.has(j);

          if (!canSlideI && !canSlideJ) continue;

          // Calculate slide amount for both options
          // To make edge i perpendicular to edge j:
          // New node position N' = N + t * dir_i
          // We need (neighbor_j - N') · dir_i = 0
          // (neighbor_j - N - t * dir_i) · dir_i = 0
          // (neighbor_j - N) · dir_i - t = 0
          // t = (neighbor_j - N) · dir_i

          const calcSlideAmount = (slideIdx: number, fixedIdx: number): number => {
            const fixedNeighborToNode = new THREE.Vector2(edges[fixedIdx].neighbor.position.x - node.position.x, edges[fixedIdx].neighbor.position.y - node.position.y);
            return fixedNeighborToNode.dot(edges[slideIdx].dir);
          };

          if (canSlideI) {
            const t = calcSlideAmount(i, j);
            slideCandidates.push({ slideEdgeIdx: i, fixedEdgeIdx: j, slideAmount: t });
          }
          if (canSlideJ) {
            const t = calcSlideAmount(j, i);
            slideCandidates.push({ slideEdgeIdx: j, fixedEdgeIdx: i, slideAmount: t });
          }
        }
      }

      if (slideCandidates.length === 0) continue;

      // Pick the candidate with the minimum absolute slide amount
      slideCandidates.sort((a, b) => Math.abs(a.slideAmount) - Math.abs(b.slideAmount));
      const best = slideCandidates[0];

      const slideDir = edges[best.slideEdgeIdx].dir;
      const t = best.slideAmount;

      const newPosition = new THREE.Vector3(node.position.x + slideDir.x * t, node.position.y + slideDir.y * t, node.position.z);

      const displacement = node.position.distanceTo(newPosition);
      if (displacement > 0.0001) {
        node.position.copy(newPosition);
        straightenedAny = true;
      }
    }

    if (!straightenedAny) break;
  }

  return skeleton;
}

/**
 * Split junction nodes (nodes with more than 2 edges that aren't perpendicular)
 * into multiple nodes arranged in a ring pattern.
 *
 * For each qualifying junction:
 * 1. Create N new nodes at the intersection of a circle (radius = slideDistance) with each edge
 * 2. Connect the new nodes in a circular ring (sorted by angle)
 * 3. Split each original edge: junction-side connects to ring node, far-side stays connected to original endpoint
 * 4. Remove the original junction node
 */
export function splitJunctionNodesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const circleRadius = kwargs?.junctionSlideDistance ?? 0.1;
  const perpendicularThreshold = kwargs?.perpendicularAngleThreshold ?? 70;

  // Find all junction nodes (more than 2 edges)
  const junctionNodes: SkeletonNode[] = [];
  for (const node of skeleton.nodes.values()) {
    if (node.edgeIds.length > 2) {
      junctionNodes.push(node);
    }
  }

  let nodeIdCounter = Date.now();
  let edgeIdCounter = Date.now() + 100000;

  for (const junctionNode of junctionNodes) {
    // Get all edges connected to this node
    const connectedEdges: SkeletonEdge[] = [];
    for (const edgeId of junctionNode.edgeIds) {
      const edge = skeleton.edges.get(edgeId);
      if (edge) {
        connectedEdges.push(edge);
      }
    }

    if (connectedEdges.length <= 2) continue;

    // Calculate direction vectors and other node for each edge
    const edgeData: {
      edge: SkeletonEdge;
      dir: THREE.Vector2;
      otherNodeId: string;
      otherNode: SkeletonNode;
      edgeLength: number;
    }[] = [];

    for (const edge of connectedEdges) {
      const otherNodeId = edge.startNodeId === junctionNode.id ? edge.endNodeId : edge.startNodeId;
      const otherNode = skeleton.nodes.get(otherNodeId);
      if (!otherNode) continue;

      const dx = otherNode.position.x - junctionNode.position.x;
      const dy = otherNode.position.y - junctionNode.position.y;
      const edgeLength = Math.sqrt(dx * dx + dy * dy);

      if (edgeLength < 0.001) continue; // Skip zero-length edges

      const dir = new THREE.Vector2(dx / edgeLength, dy / edgeLength);

      edgeData.push({ edge, dir, otherNodeId, otherNode, edgeLength });
    }

    if (edgeData.length <= 2) continue;

    // Sort edges by angle for proper ring ordering (needed for perpendicular check)
    edgeData.sort((a, b) => {
      const angleA = Math.atan2(a.dir.y, a.dir.x);
      const angleB = Math.atan2(b.dir.y, b.dir.x);
      return angleA - angleB;
    });

    // Check if this is a "perpendicular" junction that should be skipped
    // Two cases:
    // 1. T-junction (3 edges): Two edges ~180° apart (collinear) and one ~90° from both
    // 2. Cross junction (4 edges): All edges ~90° apart
    const toleranceRad = ((90 - perpendicularThreshold) * Math.PI) / 180;

    let shouldSkip = false;

    if (edgeData.length === 3) {
      // T-junction check: find if any two edges are nearly opposite (180° apart)
      // and the third is perpendicular to them
      for (let i = 0; i < 3 && !shouldSkip; i++) {
        for (let j = i + 1; j < 3; j++) {
          const dot = edgeData[i].dir.dot(edgeData[j].dir);
          // Check if edges i and j are nearly opposite (dot product close to -1)
          if (dot < -Math.cos(toleranceRad)) {
            // Found two opposite edges, check if third is perpendicular to both
            const k = 3 - i - j; // The remaining edge index
            const dotK1 = Math.abs(edgeData[k].dir.dot(edgeData[i].dir));
            const dotK2 = Math.abs(edgeData[k].dir.dot(edgeData[j].dir));
            // Third edge should be perpendicular (dot close to 0) to both
            if (dotK1 < Math.sin(toleranceRad) && dotK2 < Math.sin(toleranceRad)) {
              shouldSkip = true;
              break;
            }
          }
        }
      }
    } else if (edgeData.length === 4) {
      // Cross junction check: all adjacent pairs should be ~90° apart
      let allPerpendicular = true;
      for (let i = 0; i < edgeData.length; i++) {
        const nextIdx = (i + 1) % edgeData.length;
        const angle1 = Math.atan2(edgeData[i].dir.y, edgeData[i].dir.x);
        const angle2 = Math.atan2(edgeData[nextIdx].dir.y, edgeData[nextIdx].dir.x);

        let angleDiff = angle2 - angle1;
        if (angleDiff < 0) angleDiff += 2 * Math.PI;

        // Each sector should be ~90° for a perpendicular cross
        const expectedAngle = Math.PI / 2;
        const diffFromExpected = Math.abs(angleDiff - expectedAngle);

        if (diffFromExpected > toleranceRad) {
          allPerpendicular = false;
          break;
        }
      }
      shouldSkip = allPerpendicular;
    }
    // For 5+ edges, always split (no perpendicular pattern expected)

    if (shouldSkip) continue;

    // Create new nodes at the intersection of circle with each edge
    const ringNodes: SkeletonNode[] = [];

    for (const data of edgeData) {
      // Position is on the edge, at distance circleRadius from junction
      // (or at the other node if edge is shorter than radius)
      const dist = Math.min(circleRadius, data.edgeLength * 0.5);
      const newPosition = new THREE.Vector3(junctionNode.position.x + data.dir.x * dist, junctionNode.position.y + data.dir.y * dist, junctionNode.position.z);

      const newNodeId = `junction-ring-${nodeIdCounter++}`;
      const newNode: SkeletonNode = {
        id: newNodeId,
        position: newPosition,
        roomIds: [...junctionNode.roomIds],
        edgeIds: [],
        sourceVertices: [...junctionNode.sourceVertices],
        probedHits: [...junctionNode.probedHits],
        selected: false,
      };

      skeleton.nodes.set(newNodeId, newNode);
      ringNodes.push(newNode);

      // Update the original edge to connect from this new node to the other node
      // (instead of from junction to other node)
      if (data.edge.startNodeId === junctionNode.id) {
        data.edge.startNodeId = newNode.id;
      } else {
        data.edge.endNodeId = newNode.id;
      }

      // Add edge reference to new node
      newNode.edgeIds.push(data.edge.id);

      // Update the other node's edge reference (it still points to same edge, no change needed)
    }

    // Create ring edges connecting adjacent ring nodes in a circle
    // Mark them as junction edges so the thickness pass can handle them specially
    for (let i = 0; i < ringNodes.length; i++) {
      const currentNode = ringNodes[i];
      const nextNode = ringNodes[(i + 1) % ringNodes.length];

      const ringEdgeId = `ring-edge-${edgeIdCounter++}`;
      const ringEdge: SkeletonEdge = {
        id: ringEdgeId,
        startNodeId: currentNode.id,
        endNodeId: nextNode.id,
        roomIds: [...junctionNode.roomIds],
        leftSize: 0.1,
        rightSize: 0.1,
        wallType: 'INNER',
        selected: false,
        isJunctionEdge: true,
      };

      skeleton.edges.set(ringEdgeId, ringEdge);
      currentNode.edgeIds.push(ringEdgeId);
      nextNode.edgeIds.push(ringEdgeId);
    }

    // Remove the original junction node (edges already updated to not reference it)
    skeleton.nodes.delete(junctionNode.id);
  }

  return skeleton;
}

/**
 * Calculate the intersection point of two infinite lines defined by points and directions.
 * Returns null if lines are parallel or nearly parallel.
 */
function lineLineIntersection2D(p1: THREE.Vector3, dir1: THREE.Vector2, p2: THREE.Vector3, dir2: THREE.Vector2): THREE.Vector3 | null {
  const cross = dir1.x * dir2.y - dir1.y * dir2.x;

  // Lines are parallel
  if (Math.abs(cross) < 0.0001) return null;

  // Using parametric form: P1 + t * dir1 = P2 + s * dir2
  // Solve for t: t = ((P2 - P1) × dir2) / (dir1 × dir2)
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  const t = (dx * dir2.y - dy * dir2.x) / cross;

  return new THREE.Vector3(p1.x + t * dir1.x, p1.y + t * dir1.y, p1.z);
}

/**
 * Find all triangles in the skeleton.
 * A triangle is 3 nodes where each pair is connected by an edge.
 * Returns arrays of [nodeA, nodeB, nodeC] for each triangle found.
 */
function findTriangles(skeleton: FloorSkeleton): Array<[SkeletonNode, SkeletonNode, SkeletonNode]> {
  const triangles: Array<[SkeletonNode, SkeletonNode, SkeletonNode]> = [];
  const foundTriangleIds = new Set<string>();

  for (const nodeA of skeleton.nodes.values()) {
    // Get neighbors of nodeA
    const neighborsA: SkeletonNode[] = [];
    for (const edgeId of nodeA.edgeIds) {
      const edge = skeleton.edges.get(edgeId);
      if (!edge) continue;
      const neighborId = edge.startNodeId === nodeA.id ? edge.endNodeId : edge.startNodeId;
      const neighbor = skeleton.nodes.get(neighborId);
      if (neighbor) neighborsA.push(neighbor);
    }

    // Check each pair of neighbors for a connecting edge
    for (let i = 0; i < neighborsA.length; i++) {
      const nodeB = neighborsA[i];
      for (let j = i + 1; j < neighborsA.length; j++) {
        const nodeC = neighborsA[j];

        // Check if nodeB and nodeC are connected
        const edgeBetweenBC = findEdgeBetweenNodes(skeleton, nodeB.id, nodeC.id);
        if (edgeBetweenBC) {
          // Found a triangle! Create a unique ID to avoid duplicates
          const triangleId = [nodeA.id, nodeB.id, nodeC.id].sort().join('-');
          if (!foundTriangleIds.has(triangleId)) {
            foundTriangleIds.add(triangleId);
            triangles.push([nodeA, nodeB, nodeC]);
          }
        }
      }
    }
  }

  return triangles;
}

/**
 * For a node in a triangle, find the external edges (edges that are not part of the triangle).
 * Returns the direction vectors extending away from the node along these external edges,
 * plus the edge itself for accessing thickness values.
 */
function getExternalEdgeDirections(skeleton: FloorSkeleton, node: SkeletonNode, triangleNodeIds: Set<string>): Array<{ direction: THREE.Vector2; neighbor: SkeletonNode; edge: SkeletonEdge; isStartNode: boolean }> {
  const externalDirections: Array<{
    direction: THREE.Vector2;
    neighbor: SkeletonNode;
    edge: SkeletonEdge;
    isStartNode: boolean;
  }> = [];

  for (const edgeId of node.edgeIds) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;

    const isStartNode = edge.startNodeId === node.id;
    const neighborId = isStartNode ? edge.endNodeId : edge.startNodeId;

    // Skip if neighbor is part of the triangle
    if (triangleNodeIds.has(neighborId)) continue;

    const neighbor = skeleton.nodes.get(neighborId);
    if (!neighbor) continue;

    const dx = neighbor.position.x - node.position.x;
    const dy = neighbor.position.y - node.position.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length < 0.001) continue;

    // Direction pointing from node toward the external neighbor
    const direction = new THREE.Vector2(dx / length, dy / length);
    externalDirections.push({ direction, neighbor, edge, isStartNode });
  }

  return externalDirections;
}

/**
 * Solve triangle node positions by moving each triangle node to the intersection
 * of the wall lines that should meet at that node.
 *
 * For each triangle:
 * 1. Calculate all possible intersection points from pairs of external edges
 * 2. Match each intersection to the closest unmoved triangle node
 * 3. Each intersection can only be claimed by one node
 *
 * A node is only moved once to prevent cascading movements.
 */
export function solveTriangleNodesPass(skeleton: FloorSkeleton, _kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const triangles = findTriangles(skeleton);

  if (triangles.length === 0) {
    return skeleton;
  }

  // Track which nodes have been moved (globally across all triangles)
  const movedNodeIds = new Set<string>();

  for (const [nodeA, nodeB, nodeC] of triangles) {
    const triangleNodeIds = new Set([nodeA.id, nodeB.id, nodeC.id]);
    const triangleNodes = [nodeA, nodeB, nodeC];

    // Calculate all intersection points for this triangle
    // Each intersection comes from extending the external edges of two triangle nodes
    const intersections: Array<{ point: THREE.Vector3; fromNodes: [SkeletonNode, SkeletonNode] }> = [];

    for (let i = 0; i < 3; i++) {
      const node1 = triangleNodes[i];
      const node2 = triangleNodes[(i + 1) % 3];

      // Get external edges from each node
      const externals1 = getExternalEdgeDirections(skeleton, node1, triangleNodeIds);
      const externals2 = getExternalEdgeDirections(skeleton, node2, triangleNodeIds);

      if (externals1.length === 0 || externals2.length === 0) continue;

      const ext1 = externals1[0];
      const ext2 = externals2[0];

      // Line directions (reversed - pointing into the triangle area)
      const line1Dir = new THREE.Vector2(-ext1.direction.x, -ext1.direction.y);
      const line2Dir = new THREE.Vector2(-ext2.direction.x, -ext2.direction.y);

      const intersection = lineLineIntersection2D(node1.position, line1Dir, node2.position, line2Dir);

      if (intersection) {
        intersections.push({ point: intersection, fromNodes: [node1, node2] });
      }
    }

    // Now match intersections to triangle nodes
    // Each intersection should go to the closest unmoved node
    // Each node can only be moved once, each intersection can only be used once
    const usedIntersections = new Set<number>();

    // Sort by distance to find best matches first
    const assignments: Array<{ nodeId: string; point: THREE.Vector3; distance: number; intersectionIdx: number }> = [];

    for (const node of triangleNodes) {
      if (movedNodeIds.has(node.id)) continue;

      for (let idx = 0; idx < intersections.length; idx++) {
        const dist = node.position.distanceTo(intersections[idx].point);
        if (dist < 1.0) {
          // Sanity check
          assignments.push({ nodeId: node.id, point: intersections[idx].point, distance: dist, intersectionIdx: idx });
        }
      }
    }

    // Sort by distance (closest first)
    assignments.sort((a, b) => a.distance - b.distance);

    // Greedily assign: closest pairs first
    for (const assignment of assignments) {
      if (movedNodeIds.has(assignment.nodeId)) continue;
      if (usedIntersections.has(assignment.intersectionIdx)) continue;

      const node = skeleton.nodes.get(assignment.nodeId);
      if (node) {
        node.position.copy(assignment.point);
        movedNodeIds.add(assignment.nodeId);
        usedIntersections.add(assignment.intersectionIdx);
      }
    }
  }

  return skeleton;
}

/**
 * Measure wall thickness for a single edge — same algorithm as
 * {@link measureWallThicknessPass}, just scoped to one edge so the editor
 * UI can recompute thickness on demand without re-running the full pass.
 *
 * Returns `null` for the edge id when the edge is missing, is a junction
 * edge (which the pass handles in its second loop and isn't meaningful in
 * isolation), or its endpoints are missing. Otherwise returns
 * `{ leftSize, rightSize }` from the dominant target wall on each side (the one
 * the most probes hit); falls back to `defaultWallThickness` (0.15m) when no
 * probes hit on a side.
 *
 * Does NOT write to `skeleton.debugRays` — caller is in an interactive
 * context, not a pipeline run that wants debug visualisation.
 */
/**
 * Returns true when the hit is within `maxAngleFromPerpDeg` of a true
 * perpendicular hit on the wall. A perpendicular hit has the ray
 * direction normal to the wall tangent, i.e. `|ray · tangent| ≈ 0`.
 * Used by the wall-thickness pass to discard glancing hits — those
 * happen at junction corners where a perpendicular probe just barely
 * catches a wall's edge and reports a much shorter distance than the
 * true thickness of the wall it should be measuring.
 */
function isHitPerpendicular(rayDir: THREE.Vector2, segStart: THREE.Vector3 | undefined, segEnd: THREE.Vector3 | undefined, maxAngleFromPerpDeg: number): boolean {
  if (!segStart || !segEnd) return true; // unknown geometry → don't reject
  const wx = segEnd.x - segStart.x;
  const wy = segEnd.y - segStart.y;
  const wLen = Math.hypot(wx, wy);
  if (wLen < 1e-9) return true;
  const wxn = wx / wLen;
  const wyn = wy / wLen;
  // Cosine of the angle between ray and wall tangent. Perpendicular
  // hit → |cos| ≈ 0; grazing hit → |cos| ≈ 1.
  const absCos = Math.abs(rayDir.x * wxn + rayDir.y * wyn);
  // Convert the allowed deviation from 90° into a cap on |cos|.
  // Hit is acceptable if the deviation ≤ maxAngleFromPerpDeg, i.e.
  // |cos| ≤ sin(maxAngleFromPerpDeg).
  const maxAbsCos = Math.sin((maxAngleFromPerpDeg * Math.PI) / 180);
  return absCos <= maxAbsCos;
}

/**
 * Returns true when the hit wall runs roughly PARALLEL to the skeleton edge —
 * i.e. it's a real facing wall, not some perpendicular wall the probe happened to
 * cross at a junction. `edgeDir` is the edge's unit tangent; the wall tangent
 * comes from the hit segment. Parallel ⇒ |edgeDir · wallTangent| ≈ 1.
 */
function isHitParallelToEdge(edgeDir: THREE.Vector2, segStart: THREE.Vector3 | undefined, segEnd: THREE.Vector3 | undefined, maxAngleDeg: number): boolean {
  return parallelCosToEdge(edgeDir, segStart, segEnd) >= Math.cos((maxAngleDeg * Math.PI) / 180);
}

/** |cos| between the edge tangent and the hit wall's tangent (1 = parallel,
 *  0 = perpendicular or unknown geometry). */
function parallelCosToEdge(edgeDir: THREE.Vector2, segStart: THREE.Vector3 | undefined, segEnd: THREE.Vector3 | undefined): number {
  if (!segStart || !segEnd) return 0; // unknown geometry → not a trusted facing wall
  const wx = segEnd.x - segStart.x;
  const wy = segEnd.y - segStart.y;
  const wLen = Math.hypot(wx, wy);
  if (wLen < 1e-9) return 0;
  return Math.abs(edgeDir.x * (wx / wLen) + edgeDir.y * (wy / wLen));
}

/** A per-probe thickness hit: distance + how parallel the hit surface runs to
 *  the measured edge. */
interface ThicknessHit {
  d: number;
  parallelCos: number;
}

/** "Strictly parallel" threshold for thickness measurement: a surface within
 *  10° of the edge counts as a true facing wall. */
const THICKNESS_STRICT_PARALLEL_COS = Math.cos((10 * Math.PI) / 180);

/**
 * Dominant thickness with PARALLEL-FIRST preference. The loose 30° parallel
 * gate lets angled surfaces through (a cavity's slanted corner, a curved
 * boundary) — and since nearer hits carry more weight in the banded vote, such
 * a surface can out-vote the true facing wall and report a FAKE thickness
 * (which then, e.g., blocks collinear-node merging over a thickness mismatch).
 * So: when any probes hit a surface within 10° of parallel, only those decide
 * the thickness; the loose set is a fallback for sides with no parallel wall.
 */
function dominantParallelDistance(hits: ThicknessHit[], band = 0.03): number | null {
  if (hits.length === 0) return null;
  const strict = hits.filter((h) => h.parallelCos >= THICKNESS_STRICT_PARALLEL_COS);
  return dominantBandedDistance((strict.length > 0 ? strict : hits).map((h) => h.d), band);
}

/**
 * SMART FACE PICK for wall thickness — each side of the edge is measured
 * against the face it MATCHES: a room/outline polygon segment roughly
 * parallel to the edge, genuinely overlapping its span (≥25% of the shorter),
 * lying on that side within `maxDist`. The NEAREST such face per side wins —
 * the same matching philosophy as the prune/centering passes, instead of
 * trusting whatever geometry a probe ray happens to catch through a corner
 * gap at a junction. A parallel skeleton edge nearer than the matched face
 * marks a shared INNER wall for that side. `null` per side = no match
 * (caller falls back to ray probing).
 */
function matchedFaceThickness(skeleton: FloorSkeleton, edgeId: string, maxDist: number, extendBy = 0): { left: number | null; right: number | null; leftInner: boolean; rightInner: boolean } {
  const res = { left: null as number | null, right: null as number | null, leftInner: false, rightInner: false };
  const edge = skeleton.edges.get(edgeId);
  if (!edge) return res;
  const sn = skeleton.nodes.get(edge.startNodeId);
  const en = skeleton.nodes.get(edge.endNodeId);
  if (!sn || !en) return res;
  const ex = en.position.x - sn.position.x;
  const ey = en.position.y - sn.position.y;
  const elen = Math.hypot(ex, ey);
  if (elen < 1e-9) return res;
  const ux = ex / elen;
  const uy = ey / elen;
  const nx = -uy; // left normal
  const ny = ux;

  // SEMANTIC EXTENSION — an end may only be virtually extended when the
  // extension genuinely stays the SAME semantic line:
  //  - a COLLINEAR CONTINUATION at the end (the wall goes on) or a bare
  //    DEAD END → extension allowed;
  //  - ANY turning neighbor — a corner's single turn or a T-section's
  //    crossing wall — ends the wall's semantics: NO extension past it,
  //    that's another wall's territory.
  // On top, an allowed extension still stops at the first skeleton edge
  // crossing its path further out (never through a wall's interior).
  let extStart = extendBy;
  let extEnd = extendBy;
  if (extendBy > 0) {
    const semanticExt = (originNode: SkeletonNode, dx: number, dy: number): number => {
      const others: Array<{ ux: number; uy: number }> = [];
      let hasContinuation = false;
      for (const eid of originNode.edgeIds) {
        if (eid === edgeId) continue;
        const o = skeleton.edges.get(eid);
        if (!o) continue;
        const oa = skeleton.nodes.get(o.startNodeId);
        const ob = skeleton.nodes.get(o.endNodeId);
        if (!oa || !ob) continue;
        const ox = ob.position.x - oa.position.x;
        const oy = ob.position.y - oa.position.y;
        const ol = Math.hypot(ox, oy);
        if (ol < 1e-9) continue;
        const oux = ox / ol;
        const ouy = oy / ol;
        if (Math.abs(oux * ux + ouy * uy) >= 0.966) hasContinuation = true; // ~15°
        else others.push({ ux: oux, uy: ouy });
      }
      if (!hasContinuation && others.length > 0) return 0; // corner or T — the semantic line ends here
      const hit = castRayAgainstSkeletonEdges(skeleton, originNode.position, new THREE.Vector2(dx, dy), extendBy, [edgeId, ...originNode.edgeIds]);
      return hit.hit && hit.distance !== undefined ? Math.max(0, hit.distance - 0.01) : extendBy;
    };
    extStart = semanticExt(sn, -ux, -uy);
    extEnd = semanticExt(en, ux, uy);
  }

  // Two tiers per side, STRICTLY-PARALLEL first: a slanted sliver crossing in
  // front of the true facing wall passes a loose 30° gate and is nearer — the
  // strict tier looks straight past it, and the loose tier only decides when
  // no strict face exists on that side (mirrors the probing fallback order).
  const tiers = {
    faceStrict: [Infinity, Infinity],
    faceLoose: [Infinity, Infinity],
    innerStrict: [Infinity, Infinity],
    innerLoose: [Infinity, Infinity],
  };

  /** Lateral signed offset + parallel cos of an overlapping segment; null = no match. */
  const lateralOf = (ax: number, ay: number, bx: number, by: number): { off: number; cos: number } | null => {
    const sx = bx - ax;
    const sy = by - ay;
    const slen = Math.hypot(sx, sy);
    if (slen < 1e-9) return null;
    const sux = sx / slen;
    const suy = sy / slen;
    const cos = Math.abs(ux * sux + uy * suy);
    if (cos < 0.866) return null; // not parallel-ish (~30°)
    // The edge's span is VIRTUALLY EXTENDED by `extendBy` at both ends: a
    // short zigzag piece creates a corner whose facing face sits just BEYOND
    // its own span — without the extension it never overlaps its face and
    // falls back to junk. (Mirror of the raycaster's face extension.)
    const t1 = (ax - sn.position.x) * ux + (ay - sn.position.y) * uy;
    const t2 = (bx - sn.position.x) * ux + (by - sn.position.y) * uy;
    const lo = Math.max(Math.min(t1, t2), -extStart);
    const hi = Math.min(Math.max(t1, t2), elen + extEnd);
    if (hi - lo < 0.25 * Math.min(elen, slen)) return null; // no real overlap
    // Signed lateral distance along n̂ — measured at BOTH overlap ends, the
    // NEARER one wins. A slanted face forms a wedge with the edge; the wall's
    // thickness is where the face comes closest (grow-until-hit semantics),
    // not the fat middle of the wedge. A face whose sides disagree crosses
    // the edge line — no valid measurement.
    const denom = nx * suy - ny * sux;
    if (Math.abs(denom) < 1e-6) return null; // degenerate frame
    const offAt = (tPos: number): number => {
      const pcx = sn.position.x + ux * tPos;
      const pcy = sn.position.y + uy * tPos;
      return ((ax - pcx) * suy - (ay - pcy) * sux) / denom;
    };
    // Measure within the edge's ACTUAL span whenever a real overlap exists —
    // extrapolated offsets at the virtually-extended ends drift on slightly
    // angled faces and broke ordinary walls. Only an extension-only match (a
    // short zigzag whose face lies wholly beyond its span) measures at the
    // extended positions, because there is nowhere else to measure. The
    // measurement points are inset from the ends so junction neighbors
    // touching the endpoints can't falsely occlude.
    const loM = Math.max(lo, 0);
    const hiM = Math.min(hi, elen);
    const inset = Math.min(0.05, elen / 4);
    const [m1, m2] = loM < hiM ? [Math.min(Math.max(loM, inset), elen - inset), Math.max(Math.min(hiM, elen - inset), inset)] : [lo, hi];
    const o1 = offAt(m1);
    const o2 = offAt(m2);
    if (Math.sign(o1) !== Math.sign(o2)) return null; // face crosses the edge line
    // LATERAL LINE OF SIGHT: the measurement segment (edge → face) must not
    // cross another skeleton edge — thickness is never measured THROUGH a
    // wall, so an occluded face is simply not a candidate. Try the nearer
    // measurement end first, the other as backup.
    const visible = (tPos: number, off: number): boolean => {
      if (Math.abs(off) <= 0.02) return true;
      const px2 = sn.position.x + ux * tPos;
      const py2 = sn.position.y + uy * tPos;
      const sign = off > 0 ? 1 : -1;
      const hit = castRayAgainstSkeletonEdges(skeleton, new THREE.Vector3(px2, py2, sn.position.z), new THREE.Vector2(nx * sign, ny * sign), Math.abs(off) - 0.01, [edgeId]);
      return !hit.hit;
    };
    const ordered: Array<[number, number]> = Math.abs(o1) < Math.abs(o2) ? [[m1, o1], [m2, o2]] : [[m2, o2], [m1, o1]];
    for (const [mPos, off] of ordered) {
      if (visible(mPos, off)) return { off, cos };
    }
    return null; // measurement blocked by another wall — not a candidate
  };
  const tally = (m: { off: number; cos: number } | null, strictKey: 'faceStrict' | 'innerStrict', looseKey: 'faceLoose' | 'innerLoose'): void => {
    if (m === null || Math.abs(m.off) < 0.005 || Math.abs(m.off) > maxDist) return;
    const side = m.off > 0 ? 0 : 1;
    const d = Math.abs(m.off);
    const key = m.cos >= THICKNESS_STRICT_PARALLEL_COS ? strictKey : looseKey;
    tiers[key][side] = Math.min(tiers[key][side], d);
  };

  for (const room of skeleton.roomConnectivity ?? []) {
    const poly = room.polygon;
    for (let k = 0; k < poly.length; k++) {
      const a = poly[k];
      const b = poly[(k + 1) % poly.length];
      tally(lateralOf(a.x, a.y, b.x, b.y), 'faceStrict', 'faceLoose');
    }
  }
  for (const [oid, o] of skeleton.edges) {
    if (oid === edgeId || o.isJunctionEdge) continue;
    const oa = skeleton.nodes.get(o.startNodeId);
    const ob = skeleton.nodes.get(o.endNodeId);
    if (!oa || !ob) continue;
    tally(lateralOf(oa.position.x, oa.position.y, ob.position.x, ob.position.y), 'innerStrict', 'innerLoose');
  }

  for (const side of [0, 1] as const) {
    const face = tiers.faceStrict[side] < Infinity ? tiers.faceStrict[side] : tiers.faceLoose[side];
    const inner = tiers.innerStrict[side] < Infinity ? tiers.innerStrict[side] : tiers.innerLoose[side];
    if (inner < face) {
      if (side === 0) res.leftInner = true;
      else res.rightInner = true;
    } else if (face < Infinity) {
      if (side === 0) res.left = face;
      else res.right = face;
    }
  }
  return res;
}

/**
 * SECTOR-BASED CORNER MATCHING — the primary thickness source. A node with k
 * incident edges has exactly k angular SECTORS between consecutive edge
 * directions, and each sector claims at most ONE polygon corner: the vertex
 * inside the sector scoring best on distance-to-bisector plus a corner-SHAPE
 * mismatch weight, within `radius`, with LINE OF SIGHT (the node→corner
 * segment never crosses another wall). A node
 * therefore has as many corner matches as it has edges — except a dead end
 * (one edge), whose two half-plane sectors give it two. Discrete and exact
 * where face matching slides around.
 */
interface NodeSectorMatch {
  /** The two incident edges bounding this sector (equal on a dead end). */
  edgeA: string;
  edgeB: string;
  corner: { cx: number; cy: number } | null;
}

/**
 * Score penalty, in metres, for a candidate corner whose SHAPE fully
 * mismatches its sector (90° off); scales linearly down to 0 for a perfect
 * shape match. Added to the distance-from-bisector score. Collinear ~180°
 * pass-through sectors are exempt — no corner shape exists there.
 */
const CORNER_SHAPE_WEIGHT = 10.0;

function matchNodeCornersBySector(skeleton: FloorSkeleton, nodeId: string, radius: number): NodeSectorMatch[] {
  const node = skeleton.nodes.get(nodeId);
  if (!node) return [];
  const inc: Array<{ id: string; theta: number }> = [];
  for (const eid of node.edgeIds) {
    const e = skeleton.edges.get(eid);
    if (!e || e.isJunctionEdge) continue;
    const otherId = e.startNodeId === nodeId ? e.endNodeId : e.startNodeId;
    const other = skeleton.nodes.get(otherId);
    if (!other || otherId === nodeId) continue;
    const dx = other.position.x - node.position.x;
    const dy = other.position.y - node.position.y;
    if (Math.hypot(dx, dy) < 1e-9) continue;
    inc.push({ id: eid, theta: Math.atan2(dy, dx) });
  }
  if (inc.length === 0) return [];
  inc.sort((a, b) => a.theta - b.theta);
  const TWO_PI = Math.PI * 2;
  const sectors: Array<{ edgeA: string; edgeB: string; from: number; span: number; corner: { cx: number; cy: number } | null; score: number; dist: number }> = [];
  if (inc.length === 1) {
    // Dead end — the sole exception: two half-plane sectors, one per side.
    sectors.push({ edgeA: inc[0].id, edgeB: inc[0].id, from: inc[0].theta, span: Math.PI, corner: null, score: Infinity, dist: Infinity });
    sectors.push({ edgeA: inc[0].id, edgeB: inc[0].id, from: inc[0].theta + Math.PI, span: Math.PI, corner: null, score: Infinity, dist: Infinity });
  } else {
    for (let i = 0; i < inc.length; i++) {
      const a = inc[i];
      const b = inc[(i + 1) % inc.length];
      const span = (((b.theta - a.theta) % TWO_PI) + TWO_PI) % TWO_PI || TWO_PI;
      sectors.push({ edgeA: a.id, edgeB: b.id, from: a.theta, span, corner: null, score: Infinity, dist: Infinity });
    }
  }
  const excluded = [...node.edgeIds];
  const deadEnd = inc.length === 1;
  const HALF_PI = Math.PI / 2;
  /** Angle between two LINE directions (sign-agnostic), radians. */
  const dev = (ux: number, uy: number, vx: number, vy: number): number => Math.acos(Math.min(1, Math.abs(ux * vx + uy * vy)));
  for (const room of skeleton.roomConnectivity ?? []) {
    const poly = room.polygon;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
      const c = poly[i];
      const dx = c.x - node.position.x;
      const dy = c.y - node.position.y;
      const dist = Math.hypot(dx, dy);
      if (dist > radius || dist < 1e-6) continue;
      const theta = Math.atan2(dy, dx);
      const sector = sectors.find((s2) => (((theta - s2.from) % TWO_PI) + TWO_PI) % TWO_PI < s2.span);
      if (!sector) continue;
      // The sector's corner is the vertex CLOSEST TO ITS BISECTOR — the
      // middle line of the angle — not the euclidean-nearest, which lets a
      // vertex hugging one bounding edge steal the claim. Plain distance
      // only breaks ties.
      const bisector = sector.from + sector.span / 2;
      const bis = Math.abs(Math.cos(bisector) * dy - Math.sin(bisector) * dx);
      // SHAPE WEIGHT: the polygon corner is the offset image of the wall
      // corner it belongs to, so its two ring edges should run parallel to
      // the sector's bounding wall directions (any one of them on a dead
      // end). Mismatch is weighted into the score — a similar-shaped corner
      // beats a closer-to-bisector one of the wrong shape. A COLLINEAR
      // pass-through sector (~180° between two straight-continuing edges)
      // has no corner shape to match — the wall runs straight there — so
      // the rule doesn't apply and only the bisector ranks.
      const flat = !deadEnd && Math.abs(sector.span - Math.PI) < 0.26;
      let shapeErr = flat ? 0 : HALF_PI;
      let pi2 = (i - 1 + n) % n;
      while (pi2 !== i && Math.hypot(poly[pi2].x - c.x, poly[pi2].y - c.y) < 1e-6) pi2 = (pi2 - 1 + n) % n;
      let ni2 = (i + 1) % n;
      while (ni2 !== i && Math.hypot(poly[ni2].x - c.x, poly[ni2].y - c.y) < 1e-6) ni2 = (ni2 + 1) % n;
      if (!flat && pi2 !== i && ni2 !== i) {
        const l1 = Math.hypot(poly[pi2].x - c.x, poly[pi2].y - c.y);
        const d1x = (poly[pi2].x - c.x) / l1;
        const d1y = (poly[pi2].y - c.y) / l1;
        const l2 = Math.hypot(poly[ni2].x - c.x, poly[ni2].y - c.y);
        const d2x = (poly[ni2].x - c.x) / l2;
        const d2y = (poly[ni2].y - c.y) / l2;
        const ax = Math.cos(sector.from);
        const ay = Math.sin(sector.from);
        const bx = Math.cos(sector.from + sector.span);
        const by = Math.sin(sector.from + sector.span);
        shapeErr = deadEnd
          ? Math.min(dev(ax, ay, d1x, d1y), dev(ax, ay, d2x, d2y))
          : Math.min(Math.max(dev(ax, ay, d1x, d1y), dev(bx, by, d2x, d2y)), Math.max(dev(ax, ay, d2x, d2y), dev(bx, by, d1x, d1y)));
      }
      const score = bis + CORNER_SHAPE_WEIGHT * (shapeErr / HALF_PI);
      if (score > sector.score + 1e-9 || (score > sector.score - 1e-9 && dist >= sector.dist)) continue;
      // DIRECT line of sight only: the node→corner segment may cross neither
      // another skeleton wall nor any room/outline face on the way.
      const losDir = new THREE.Vector2(dx / dist, dy / dist);
      const hit = castRayAgainstSkeletonEdges(skeleton, node.position, losDir, dist - 0.01, excluded);
      if (hit.hit) continue;
      if (castFacingWall(skeleton, node.position, losDir, dist - 0.01, 0).hit) continue;
      sector.corner = { cx: c.x, cy: c.y };
      sector.score = score;
      sector.dist = dist;
    }
  }
  return sectors.map((s2) => ({ edgeA: s2.edgeA, edgeB: s2.edgeB, corner: s2.corner }));
}

/**
 * Corner-matched side thicknesses for one edge: the edge ASSUMES its width
 * from the corners matched in the sectors ADJACENT to it at its two end
 * nodes — the corner's lateral distance from the edge line, on whichever
 * side it lies. When BOTH ends claim a corner on the same side, the width
 * uses both — the average of the two claims — never just one of them.
 * `null` per side = no corner evidence (caller falls back to face matching /
 * probes).
 */
function cornerSideThickness(skeleton: FloorSkeleton, edgeId: string, radius: number): { left: number | null; right: number | null } {
  const edge = skeleton.edges.get(edgeId);
  if (!edge) return { left: null, right: null };
  const sn = skeleton.nodes.get(edge.startNodeId);
  const en = skeleton.nodes.get(edge.endNodeId);
  if (!sn || !en) return { left: null, right: null };
  const ex = en.position.x - sn.position.x;
  const ey = en.position.y - sn.position.y;
  const elen = Math.hypot(ex, ey);
  if (elen < 1e-9) return { left: null, right: null };
  const nx = -ey / elen; // left normal
  const ny = ex / elen;
  const lefts: number[] = [];
  const rights: number[] = [];
  for (const nid of [edge.startNodeId, edge.endNodeId]) {
    for (const sec of matchNodeCornersBySector(skeleton, nid, radius)) {
      if (!sec.corner || (sec.edgeA !== edgeId && sec.edgeB !== edgeId)) continue;
      const lat = (sec.corner.cx - sn.position.x) * nx + (sec.corner.cy - sn.position.y) * ny;
      if (lat > 0.005) lefts.push(lat);
      else if (lat < -0.005) rights.push(-lat);
    }
  }
  const avg = (v: number[]): number | null => (v.length === 0 ? null : v.reduce((s2, x) => s2 + x, 0) / v.length);
  return { left: avg(lefts), right: avg(rights) };
}

/**
 * Two-phase smart pick: UN-EXTENDED matches first — the span extension exists
 * ONLY to rescue short zigzag pieces whose face lies wholly beyond their own
 * span. Letting extended candidates compete on every edge lets far-off faces
 * dominate ordinary walls; here a side only sees extended matches when the
 * honest pass found nothing at all for it.
 */
function smartSideThickness(skeleton: FloorSkeleton, edgeId: string, maxDist: number, extendBy: number, innerThickness: number): { left: number | null; right: number | null } {
  const base = matchedFaceThickness(skeleton, edgeId, maxDist, 0);
  let left = base.leftInner ? innerThickness : base.left;
  let right = base.rightInner ? innerThickness : base.right;
  if (left === null || right === null) {
    const ext = matchedFaceThickness(skeleton, edgeId, maxDist, extendBy);
    if (left === null) left = ext.leftInner ? innerThickness : ext.left;
    if (right === null) right = ext.rightInner ? innerThickness : ext.right;
  }
  return { left, right };
}

export function measureEdgeThickness(
  skeleton: FloorSkeleton,
  edgeId: string,
  opts: { maxRayDistance?: number; defaultWallThickness?: number; sampleCount?: number; maxAngleFromPerpDeg?: number; extendEdgesBy?: number } = {},
): { leftSize: number; rightSize: number } | null {
  const maxRayDistance = opts.maxRayDistance ?? 1.0;
  const defaultWallThickness = opts.defaultWallThickness ?? 0.15;
  const maxAngleFromPerpDeg = opts.maxAngleFromPerpDeg ?? 30;
  // Virtually extend every polygon edge by this much along its
  // tangent at each end during ray-casts. Catches glancing probes
  // that would otherwise slip past the end of a local wall through a
  // small corner gap and latch onto a far wall.
  const extendEdgesBy = opts.extendEdgesBy ?? 0.5;
  // Per-side measurement picks the dominant target wall (most probe hits) and
  // averages just its hits; the glancing-hit gate filters outlier probes first.
  const sampleCount = Math.max(3, opts.sampleCount ?? 9);

  const edge = skeleton.edges.get(edgeId);
  if (!edge || edge.isJunctionEdge) return null;
  const startNode = skeleton.nodes.get(edge.startNodeId);
  const endNode = skeleton.nodes.get(edge.endNodeId);
  if (!startNode || !endNode) return null;

  const edgeDir = new THREE.Vector2(endNode.position.x - startNode.position.x, endNode.position.y - startNode.position.y).normalize();
  const perpLeft = new THREE.Vector2(-edgeDir.y, edgeDir.x);
  const perpRight = new THREE.Vector2(edgeDir.y, -edgeDir.x);

  // Collect per-probe hit distances per side. Pick the thickness shared by the
  // MOST probes (the dominant wall this side faces), grouping by DISTANCE BAND
  // rather than exact wall identity — at a junction the probes fan out onto
  // different walls at different distances, but the real facing wall is whatever
  // most probes land at. Banding by distance keeps a single wall together even
  // when the ray-caster reports slightly different segments per probe.
  const innerWallThickness = 0.05;
  const leftDists: ThicknessHit[] = [];
  const rightDists: ThicknessHit[] = [];

  // SMART FACE PICK first: match the faces this edge actually runs
  // alongside; ray probing below only measures sides matching couldn't
  // resolve.
  const corner = cornerSideThickness(skeleton, edgeId, maxRayDistance);
  const face = corner.left === null || corner.right === null ? smartSideThickness(skeleton, edgeId, maxRayDistance, extendEdgesBy, innerWallThickness) : { left: null, right: null };
  const smartLeft = corner.left ?? face.left;
  const smartRight = corner.right ?? face.right;

  for (let i = 1; smartLeft === null || smartRight === null ? i < sampleCount : false; i++) {
    const t = i / sampleCount;
    // Probe origin sits ON the skeleton edge so the reported hit
    // distance IS the wall thickness from the line itself, with no
    // offset-back correction needed.
    const samplePoint = new THREE.Vector3(startNode.position.x + t * (endNode.position.x - startNode.position.x), startNode.position.y + t * (endNode.position.y - startNode.position.y), startNode.position.z);

    // Prefer the nearest STRICTLY-PARALLEL face: the gated cast sees straight
    // past slanted corners / curved boundaries occluding the true facing wall.
    // Only when no parallel face exists within range does the ungated nearest
    // hit (still subject to the loose gates below) measure the side.
    const strictGate = { dir: edgeDir, minCos: THICKNESS_STRICT_PARALLEL_COS };
    const leftParallelHit = castFacingWall(skeleton, samplePoint, perpLeft, maxRayDistance, extendEdgesBy, strictGate);
    const rightParallelHit = castFacingWall(skeleton, samplePoint, perpRight, maxRayDistance, extendEdgesBy, strictGate);
    const leftHit = leftParallelHit.hit ? leftParallelHit : castFacingWall(skeleton, samplePoint, perpLeft, maxRayDistance, extendEdgesBy);
    const rightHit = rightParallelHit.hit ? rightParallelHit : castFacingWall(skeleton, samplePoint, perpRight, maxRayDistance, extendEdgesBy);
    const leftSkeletonHit = castRayAgainstSkeletonEdges(skeleton, samplePoint, perpLeft, maxRayDistance, [edgeId]);
    const rightSkeletonHit = castRayAgainstSkeletonEdges(skeleton, samplePoint, perpRight, maxRayDistance, [edgeId]);

    // A trusted facing-wall hit is both perpendicular to the ray AND on a wall
    // PARALLEL to the edge (not a perpendicular wall grazed at a junction).
    const leftRoomOk = leftHit.hit && isHitPerpendicular(perpLeft, leftHit.hitSegStart, leftHit.hitSegEnd, maxAngleFromPerpDeg) && isHitParallelToEdge(edgeDir, leftHit.hitSegStart, leftHit.hitSegEnd, maxAngleFromPerpDeg);
    const rightRoomOk = rightHit.hit && isHitPerpendicular(perpRight, rightHit.hitSegStart, rightHit.hitSegEnd, maxAngleFromPerpDeg) && isHitParallelToEdge(edgeDir, rightHit.hitSegStart, rightHit.hitSegEnd, maxAngleFromPerpDeg);

    const leftSkeletonDist = leftSkeletonHit.distance ?? Infinity;
    const leftHitDist = leftRoomOk ? (leftHit.distance ?? Infinity) : Infinity;
    if (leftSkeletonHit.hit && (!leftRoomOk || leftSkeletonDist < leftHitDist)) leftDists.push({ d: innerWallThickness, parallelCos: 1 });
    else if (leftRoomOk) leftDists.push({ d: leftHitDist, parallelCos: parallelCosToEdge(edgeDir, leftHit.hitSegStart, leftHit.hitSegEnd) });

    const rightSkeletonDist = rightSkeletonHit.distance ?? Infinity;
    const rightHitDist = rightRoomOk ? (rightHit.distance ?? Infinity) : Infinity;
    if (rightSkeletonHit.hit && (!rightRoomOk || rightSkeletonDist < rightHitDist)) rightDists.push({ d: innerWallThickness, parallelCos: 1 });
    else if (rightRoomOk) rightDists.push({ d: rightHitDist, parallelCos: parallelCosToEdge(edgeDir, rightHit.hitSegStart, rightHit.hitSegEnd) });
  }

  const leftDist = smartLeft ?? dominantParallelDistance(leftDists);
  const rightDist = smartRight ?? dominantParallelDistance(rightDists);
  return {
    leftSize: leftDist ?? defaultWallThickness,
    rightSize: rightDist ?? defaultWallThickness,
  };
}

/**
 * From a list of per-probe hit distances, return the thickness the probes most
 * agree on: bucket the distances into ~`band`-metre bins and take the bin with
 * the highest WEIGHTED score, then average just that bin's distances.
 *
 * Each hit's weight is `1 / (d + EPS)`, so SHORTER distances count for more — a
 * thinner wall is preferred over a thicker one at comparable support, since a
 * real wall sits closer than a far wall a few stray probes reached across a gap.
 * Ties break toward the nearer bin. Returns null only when the list is empty.
 */
function dominantBandedDistance(dists: number[], band = 0.03): number | null {
  if (dists.length === 0) return null;
  const EPS = 0.02;
  const bins = new Map<number, number[]>();
  for (const d of dists) {
    const key = Math.round(d / band);
    const arr = bins.get(key);
    if (arr) arr.push(d);
    else bins.set(key, [d]);
  }
  const avg = (a: number[]): number => a.reduce((s, v) => s + v, 0) / a.length;
  const score = (a: number[]): number => a.reduce((s, v) => s + 1 / (v + EPS), 0);
  let best: number[] | null = null;
  let bestScore = -Infinity;
  let bestKey = Infinity;
  for (const [key, arr] of bins) {
    const sc = score(arr);
    if (!best || sc > bestScore || (sc === bestScore && key < bestKey)) {
      best = arr;
      bestScore = sc;
      bestKey = key;
    }
  }
  return best ? avg(best) : null;
}

/**
 * Measure wall thickness for each edge by casting rays perpendicular to the edge.
 * Sets leftSize and rightSize based on the distance to room polygon walls.
 *
 * For each edge:
 * - Sample multiple points along the edge
 * - Cast rays perpendicular to the edge (left and right)
 * - Group hits by which target wall they landed on; set leftSize/rightSize from
 *   the DOMINANT wall (most probe hits) on each side, averaging just its hits
 * - If no hit, use a default wall thickness
 */
export function measureWallThicknessPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxRayDistance = kwargs?.maxRayDistance ?? 1.0;
  const defaultWallThickness = kwargs?.outerWallThickness ?? 0.15;
  // Per-side measurement picks the target wall the most probes hit and averages
  // just its hits (see the dominant-target reduction below). The glancing-hit
  // gate and tangent extension discard outlier probes before tallying.
  const sampleCount = Math.max(3, kwargs?.measureWallThicknessSampleCount ?? 9);
  // Glancing-hit gate: discard probes whose hit on the wall sits more
  // than `maxAngleFromPerpDeg` off perpendicular. Those tend to catch
  // a corner of a neighboring wall and produce a misleadingly short
  // thickness.
  const maxAngleFromPerpDeg = kwargs?.measureWallThicknessMaxAngleDeg ?? 30;
  // Virtually extend every polygon edge by this much along its
  // tangent at each end during ray-casts. Catches glancing probes
  // that would otherwise slip past the end of a local wall through a
  // small corner gap and latch onto a far wall.
  const extendEdgesBy = kwargs?.measureWallThicknessThickenBy ?? 0.5;

  // Initialize debug rays array and capture a non-nullable reference
  // so the inner sweep can append without a `!` assertion on every
  // push. Own corner-match ties are cleared first so pipeline re-runs on
  // the same skeleton don't accumulate stale ones.
  const debugRays: DebugRay[] = (skeleton.debugRays ?? []).filter((r) => r.label !== 'corner-match');
  skeleton.debugRays = debugRays;
  /** Nodes whose sector ties were already emitted (one set per pass run). */
  const cornerTieNodes = new Set<string>();

  // Edges too short to probe meaningfully — resolved AFTER the measuring loop
  // by inheriting from their most-collinear measured neighbour.
  const minMeasurableLength = kwargs?.measureWallThicknessMinEdgeLength ?? 0.15;
  const shortEdgeIds = new Set<string>();

  // First pass: measure thickness for non-junction edges
  for (const [edgeId, edge] of skeleton.edges) {
    // Skip junction edges in first pass - they'll be handled after
    if (edge.isJunctionEdge) continue;

    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);
    if (!startNode || !endNode) continue;

    // A sub-length edge (junction bridge connector, sliver jog) has no span of
    // its own to probe — its perpendicular rays only see junction interior and
    // come back with junk asymmetric thickness. Defer to neighbour inheritance.
    if (Math.hypot(endNode.position.x - startNode.position.x, endNode.position.y - startNode.position.y) < minMeasurableLength) {
      shortEdgeIds.add(edgeId);
      continue;
    }

    // Calculate edge direction and perpendicular
    const edgeDir = new THREE.Vector2(endNode.position.x - startNode.position.x, endNode.position.y - startNode.position.y).normalize();

    // Perpendicular directions (left = counter-clockwise, right = clockwise)
    const perpLeft = new THREE.Vector2(-edgeDir.y, edgeDir.x);
    const perpRight = new THREE.Vector2(edgeDir.y, -edgeDir.x);

    // Most-hits reduction: tag each probe hit with WHICH target it hit (a polygon
    // wall by room + segment, or the skeleton inner wall), then pick the target
    // with the most hits and average just those distances. At a junction the
    // probes fan out onto several walls; the dominant-target pick avoids blending
    // them into a meaningless mean.
    const innerWallThickness = 0.05;
    const leftDists: ThicknessHit[] = [];
    const rightDists: ThicknessHit[] = [];

    // SMART FACE PICK first: match the faces this edge actually runs
    // alongside (parallel + overlapping + nearest per side); ray probing
    // below only measures the sides matching couldn't resolve.
    const corner = cornerSideThickness(skeleton, edgeId, maxRayDistance);
    const face = corner.left === null || corner.right === null ? smartSideThickness(skeleton, edgeId, maxRayDistance, extendEdgesBy, innerWallThickness) : { left: null, right: null };
    const smartLeft = corner.left ?? face.left;
    const smartRight = corner.right ?? face.right;

    // Corner-match debug ties: ONE per node sector (a node shows as many
    // ties as it has edges; dead ends show two) — rendered by the skeleton
    // debug menu's "Show Corner Matches" toggle. Deduped per node across
    // the edge loop.
    for (const nid of [edge.startNodeId, edge.endNodeId]) {
      if (cornerTieNodes.has(nid)) continue;
      cornerTieNodes.add(nid);
      const nodeObj = skeleton.nodes.get(nid);
      if (!nodeObj) continue;
      for (const sec of matchNodeCornersBySector(skeleton, nid, maxRayDistance)) {
        if (!sec.corner) continue;
        const cdx = sec.corner.cx - nodeObj.position.x;
        const cdy = sec.corner.cy - nodeObj.position.y;
        const cd = Math.hypot(cdx, cdy);
        if (cd < 1e-6) continue;
        debugRays.push({ origin: nodeObj.position.clone(), direction: new THREE.Vector3(cdx / cd, cdy / cd, 0), distance: cd, hit: true, label: 'corner-match' });
      }
    }

    for (let i = 1; smartLeft === null || smartRight === null ? i < sampleCount : false; i++) {
      const t = i / sampleCount;
      // Probe origin sits ON the skeleton edge so the reported hit
      // distance IS the wall thickness from the line itself, with no
      // offset-back correction needed.
      const samplePoint = new THREE.Vector3(startNode.position.x + t * (endNode.position.x - startNode.position.x), startNode.position.y + t * (endNode.position.y - startNode.position.y), startNode.position.z);

      // Prefer the nearest STRICTLY-PARALLEL face — see measureEdgeThickness.
      const strictGate = { dir: edgeDir, minCos: THICKNESS_STRICT_PARALLEL_COS };
      const leftParallelHit = castFacingWall(skeleton, samplePoint, perpLeft, maxRayDistance, extendEdgesBy, strictGate);
      const rightParallelHit = castFacingWall(skeleton, samplePoint, perpRight, maxRayDistance, extendEdgesBy, strictGate);
      const leftHit = leftParallelHit.hit ? leftParallelHit : castFacingWall(skeleton, samplePoint, perpLeft, maxRayDistance, extendEdgesBy);
      const rightHit = rightParallelHit.hit ? rightParallelHit : castFacingWall(skeleton, samplePoint, perpRight, maxRayDistance, extendEdgesBy);
      const leftSkeletonHit = castRayAgainstSkeletonEdges(skeleton, samplePoint, perpLeft, maxRayDistance, [edgeId]);
      const rightSkeletonHit = castRayAgainstSkeletonEdges(skeleton, samplePoint, perpRight, maxRayDistance, [edgeId]);

      // A trusted facing-wall hit is both perpendicular to the ray AND on a wall
      // PARALLEL to the edge (not a perpendicular wall grazed at a junction).
      const leftRoomOk = leftHit.hit && isHitPerpendicular(perpLeft, leftHit.hitSegStart, leftHit.hitSegEnd, maxAngleFromPerpDeg) && isHitParallelToEdge(edgeDir, leftHit.hitSegStart, leftHit.hitSegEnd, maxAngleFromPerpDeg);
      const rightRoomOk = rightHit.hit && isHitPerpendicular(perpRight, rightHit.hitSegStart, rightHit.hitSegEnd, maxAngleFromPerpDeg) && isHitParallelToEdge(edgeDir, rightHit.hitSegStart, rightHit.hitSegEnd, maxAngleFromPerpDeg);

      const leftSkeletonDist = leftSkeletonHit.distance ?? Infinity;
      const leftHitDist = leftRoomOk ? (leftHit.distance ?? Infinity) : Infinity;
      const effectiveLeftDist = leftSkeletonHit.hit && (!leftRoomOk || leftSkeletonDist < leftHitDist) ? innerWallThickness : leftRoomOk ? leftHitDist : null;
      if (effectiveLeftDist !== null) {
        leftDists.push({ d: effectiveLeftDist, parallelCos: effectiveLeftDist === innerWallThickness ? 1 : parallelCosToEdge(edgeDir, leftHit.hitSegStart, leftHit.hitSegEnd) });
      }

      const rightSkeletonDist = rightSkeletonHit.distance ?? Infinity;
      const rightHitDist = rightRoomOk ? (rightHit.distance ?? Infinity) : Infinity;
      const effectiveRightDist = rightSkeletonHit.hit && (!rightRoomOk || rightSkeletonDist < rightHitDist) ? innerWallThickness : rightRoomOk ? rightHitDist : null;
      if (effectiveRightDist !== null) {
        rightDists.push({ d: effectiveRightDist, parallelCos: effectiveRightDist === innerWallThickness ? 1 : parallelCosToEdge(edgeDir, rightHit.hitSegStart, rightHit.hitSegEnd) });
      }

      debugRays.push({
        origin: samplePoint.clone(),
        direction: new THREE.Vector3(perpLeft.x, perpLeft.y, 0),
        distance: effectiveLeftDist ?? maxRayDistance,
        hit: effectiveLeftDist !== null,
        label: 'wall-thickness-left',
      });
      debugRays.push({
        origin: samplePoint.clone(),
        direction: new THREE.Vector3(perpRight.x, perpRight.y, 0),
        distance: effectiveRightDist ?? maxRayDistance,
        hit: effectiveRightDist !== null,
        label: 'wall-thickness-right',
      });
    }

    const leftDist = smartLeft ?? dominantParallelDistance(leftDists);
    const rightDist = smartRight ?? dominantParallelDistance(rightDists);
    edge.leftSize = leftDist ?? defaultWallThickness;
    edge.rightSize = rightDist ?? defaultWallThickness;

    // Debug rays for smart-resolved sides (probed sides already pushed theirs).
    const midX = (startNode.position.x + endNode.position.x) / 2;
    const midY = (startNode.position.y + endNode.position.y) / 2;
    if (smartLeft !== null) {
      debugRays.push({ origin: new THREE.Vector3(midX, midY, startNode.position.z), direction: new THREE.Vector3(perpLeft.x, perpLeft.y, 0), distance: smartLeft, hit: true, label: 'wall-thickness-left' });
    }
    if (smartRight !== null) {
      debugRays.push({ origin: new THREE.Vector3(midX, midY, startNode.position.z), direction: new THREE.Vector3(perpRight.x, perpRight.y, 0), distance: smartRight, hit: true, label: 'wall-thickness-right' });
    }
  }

  // Deferred SHORT edges inherit from the most-collinear resolved neighbour at
  // either endpoint — a bridge connector along a wall is part of THAT wall, so
  // matching its thickness lets the collinear passes dissolve it instead of
  // being blocked by a fake thickness step. Side mapping follows orientation:
  // an antiparallel neighbour's left is this edge's right. Runs in rounds so a
  // CHAIN of short edges propagates from its measured end; leftovers with no
  // resolved neighbour at all fall back to the default.
  const pendingShort = new Set(shortEdgeIds);
  let inheritedAny = true;
  while (inheritedAny) {
    inheritedAny = false;
    for (const edgeId of [...pendingShort]) {
      const edge = skeleton.edges.get(edgeId);
      if (!edge) {
        pendingShort.delete(edgeId);
        continue;
      }
      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);
      if (!startNode || !endNode) {
        pendingShort.delete(edgeId);
        continue;
      }
      const ex = endNode.position.x - startNode.position.x;
      const ey = endNode.position.y - startNode.position.y;
      const elen = Math.hypot(ex, ey);

      let best: SkeletonEdge | null = null;
      let bestCos = 0;
      for (const nodeId of [edge.startNodeId, edge.endNodeId]) {
        const n = skeleton.nodes.get(nodeId);
        if (!n) continue;
        for (const nbId of n.edgeIds) {
          if (nbId === edgeId) continue;
          const nb = skeleton.edges.get(nbId);
          if (!nb || nb.isJunctionEdge || pendingShort.has(nbId)) continue;
          const ns = skeleton.nodes.get(nb.startNodeId);
          const ne = skeleton.nodes.get(nb.endNodeId);
          if (!ns || !ne) continue;
          const nx = ne.position.x - ns.position.x;
          const ny = ne.position.y - ns.position.y;
          const nlen = Math.hypot(nx, ny);
          if (nlen < 1e-9) continue;
          // Degenerate short edge (zero length) → any neighbour will do.
          const cos = elen < 1e-9 ? 1 : (ex * nx + ey * ny) / (elen * nlen);
          if (Math.abs(cos) > Math.abs(bestCos) || best === null) {
            bestCos = cos;
            best = nb;
          }
        }
      }
      if (best) {
        const sameDir = bestCos >= 0;
        edge.leftSize = sameDir ? best.leftSize : best.rightSize;
        edge.rightSize = sameDir ? best.rightSize : best.leftSize;
        pendingShort.delete(edgeId);
        inheritedAny = true;
      }
    }
  }
  for (const edgeId of pendingShort) {
    const edge = skeleton.edges.get(edgeId);
    if (!edge) continue;
    edge.leftSize = defaultWallThickness;
    edge.rightSize = defaultWallThickness;
  }

  // Second pass: set junction edge thickness based on the external edge at the start node
  // Junction edges form a ring, and each ring node connects to exactly one external edge
  // Only the outside (facing the wall) gets thickness, inside (facing junction center) is minimal
  for (const [, edge] of skeleton.edges) {
    if (!edge.isJunctionEdge) continue;

    const startNode = skeleton.nodes.get(edge.startNodeId);
    if (!startNode) continue;

    // Find the non-junction edge connected to the start node
    let externalEdge: SkeletonEdge | null = null;
    for (const connectedEdgeId of startNode.edgeIds) {
      const connectedEdge = skeleton.edges.get(connectedEdgeId);
      if (connectedEdge && !connectedEdge.isJunctionEdge) {
        externalEdge = connectedEdge;
        break;
      }
    }

    if (externalEdge) {
      // Determine which side of the external edge faces outward (the wall side)
      // If startNode is the start of the external edge, the wall is on the left side
      // If startNode is the end of the external edge, the wall is on the right side
      const isStartOfExternal = externalEdge.startNodeId === startNode.id;
      const wallThickness = isStartOfExternal ? externalEdge.leftSize : externalEdge.rightSize;

      // Junction edges go around the ring counter-clockwise (sorted by angle in splitJunctionNodesPass)
      // The outside of the ring (wall side) is on the left, inside (junction center) is on the right
      edge.leftSize = 0.001;
      edge.rightSize = wallThickness;
    } else {
      // Fallback to default
      edge.leftSize = 0.001;
      edge.rightSize = defaultWallThickness;
    }
  }

  return skeleton;
}

/**
 * Prune Dead Ends Pass
 *
 * Removes dead-end stubs that don't look like a real wall — i.e. the protruding
 * edge that has no "flat head" capping its open end.
 *
 * A dead end is a node with exactly one edge. That edge runs from its other node
 * (`O`) out to the dead-end node (`N`); the OPEN END is just past `N`. A genuine
 * wall stub looks like a rectangle: two PARALLEL side walls running along the edge
 * plus a PERPENDICULAR wall capping the open end (the "flat head"). We keep the
 * dead end only when all three are present (within `pruneDeadEndAngleTolDeg`,
 * default ±5°):
 *   1. A wall in front of `N` (along `edgeDir`) that is ~perpendicular to the edge.
 *   2. A wall to the left of the edge that is ~parallel to it.
 *   3. A wall to the right of the edge that is ~parallel to it.
 * Otherwise the edge and its now-orphaned dead-end node are deleted.
 *
 * Probes are cast against room polygons (the real walls). Run this BEFORE
 * `extendDeadEndNodesPass` so legitimate stubs survive to be extended and stray
 * ones are removed rather than extended into the void.
 */
export function pruneDeadEndsPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxRayDistance = kwargs?.pruneDeadEndMaxRayDistance ?? 1.0;
  const angleTolDeg = kwargs?.pruneDeadEndAngleTolDeg ?? 5;

  if (!skeleton.debugRays) {
    skeleton.debugRays = [];
  }

  // Collect dead-end nodes (exactly one incident edge) up front; we mutate the
  // edge/node maps as we prune, so snapshot the candidates first.
  const deadEndNodes: SkeletonNode[] = [];
  for (const node of skeleton.nodes.values()) {
    if (node.edgeIds.length === 1) {
      deadEndNodes.push(node);
    }
  }

  for (const node of deadEndNodes) {
    const edge = skeleton.edges.get(node.edgeIds[0]);
    if (!edge) continue;

    const otherNodeId = edge.startNodeId === node.id ? edge.endNodeId : edge.startNodeId;
    const otherNode = skeleton.nodes.get(otherNodeId);
    if (!otherNode) continue;

    // Outward direction (from the other node toward the open end) and its
    // left/right perpendiculars.
    const edgeDir = new THREE.Vector2(node.position.x - otherNode.position.x, node.position.y - otherNode.position.y);
    if (edgeDir.lengthSq() < 1e-12) continue;
    edgeDir.normalize();
    const perpLeft = new THREE.Vector2(-edgeDir.y, edgeDir.x);
    const perpRight = new THREE.Vector2(edgeDir.y, -edgeDir.x);

    // (1) Flat head: a perpendicular wall just past the open end.
    const headHit = castRayAgainstRoomPolygons(skeleton, node.position, edgeDir, maxRayDistance);
    const hasFlatHead = headHit.hit && isHitPerpendicular(edgeDir, headHit.hitSegStart, headHit.hitSegEnd, angleTolDeg);

    // (2)/(3) Side walls: parallel walls flanking the edge. Probe from the
    // edge midpoint so we sit between the two side walls rather than at a
    // corner where a perpendicular wall could intrude.
    const midPoint = new THREE.Vector3((node.position.x + otherNode.position.x) / 2, (node.position.y + otherNode.position.y) / 2, node.position.z);
    const leftHit = castRayAgainstRoomPolygons(skeleton, midPoint, perpLeft, maxRayDistance);
    const rightHit = castRayAgainstRoomPolygons(skeleton, midPoint, perpRight, maxRayDistance);
    const hasLeftWall = leftHit.hit && isHitParallelToEdge(edgeDir, leftHit.hitSegStart, leftHit.hitSegEnd, angleTolDeg);
    const hasRightWall = rightHit.hit && isHitParallelToEdge(edgeDir, rightHit.hitSegStart, rightHit.hitSegEnd, angleTolDeg);

    const keep = hasFlatHead && hasLeftWall && hasRightWall;

    // Debug rays: the head probe and the two side probes.
    skeleton.debugRays.push({
      origin: node.position.clone(),
      direction: new THREE.Vector3(edgeDir.x, edgeDir.y, 0),
      distance: headHit.hit ? (headHit.distance ?? maxRayDistance) : maxRayDistance,
      hit: headHit.hit,
      label: keep ? 'prune-dead-end-keep' : 'prune-dead-end-remove',
    });
    skeleton.debugRays.push({
      origin: midPoint.clone(),
      direction: new THREE.Vector3(perpLeft.x, perpLeft.y, 0),
      distance: leftHit.hit ? (leftHit.distance ?? maxRayDistance) : maxRayDistance,
      hit: leftHit.hit,
      label: 'prune-dead-end-side',
    });
    skeleton.debugRays.push({
      origin: midPoint.clone(),
      direction: new THREE.Vector3(perpRight.x, perpRight.y, 0),
      distance: rightHit.hit ? (rightHit.distance ?? maxRayDistance) : maxRayDistance,
      hit: rightHit.hit,
      label: 'prune-dead-end-side',
    });

    if (keep) continue;

    // Remove the stub: drop the edge from the other node, delete the edge, and
    // delete the dead-end node (now orphaned).
    otherNode.edgeIds = otherNode.edgeIds.filter((id) => id !== edge.id);
    skeleton.edges.delete(edge.id);
    skeleton.nodes.delete(node.id);
  }

  return skeleton;
}

/**
 * Extend Dead End Nodes Pass
 *
 * Finds nodes with only one edge (dead ends) and extends them by casting a ray
 * in the opposite direction of their edge. If the ray hits a room wall, the node
 * is moved to the hit point and a new edge is created connecting to the skeleton.
 */
export function extendDeadEndNodesPass(skeleton: FloorSkeleton, kwargs?: SkeletonPassKwargs): FloorSkeleton {
  const maxRayDistance = kwargs?.deadEndMaxRayDistance ?? 5.0;

  // Initialize debug rays array
  if (!skeleton.debugRays) {
    skeleton.debugRays = [];
  }

  // Find all dead-end nodes (nodes with only one edge)
  const deadEndNodes: SkeletonNode[] = [];
  for (const node of skeleton.nodes.values()) {
    if (node.edgeIds.length === 1) {
      deadEndNodes.push(node);
    }
  }

  let newEdgeIdCounter = skeleton.edges.size;

  for (const node of deadEndNodes) {
    // Get the single connected edge
    const edge = skeleton.edges.get(node.edgeIds[0]);
    if (!edge) continue;

    // Find the other node on this edge
    const otherNodeId = edge.startNodeId === node.id ? edge.endNodeId : edge.startNodeId;
    const otherNode = skeleton.nodes.get(otherNodeId);
    if (!otherNode) continue;

    // Calculate direction from other node to this node (the direction we want to extend)
    const edgeDir = new THREE.Vector2(node.position.x - otherNode.position.x, node.position.y - otherNode.position.y).normalize();

    // Cast ray in the extension direction against room polygons AND skeleton edges
    // We want to find the FIRST hit (closest to the node)
    const roomHit = castRayAgainstRoomPolygons(skeleton, node.position, edgeDir, maxRayDistance);
    const skeletonHit = castRayAgainstSkeletonEdges(skeleton, node.position, edgeDir, maxRayDistance, [edge.id]);

    // Determine which hit is closer (first hit)
    const roomDist = roomHit.hit ? (roomHit.distance ?? Infinity) : Infinity;
    const skeletonDist = skeletonHit.hit ? (skeletonHit.distance ?? Infinity) : Infinity;
    const useSkeletonHit = skeletonHit.hit && skeletonDist < roomDist;

    const effectiveHit = useSkeletonHit ? skeletonHit : roomHit;
    const effectiveDist = useSkeletonHit ? skeletonDist : roomDist;

    // Add debug ray
    skeleton.debugRays.push({
      origin: node.position.clone(),
      direction: new THREE.Vector3(edgeDir.x, edgeDir.y, 0),
      distance: effectiveHit.hit ? effectiveDist : maxRayDistance,
      hit: effectiveHit.hit,
      label: 'dead-end-extend',
    });

    if (effectiveHit.hit && effectiveHit.hitPoint) {
      // Move the node to the hit point
      node.position.copy(effectiveHit.hitPoint);

      // If we hit a skeleton edge, split it
      if (useSkeletonHit && skeletonHit.hitEdgeId && skeletonHit.hitPoint) {
        const hitEdge = skeleton.edges.get(skeletonHit.hitEdgeId);
        if (hitEdge) {
          // Move node to the intersection point on the skeleton edge
          node.position.copy(skeletonHit.hitPoint);

          // Remove the old edge from the hit edge's nodes
          const hitStartNode = skeleton.nodes.get(hitEdge.startNodeId);
          const hitEndNode = skeleton.nodes.get(hitEdge.endNodeId);

          if (hitStartNode) {
            hitStartNode.edgeIds = hitStartNode.edgeIds.filter((id) => id !== hitEdge.id);
          }
          if (hitEndNode) {
            hitEndNode.edgeIds = hitEndNode.edgeIds.filter((id) => id !== hitEdge.id);
          }

          // Create two new edges to replace the split edge
          const newEdge1: SkeletonEdge = {
            id: `edge_${newEdgeIdCounter++}`,
            startNodeId: hitEdge.startNodeId,
            endNodeId: node.id,
            roomIds: [...hitEdge.roomIds],
            leftSize: hitEdge.leftSize,
            rightSize: hitEdge.rightSize,
            wallType: hitEdge.wallType,
            selected: false,
          };

          const newEdge2: SkeletonEdge = {
            id: `edge_${newEdgeIdCounter++}`,
            startNodeId: node.id,
            endNodeId: hitEdge.endNodeId,
            roomIds: [...hitEdge.roomIds],
            leftSize: hitEdge.leftSize,
            rightSize: hitEdge.rightSize,
            wallType: hitEdge.wallType,
            selected: false,
          };

          // Add new edges to skeleton
          skeleton.edges.set(newEdge1.id, newEdge1);
          skeleton.edges.set(newEdge2.id, newEdge2);

          // Update node edge references
          node.edgeIds.push(newEdge1.id, newEdge2.id);
          if (hitStartNode) hitStartNode.edgeIds.push(newEdge1.id);
          if (hitEndNode) hitEndNode.edgeIds.push(newEdge2.id);

          // Remove the original hit edge
          skeleton.edges.delete(hitEdge.id);
        }
      }
    }
  }

  return skeleton;
}

// ============================================================================
// Pipeline Class
// ============================================================================

/**
 * Processing pipeline for skeleton graphs.
 * Provides configurable passes for cleaning up node/edge structures.
 */
export class SkeletonProcessingPipeline extends BasePipeline<FloorSkeleton, FloorSkeleton, FloorSkeleton, SkeletonPassKwargs> {
  constructor() {
    super();
    this.registerProcessors();
    this.setupDefaultPasses();
  }

  protected inputToContext(input: FloorSkeleton): FloorSkeleton {
    return input;
  }

  protected contextToOutput(context: FloorSkeleton): FloorSkeleton {
    return context;
  }

  /**
   * Register all available processors
   */
  private registerProcessors(): void {
    // Wall-pairing pass (runs before node/edge creation).
    this.registerProcessor('pair_walls', pairWallsPass, {
      defaultKwargs: { outerWallThickness: 0.3 },
    });

    // Grow paired-segment endpoints out to the surrounding polygon
    // walls so the next pass can find real intersections.
    this.registerProcessor('grow_pairs', growPairsPass, {
      defaultKwargs: { growPairsMergeAngleDeg: 10, growPairsMergePerpTol: 0.1, growPairsMergeGapTol: 0.2 },
    });

    // Merge the grown paired segments into a connected node graph
    // (intersections become shared nodes; trim grown endpoints whose
    // side has any intersection).
    this.registerProcessor('merge_pairs', mergePairsPass, {
      defaultKwargs: {},
    });

    // Extend paired-segment endpoints along their tangents to meet
    // other skeleton edges' infinite lines (alternative to Merge
    // Pairs — builds the connected node graph by extension instead
    // of by intersection). Polygon edges block extension.
    this.registerProcessor('extend_pairs', extendPairsPass, {
      defaultKwargs: { extendPairsMaxDistance: 5.0, extendPairsSnapTol: 0.05 },
    });

    // Node creation and merging passes
    this.registerProcessor('create_nodes', createNodesPass, {
      defaultKwargs: { probeMaxWalk: 0.32 },
    });

    this.registerProcessor('merge_close_nodes', mergeCloseNodesPass, {
      defaultKwargs: { mergeTolerance: 0.05 },
    });

    this.registerProcessor('remove_collinear_nodes', removeCollinearNodesPass, {
      defaultKwargs: { straightenAngleThreshold: 15, removeCollinearThicknessTol: 0.03 },
    });

    this.registerProcessor('slide_collinear_to_crossing', slideCollinearNodesToCrossingPass, {
      defaultKwargs: { straightenAngleThreshold: 15, perpendicularAngleThreshold: 70, maxRayDistance: 1.0 },
    });

    this.registerProcessor('create_edges', createEdgesPass, {
      defaultKwargs: {},
    });

    this.registerProcessor('delete_edges_crossing_polygons', deleteSkeletonEdgesCrossingPolygonsPass, {
      defaultKwargs: {},
    });

    this.registerProcessor('split_edges_through_nearby_nodes', splitEdgesThroughNearbyNodesPass, {
      defaultKwargs: { snapTolerance: 0.1, endpointMargin: 0.05 },
    });

    this.registerProcessor('delete_duplicate_edges', deleteDuplicateEdgesPass, {
      defaultKwargs: {},
    });

    this.registerProcessor('delete_longest_leg_of_large_triangles', deleteLongestLegOfLargeTrianglesPass, {
      defaultKwargs: { triangleMinCircumradius: 0.5 },
    });

    // Cleanup passes
    this.registerProcessor('snap_to_edges', snapNodesToEdgesPass, {
      defaultKwargs: { snapTolerance: 0.1 },
    });

    this.registerProcessor('resolve_crossings', resolveEdgeCrossingsPass, {
      defaultKwargs: { moveNodes: true },
    });

    this.registerProcessor('merge_parallel', mergeParallelEdgesPass, {
      defaultKwargs: { angleThreshold: PARALLEL_EDGE_ANGLE_THRESHOLD },
    });

    this.registerProcessor('merge_distinct_origins', mergeDistinctOriginsPass, {
      defaultKwargs: { mergeTolerance: 0.15 },
    });

    this.registerProcessor('collapse_triangles', collapseSmallTrianglesPass, {
      defaultKwargs: { maxEdgeLength: 0.3 },
    });

    this.registerProcessor('straighten_edges', straightenEdgesPass, {
      defaultKwargs: {
        outerWallThickness: 0.3,
        innerWallThickness: 0.15,
        maxRayDistance: 5.0,
        straightenMaxIterations: 50,
      },
    });

    this.registerProcessor('split_junctions', splitJunctionNodesPass, {
      defaultKwargs: { junctionSlideDistance: 0.1, perpendicularAngleThreshold: 70 },
    });

    this.registerProcessor('straighten_collinear', straightenCollinearEdgesPass, {
      defaultKwargs: { straightenAngleThreshold: 15 },
    });

    this.registerProcessor('straighten_angles', straightenAnglesPass, {
      defaultKwargs: { straightenAngleThreshold: 15 },
    });

    this.registerProcessor('solve_triangles', solveTriangleNodesPass, {
      defaultKwargs: {},
    });

    this.registerProcessor('measure_wall_thickness', measureWallThicknessPass, {
      defaultKwargs: { maxRayDistance: 1.0, outerWallThickness: 0.15, measureWallThicknessSampleCount: 9, measureWallThicknessMaxAngleDeg: 30, measureWallThicknessThickenBy: 0.5 },
    });

    // Collapse stair-step zigzags (short jog between two collinear walls) into
    // one straight wall at the thinnest common width. Runs AFTER wall-thickness
    // measurement so "thinnest common" uses real measured sizes.
    this.registerProcessor('merge_collinear_zigzags', mergeCollinearZigzagsPass, {
      defaultKwargs: { zigzagMaxConnectorLength: 0.25, zigzagCollinearAngleDeg: 12, zigzagThicknessEqTol: 0.03 },
    });

    // Alternative skeleton builder: the straight skeleton of the wall region
    // (outer shell minus rooms/cavities). Registered for the debug menu; NOT in
    // the default chain — it replaces the pair-walls construction wholesale.
    this.registerProcessor('straight_skeleton', straightSkeletonPass, {
      defaultKwargs: {},
    });

    // Raster-thinned medial axis of the wall region (Zhang–Suen). Cleaner and
    // more robust than the straight skeleton on noisy scan data. Registered for
    // the debug menu; NOT in the default chain. All three kwargs are listed so
    // the debug panel renders a control for each; rasterCellSize /
    // rasterSimplifyTolerance = 0 means "auto" (derived from wall thickness).
    this.registerProcessor('raster_skeleton', rasterSkeletonPass, {
      defaultKwargs: { rasterCellSize: 0, rasterSimplifyTolerance: 0, rasterAxisSnapAngleDeg: 8 },
    });

    // Straighten/center each edge between the two real walls it sits in.
    // Registered for the debug menu; NOT in the default chain. The three sub-
    // passes are also registered separately so each stage can be run and
    // inspected on its own (match → combine → reconnect); they share state via
    // `skeleton.centerWalls`, so run them in that order.
    // MATCH searches wider (a wall pair can be far/angled), so it gets a larger
    // max distance + angle; COMBINE and RECONNECT work on the already-matched
    // geometry and stay tight.
    const centerMatchKwargs = { centerWallsMaxDistance: 9.5, centerWallsMaxAngleDeg: 42, centerWallsMinEdgeLength: 0.5 };
    const centerKwargs = { centerWallsMaxDistance: 1.5, centerWallsMaxAngleDeg: 20 };
    this.registerProcessor('center_edges_in_walls', centerEdgesInWallsPass, { defaultKwargs: centerMatchKwargs });
    this.registerProcessor('center_match_walls', centerMatchWallsPass, { defaultKwargs: centerMatchKwargs });
    this.registerProcessor('center_combine_shared', centerCombineSharedPass, { defaultKwargs: centerKwargs });
    this.registerProcessor('center_reconnect', centerReconnectPass, { defaultKwargs: centerKwargs });

    this.registerProcessor('prune_unmatched_walls', pruneUnmatchedWallsPass, {
      defaultKwargs: { pruneUnmatchedMaxDistance: 0.5, pruneUnmatchedConnectorKeepLength: 1.0, pruneUnmatchedCoverageDistance: 0.5, pruneUnmatchedContinuationAngleDeg: 35, pruneUnmatchedPromotionAngleDeg: 15 },
    });

    this.registerProcessor('snap_small_segments', snapSmallSegmentsPass, {
      defaultKwargs: { snapSmallMaxLength: 0.5 },
    });

    this.registerProcessor('nudge_walls_into_bounds', nudgeWallsIntoBoundsPass, {
      defaultKwargs: { nudgeMaxDistance: 1.0 },
    });

    this.registerProcessor('prune_dead_ends', pruneDeadEndsPass, {
      defaultKwargs: { pruneDeadEndMaxRayDistance: 1.0, pruneDeadEndAngleTolDeg: 5 },
    });

    this.registerProcessor('extend_dead_ends', extendDeadEndNodesPass, {
      defaultKwargs: { deadEndMaxRayDistance: 5.0 },
    });

    this.registerProcessor('fixup_edge_rooms_from_nodes', fixupEdgeRoomsFromNodesPass, {
      defaultKwargs: {},
    });
  }

  /**
   * Setup default passes
   */
  private setupDefaultPasses(): void {
    // Wall-pairing pass — produces loose midline segments per polygon
    // edge. The next pass (connection) turns these into a skeleton.
    this.addPass('Pair Walls', pairWallsPass, {
      processorId: 'pair_walls',
      kwargs: { outerWallThickness: 0.3 },
    });

    // Grow paired segments out to the surrounding walls and merge
    // collinear neighbors that the grow step makes redundant.
    this.addPass('Grow Pairs', growPairsPass, {
      processorId: 'grow_pairs',
      kwargs: { growPairsMergeAngleDeg: 10, growPairsMergePerpTol: 0.1, growPairsMergeGapTol: 0.2 },
    });

    // Stitch grown segments into a connected node graph at their
    // crossings (and trim grown endpoints whose side intersects).
    this.addPass('Merge Pairs', mergePairsPass, {
      processorId: 'merge_pairs',
      kwargs: {},
    });

    // Alternative connector: extends each paired segment's endpoints
    // along its tangent until they hit another skeleton edge's
    // infinite line, then snaps/splits to share a node. Polygon
    // edges (rooms + outline) block the extension.
    this.addPass('Extend Pairs', extendPairsPass, {
      processorId: 'extend_pairs',
      kwargs: { extendPairsMaxDistance: 5.0, extendPairsSnapTol: 0.05 },
    });

    // Node creation passes (required for pipeline to work)
    this.addPass('Create Nodes', createNodesPass, {
      processorId: 'create_nodes',
      kwargs: { probeMaxWalk: 0.32 },
    });

    this.addPass('Merge Close Nodes', mergeCloseNodesPass, {
      processorId: 'merge_close_nodes',
      kwargs: { mergeTolerance: 0.05 },
    });

    this.addPass('Remove Collinear Nodes', removeCollinearNodesPass, {
      processorId: 'remove_collinear_nodes',
      kwargs: { straightenAngleThreshold: 15, removeCollinearThicknessTol: 0.03 },
    });

    this.addPass('Slide Collinear To Crossing', slideCollinearNodesToCrossingPass, {
      processorId: 'slide_collinear_to_crossing',
      kwargs: { straightenAngleThreshold: 15, perpendicularAngleThreshold: 70, maxRayDistance: 1.0 },
    });

    this.addPass('Create Edges', createEdgesPass, {
      processorId: 'create_edges',
    });

    this.addPass('Delete Edges Crossing Polygons', deleteSkeletonEdgesCrossingPolygonsPass, {
      processorId: 'delete_edges_crossing_polygons',
    });

    this.addPass('Split Edges Through Nearby Nodes', splitEdgesThroughNearbyNodesPass, {
      processorId: 'split_edges_through_nearby_nodes',
      kwargs: { snapTolerance: 0.1, endpointMargin: 0.05 },
    });

    this.addPass('Delete Duplicate Edges', deleteDuplicateEdgesPass, {
      processorId: 'delete_duplicate_edges',
    });

    this.addPass('Delete Longest Leg of Large Triangles', deleteLongestLegOfLargeTrianglesPass, {
      processorId: 'delete_longest_leg_of_large_triangles',
      kwargs: { triangleMinCircumradius: 0.5 },
    });

    // Cleanup passes
    this.addPass('Snap to Edges', snapNodesToEdgesPass, {
      processorId: 'snap_to_edges',
      kwargs: { snapTolerance: 0.1 },
    });

    this.addPass('Resolve Crossings', resolveEdgeCrossingsPass, {
      processorId: 'resolve_crossings',
      kwargs: { moveNodes: true },
    });

    this.addPass('Merge Parallel Edges', mergeParallelEdgesPass, {
      processorId: 'merge_parallel',
      kwargs: { angleThreshold: PARALLEL_EDGE_ANGLE_THRESHOLD },
    });

    this.addPass('Merge Distinct Origins', mergeDistinctOriginsPass, {
      processorId: 'merge_distinct_origins',
      kwargs: { mergeTolerance: 0.15 },
    });

    // Collapse triangles is disabled by default
    this.addPass('Collapse Small Triangles', collapseSmallTrianglesPass, {
      processorId: 'collapse_triangles',
      kwargs: { maxEdgeLength: 0.3 },
    });
    this.disablePass('Collapse Small Triangles');

    // Straighten edges is disabled by default
    this.addPass('Straighten Edges', straightenEdgesPass, {
      processorId: 'straighten_edges',
      kwargs: { outerWallThickness: 0.3, innerWallThickness: 0.15, maxRayDistance: 5.0, straightenMaxIterations: 50 },
    });
    this.disablePass('Straighten Edges');

    // Split junctions is disabled by default
    this.addPass('Split Junctions', splitJunctionNodesPass, {
      processorId: 'split_junctions',
      kwargs: { junctionSlideDistance: 0.1, perpendicularAngleThreshold: 70 },
    });
    this.disablePass('Split Junctions');

    // Straighten collinear edges - makes almost-straight edges perfectly straight
    this.addPass('Straighten Collinear', straightenCollinearEdgesPass, {
      processorId: 'straighten_collinear',
      kwargs: { straightenAngleThreshold: 15 },
    });
    this.disablePass('Straighten Collinear');

    // Straighten angles pass - slides nodes along collinear edges to make 90° angles
    this.addPass('Straighten Angles', straightenAnglesPass, {
      processorId: 'straighten_angles',
      kwargs: { straightenAngleThreshold: 15 },
    });
    this.disablePass('Straighten Angles');

    // Solve triangle nodes - moves triangle nodes to wall line intersections
    this.addPass('Solve Triangles', solveTriangleNodesPass, {
      processorId: 'solve_triangles',
    });
    this.disablePass('Solve Triangles');

    // Measure wall thickness - raycasts to room polygons to set leftSize/rightSize
    this.addPass('Measure Wall Thickness', measureWallThicknessPass, {
      processorId: 'measure_wall_thickness',
      kwargs: { maxRayDistance: 1.0, outerWallThickness: 0.15, measureWallThicknessSampleCount: 9, measureWallThicknessMaxAngleDeg: 30, measureWallThicknessThickenBy: 0.5 },
    });

    // Prune unmatched walls — every outline/room edge locks its single best
    // trace (property matching); everything unlocked is pruned so stair
    // treads / furniture lines never get skinned with a meaningless thickness.
    this.addPass('Prune Unmatched Walls', pruneUnmatchedWallsPass, {
      processorId: 'prune_unmatched_walls',
      kwargs: { pruneUnmatchedMaxDistance: 0.5, pruneUnmatchedConnectorKeepLength: 1.0, pruneUnmatchedCoverageDistance: 0.5, pruneUnmatchedContinuationAngleDeg: 35, pruneUnmatchedPromotionAngleDeg: 15 },
    });
    this.disablePass('Prune Unmatched Walls');

    // Snap small segments — short corner/step pieces are forced collinear or
    // perpendicular to their big neighbors; big edges never rotate.
    this.addPass('Snap Small Segments', snapSmallSegmentsPass, {
      processorId: 'snap_small_segments',
      kwargs: { snapSmallMaxLength: 0.5 },
    });
    this.disablePass('Snap Small Segments');

    // Nudge walls into bounds — collinear chains sitting inside a room or
    // outside the outline are translated laterally back into the wall band;
    // angles are never changed (chains that can't legally move stay put).
    this.addPass('Nudge Walls Into Bounds', nudgeWallsIntoBoundsPass, {
      processorId: 'nudge_walls_into_bounds',
      kwargs: { nudgeMaxDistance: 1.0 },
    });
    this.disablePass('Nudge Walls Into Bounds');

    // Prune dead ends - removes single-edge stubs lacking a perpendicular flat
    // head ahead of the open end and two parallel side walls (a real wall stub).
    this.addPass('Prune Dead Ends', pruneDeadEndsPass, {
      processorId: 'prune_dead_ends',
      kwargs: { pruneDeadEndMaxRayDistance: 1.0, pruneDeadEndAngleTolDeg: 5 },
    });
    this.disablePass('Prune Dead Ends');

    // Extend dead end nodes - extends nodes with single edge to hit walls
    this.addPass('Extend Dead Ends', extendDeadEndNodesPass, {
      processorId: 'extend_dead_ends',
      kwargs: { deadEndMaxRayDistance: 5.0 },
    });
    this.disablePass('Extend Dead Ends');

    // Reconcile per-edge room membership against the (post-cleanup) node
    // room memberships. Earlier merges/splits propagate metadata from
    // sibling edges; this pass re-derives `edge.roomIds` from the two
    // endpoint nodes so an edge claims a room exactly when both its
    // corners do. Runs last because every preceding pass can mutate
    // node `roomIds` or edge endpoints.
    this.addPass('Fixup Edge Rooms From Nodes', fixupEdgeRoomsFromNodesPass, {
      processorId: 'fixup_edge_rooms_from_nodes',
      kwargs: {},
    });
  }

  /**
   * Override loadFromFile to use default skeleton pipeline config path
   */
  override async loadFromFile(url = '/config/skeleton/skeleton-pipeline-config.json'): Promise<boolean> {
    return super.loadFromFile(url);
  }
}
